import React, { useState, useEffect } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import axios from "axios";
import { Toaster } from "./components/ui/sonner";
import { toast } from "sonner";
import { Button } from "./components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./components/ui/card";
import { Input } from "./components/ui/input";
import { Textarea } from "./components/ui/textarea";
import { Label } from "./components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./components/ui/tabs";
import { Badge } from "./components/ui/badge";
import { Separator } from "./components/ui/separator";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "./components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./components/ui/select";
import { Progress } from "./components/ui/progress";
import { Calendar } from "./components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "./components/ui/popover";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "./components/ui/tooltip";
import { format } from "date-fns";
import { nl } from "date-fns/locale";
import { Eye, Edit, Trash2, Plus, CalendarIcon, FileText, Copy, Wrench, Shield, Info, Download, Upload, Clock, CheckCircle, Star, Car, Gift, AlertCircle, AlertTriangle, Bell, Users, Building2, Key, FileCheck, UserCheck, MessageSquare } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AuthContext = React.createContext();

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      // Verify token by making a request
      fetchUserProfile();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchUserProfile = async () => {
    try {
      // Since we don't have a profile endpoint, we'll use the dashboard stats to verify the token
      await axios.get(`${API}/dashboard/stats`);
      // If successful, the token is valid
      setLoading(false);
    } catch (error) {
      console.error('Token verification failed:', error);
      logout();
    }
  };

  const login = async (email, password) => {
    try {
      const response = await axios.post(`${API}/auth/login`, { email, password });
      const { access_token, user: userData } = response.data;
      
      setToken(access_token);
      setUser(userData);
      localStorage.setItem('token', access_token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      
      toast.success("Succesvol ingelogd!");
      return true;
    } catch (error) {
      toast.error(error.response?.data?.detail || "Inloggen mislukt");
      return false;
    }
  };

  const register = async (email, password, naam) => {
    try {
      const response = await axios.post(`${API}/auth/register`, { email, password, naam });
      const { access_token, user: userData } = response.data;
      
      setToken(access_token);
      setUser(userData);
      localStorage.setItem('token', access_token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      
      toast.success("Account succesvol aangemaakt!");
      return true;
    } catch (error) {
      toast.error(error.response?.data?.detail || "Registratie mislukt");
      return false;
    }
  };

  const logout = () => {
    setToken(null);
    setUser(null);
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
    toast.success("Succesvol uitgelogd");
  };

  const value = {
    user,
    token,
    login,
    register,
    logout,
    loading
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

const useAuth = () => {
  const context = React.useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Editable Tab Info Component
const EditableTabInfo = ({ tabName, user, tabInfos, editingTabInfo, setEditingTabInfo, updateTabInfo, fetchTabInfo }) => {
  const currentInfo = tabInfos[tabName] || '';
  const canEdit = user?.rol && (['manager', 'hr', 'admin'].includes(user.rol) || user.kan_tab_info_bewerken);
  
  useEffect(() => {
    // Fetch tab info when component mounts or tab changes
    fetchTabInfo(tabName);
  }, [tabName]);

  if (!canEdit && !currentInfo) {
    return null; // Don't show anything if user can't edit and there's no info
  }

  return (
    <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
      {editingTabInfo?.tab === tabName ? (
        <div className="space-y-2">
          <Textarea
            value={editingTabInfo.text}
            onChange={(e) => setEditingTabInfo({...editingTabInfo, text: e.target.value})}
            placeholder="Voeg belangrijke informatie toe voor dit tabblad..."
            className="min-h-[80px] resize-none"
          />
          <div className="flex gap-2 justify-end">
            <Button
              size="sm"
              variant="outline"
              onClick={() => setEditingTabInfo(null)}
            >
              Annuleren
            </Button>
            <Button
              size="sm"
              onClick={() => updateTabInfo(tabName, editingTabInfo.text)}
            >
              Opslaan
            </Button>
          </div>
        </div>
      ) : (
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1">
            {currentInfo ? (
              <p className="text-sm text-blue-700 whitespace-pre-wrap">{currentInfo}</p>
            ) : (
              <p className="text-sm text-blue-600 italic">Geen informatie toegevoegd</p>
            )}
          </div>
          {canEdit && (
            <Button
              size="sm"
              variant="ghost"
              className="p-1 h-6 w-6"
              onClick={() => setEditingTabInfo({ tab: tabName, text: currentInfo })}
            >
              <Edit className="h-3 w-3" />
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

// Info Tooltip Component
const InfoTooltip = ({ children, content }) => {
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          {children}
        </TooltipTrigger>
        <TooltipContent>
          <p className="max-w-xs text-sm">{content}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

const LoginPage = () => {
  const { login, register } = useAuth();
  const [isLogin, setIsLogin] = useState(true);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    naam: ''
  });

  // Password reset state
  const [showPasswordReset, setShowPasswordReset] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetToken, setResetToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [resetStep, setResetStep] = useState('request'); // 'request', 'confirm'

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (isLogin) {
      await login(formData.email, formData.password);
    } else {
      await register(formData.email, formData.password, formData.naam);
    }
  };

  // Password reset functions
  const requestPasswordReset = async () => {
    try {
      console.log('🔄 Sending password reset request for:', resetEmail);
      const response = await axios.post(`${API}/auth/password-reset-request`, {
        email: resetEmail
      });
      console.log('✅ Password reset response:', response.data);
      
      toast.success('Als het e-mailadres bestaat, wordt er een herstellink verzonden naar uw e-mail');
      
      setShowPasswordReset(false);
      setResetEmail('');
    } catch (error) {
      console.error('❌ Password reset error:', error);
      console.error('❌ Error response:', error.response?.data);
      toast.error(error.response?.data?.detail || 'Fout bij versturen herstellink');
    }
  };

  const confirmPasswordReset = async () => {
    if (newPassword !== confirmPassword) {
      toast.error('Wachtwoorden komen niet overeen');
      return;
    }

    if (newPassword.length < 8) {
      toast.error('Wachtwoord moet minimaal 8 karakters zijn');
      return;
    }

    try {
      await axios.post(`${API}/auth/password-reset-confirm`, {
        token: resetToken,
        new_password: newPassword
      });
      
      toast.success('Wachtwoord succesvol hersteld. U kunt nu inloggen met uw nieuwe wachtwoord');
      
      // Reset form and go back to login
      setResetStep('request');
      setResetToken('');
      setNewPassword('');
      setConfirmPassword('');
      setShowPasswordReset(false);
      
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fout bij herstellen wachtwoord');
    }
  };

  // Handle password reset from URL
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('token');
    
    if (token) {
      // Validate token
      axios.get(`${API}/auth/validate-reset-token/${token}`)
        .then(response => {
          setResetToken(token);
          setResetEmail(response.data.email);
          setResetStep('confirm');
          setShowPasswordReset(true);
          
          // Clear URL parameters
          window.history.replaceState({}, document.title, window.location.pathname);
        })
        .catch(error => {
          toast.error('Ongeldige of verlopen herstellink');
        });
    }
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-1">
          <CardTitle className="text-2xl font-bold text-center">
            HR Evaluatiesysteem
          </CardTitle>
          <CardDescription className="text-center">
            {isLogin ? 'Log in op uw account' : 'Maak een nieuw account aan'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">E-mailadres</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
              />
            </div>
            
            {!isLogin && (
              <div className="space-y-2">
                <Label htmlFor="naam">Naam</Label>
                <Input
                  id="naam"
                  type="text"
                  value={formData.naam}
                  onChange={(e) => setFormData({ ...formData, naam: e.target.value })}
                  required
                />
              </div>
            )}
            
            <div className="space-y-2">
              <Label htmlFor="password">Wachtwoord</Label>
              <Input
                id="password"
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                required
              />
            </div>
            
            <Button type="submit" className="w-full">
              {isLogin ? 'Inloggen' : 'Registreren'}
            </Button>
          </form>
          
          <div className="mt-4 text-center space-y-2">
            <Button
              variant="link"
              onClick={() => setIsLogin(!isLogin)}
              className="text-sm"
            >
              {isLogin 
                ? "Nog geen account? Registreer hier" 
                : "Al een account? Login hier"
              }
            </Button>
            
            {isLogin && (
              <div>
                <Button
                  variant="link"
                  onClick={() => setShowPasswordReset(true)}
                  className="text-sm text-gray-600"
                >
                  Wachtwoord vergeten?
                </Button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Password Reset Dialog */}
      <Dialog open={showPasswordReset} onOpenChange={setShowPasswordReset}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {resetStep === 'request' ? 'Wachtwoord Herstellen' : 'Nieuw Wachtwoord Instellen'}
            </DialogTitle>
            <DialogDescription>
              {resetStep === 'request' 
                ? 'Vul uw e-mailadres in om een herstellink te ontvangen'
                : 'Voer uw nieuwe wachtwoord in'
              }
            </DialogDescription>
          </DialogHeader>
          
          {resetStep === 'request' ? (
            <div className="space-y-4">
              <div>
                <Label htmlFor="resetEmail">E-mailadres</Label>
                <Input
                  id="resetEmail"
                  type="email"
                  value={resetEmail}
                  onChange={(e) => setResetEmail(e.target.value)}
                  placeholder="voornaam@bedrijf.nl"
                  required
                />
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div>
                <Label htmlFor="resetEmail">E-mailadres</Label>
                <Input
                  id="resetEmail"
                  type="email"
                  value={resetEmail}
                  disabled
                  className="bg-gray-100"
                />
              </div>
              <div>
                <Label htmlFor="newPassword">Nieuw Wachtwoord</Label>
                <Input
                  id="newPassword"
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Minimaal 8 karakters"
                  required
                />
              </div>
              <div>
                <Label htmlFor="confirmPassword">Bevestig Wachtwoord</Label>
                <Input
                  id="confirmPassword"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Herhaal uw nieuwe wachtwoord"
                  required
                />
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowPasswordReset(false);
              setResetStep('request');
              setResetEmail('');
              setResetToken('');
              setNewPassword('');
              setConfirmPassword('');
            }}>
              Annuleren
            </Button>
            <Button onClick={resetStep === 'request' ? requestPasswordReset : confirmPasswordReset}>
              {resetStep === 'request' ? 'Herstellink Versturen' : 'Wachtwoord Wijzigen'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const Dashboard = () => {
  const { user, logout, token } = useAuth();
  const [stats, setStats] = useState({});
  const [bedrijven, setBedrijven] = useState([]);
  const [selectedBedrijf, setSelectedBedrijf] = useState('alle');
  const [activeTab, setActiveTab] = useState('evaluaties');
  const [evaluaties, setEvaluaties] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [users, setUsers] = useState([]);
  const [voertuigen, setVoertuigen] = useState([]);
  const [voordelen, setVoordelen] = useState([]);
  const [verzekeringen, setVerzekeringen] = useState([]);
  const [claims, setClaims] = useState([]);
  const [herinneringen, setHerinneringen] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [logs, setLogs] = useState([]);
  const [logsFilter, setLogsFilter] = useState('alle_types');
  const [editingUserRights, setEditingUserRights] = useState(null);
  const [userRightsData, setUserRightsData] = useState({
    rol: '',
    bedrijf_ids: [],
    opmerking: ''
  });
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  // Form states
  const [newBedrijf, setNewBedrijf] = useState({ naam: '', beschrijving: '' });
  const [newTemplate, setNewTemplate] = useState({
    naam: '',
    beschrijving: '',
    type: 'periodiek',
    bedrijf_id: '',
    vragen: []
  });
  const [newEvaluatie, setNewEvaluatie] = useState({
    template_id: '',
    werknemer_id: '',
    manager_id: '',
    deadline: new Date(),
    type: 'periodiek'
  });
  const [newVoertuig, setNewVoertuig] = useState({
    kenteken: '',
    merk: '',
    model: '',
    type: 'lease',
    kilometerstand: 0,
    brandstoftype: 'benzine',
    eerste_inschrijving: new Date(),
    bestuurder_id: 'geen',
    bedrijf_id: '',
    lease_gegevens: {}
  });
  const [newVoordeel, setNewVoordeel] = useState({
    werknemer_id: '',
    type: 'fietslease',
    beschrijving: '',
    waarde: 0,
    startdatum: new Date(),
    einddatum: null,
    actief: true,
    voertuig_id: ''
  });
  const [newVerzekering, setNewVerzekering] = useState({
    naam: '',
    type: 'ba',
    verzekeraar: '',
    polisnummer: '',
    begindatum: new Date(),
    einddatum: null,
    premie: 0,
    dekking: '',
    opmerking: '',
    voertuig_id: ''
  });
  const [newClaim, setNewClaim] = useState({
    polis_id: '',
    omschrijving: '',
    schade_datum: new Date(),
    gemeld_datum: new Date(),
    schadebedrag: 0,
    eigen_risico: 0,
    status: 'ingediend',
    opmerking: ''
  });
  const [newUser, setNewUser] = useState({
    naam: '',
    email: '',
    password: '',
    rol: 'werknemer',
    bedrijf_ids: []
  });

  // States for editing items
  const [editingItem, setEditingItem] = useState(null);
  const [editData, setEditData] = useState({});
  const [viewingItem, setViewingItem] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [fillingEvaluation, setFillingEvaluation] = useState(null);
  const [evaluationMode, setEvaluationMode] = useState('zelfevaluatie'); // 'zelfevaluatie' or 'manager'
  const [showPasswordReset, setShowPasswordReset] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetToken, setResetToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [resetStep, setResetStep] = useState('request'); // 'request', 'confirm'
  const [showDocumentUpload, setShowDocumentUpload] = useState(null); // { type: 'claim', id: 'xxx' }
  const [uploadingDocument, setUploadingDocument] = useState(false);
  const [entityDocuments, setEntityDocuments] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [newClaimDocuments, setNewClaimDocuments] = useState([]);
  const [newVerzekeringsDocuments, setNewVerzekeringsDocuments] = useState([]);
  const [updates, setUpdates] = useState([]);
  const [newUpdate, setNewUpdate] = useState('');
  const [licenses, setLicenses] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [userFamilies, setUserFamilies] = useState([]);
  const [editingUserFamily, setEditingUserFamily] = useState(null);
  const [newLicense, setNewLicense] = useState({
    naam: '', code: '', email: '', wachtwoord: '', vervaldatum: null,
    gebruiker: '', kostprijs: '', website: '', aangekocht: null
  });
  const [newContract, setNewContract] = useState({
    naam: '', type: '', partij: '', maandelijks_bedrag: 0, jaarlijks_bedrag: 0,
    begindatum: new Date(), einddatum: null, opmerkingen: ''
  });
  const [newContractDocuments, setNewContractDocuments] = useState([]);
  const [newFamilyMember, setNewFamilyMember] = useState({ naam: '', relatie: 'man' });
  const [claimComments, setClaimComments] = useState([]);
  const [newClaimComment, setNewClaimComment] = useState('');
  const [editingComment, setEditingComment] = useState(null);
  
  // Claim Contacts State
  const [claimContacts, setClaimContacts] = useState([]);
  const [newClaimContact, setNewClaimContact] = useState({ naam: '', rol: '', email: '', gsm: '' });
  const [editingContact, setEditingContact] = useState(null);
  const [showContactDialog, setShowContactDialog] = useState(null); // { claimId: 'xxx' }
  
  // Tab Info State
  const [tabInfos, setTabInfos] = useState({}); // { 'evaluaties': 'info text', 'voertuigen': 'info text' }
  const [editingTabInfo, setEditingTabInfo] = useState(null); // { tab: 'evaluaties', text: 'current text' }
  
  // Dialog states
  const [showAddLicenseDialog, setShowAddLicenseDialog] = useState(false);

  // Helper function to get company name by ID
  const getBedrijfNaam = (bedrijfId) => {
    const bedrijf = bedrijven.find(b => b.id === bedrijfId);
    return bedrijf?.naam || 'Onbekend bedrijf';
  };

  // Update evaluation answers when mode changes
  useEffect(() => {
    if (fillingEvaluation && fillingEvaluation.template) {
      const existingAnswers = evaluationMode === 'zelfevaluatie' ? 
        (fillingEvaluation.zelfevaluatie || []) : 
        (fillingEvaluation.manager_beoordeling || []);
      
      const updatedAnswers = fillingEvaluation.template.vragen.map(vraag => {
        const existing = existingAnswers.find(a => a.vraag_id === vraag.id);
        return {
          vraag_id: vraag.id,
          score: existing?.score || 1,
          opmerking: existing?.opmerking || '',
          doelstelling: existing?.doelstelling || ''
        };
      });
      
      setEvaluationAnswers(updatedAnswers);
    }
  }, [evaluationMode, fillingEvaluation]);
  const [evaluationAnswers, setEvaluationAnswers] = useState([]);
  const [editingTemplateQuestions, setEditingTemplateQuestions] = useState(null);
  const [templateQuestions, setTemplateQuestions] = useState([]);
  const [addingMaintenance, setAddingMaintenance] = useState(null);
  const [newMaintenance, setNewMaintenance] = useState({
    type: 'onderhoud',
    datum: new Date(),
    kilometerstand: 0,
    kosten: 0,
    omschrijving: '',
    garage: '',
    factuur_nummer: ''
  });

  // Document functions
  const uploadDocument = async (files, description = '', entityType = null, entityId = null) => {
    // Use parameters if provided, otherwise use showDocumentUpload state
    const uploadTarget = entityType && entityId ? 
      { type: entityType, id: entityId } : 
      showDocumentUpload;
    
    if (!uploadTarget) return;
    
    setUploadingDocument(true);
    const uploadPromises = [];
    
    for (const file of files) {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('entity_type', uploadTarget.type);
      formData.append('entity_id', uploadTarget.id);
      formData.append('description', description);
      
      uploadPromises.push(
        axios.post(`${API}/upload-document`, formData, {
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        })
      );
    }
    
    try {
      await Promise.all(uploadPromises);
      toast.success(`${files.length} document(en) succesvol geüpload`);
      
      // Close dialog if it was used
      if (!entityType && !entityId) {
        setShowDocumentUpload(null);
      }
      
      // Refresh documents list if needed
      if (files.length > 0) {
        fetchEntityDocuments(uploadTarget.type, uploadTarget.id);
      }
      
      return true; // Success indicator
    } catch (error) {
      console.error('Error uploading documents:', error);
      toast.error(error.response?.data?.detail || 'Fout bij uploaden documenten');
      return false; // Failure indicator
    } finally {
      setUploadingDocument(false);
    }
  };

  // Function to handle documents in create forms
  const handleFormDocuments = (files, documentsList, setDocumentsList) => {
    const newDocs = Array.from(files).map(file => ({
      id: Math.random().toString(36).substr(2, 9),
      file: file,
      naam: file.name,
      size: file.size,
      type: file.type
    }));
    setDocumentsList([...documentsList, ...newDocs]);
  };

  const removeFormDocument = (docId, documentsList, setDocumentsList) => {
    setDocumentsList(documentsList.filter(doc => doc.id !== docId));
  };

  const fetchEntityDocuments = async (entityType, entityId) => {
    try {
      const response = await axios.get(`${API}/documents/${entityType}/${entityId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setEntityDocuments(response.data);
    } catch (error) {
      console.error('Error fetching documents:', error);
      setEntityDocuments([]);
    }
  };

  const fetchAllDocuments = async () => {
    try {
      const response = await axios.get(`${API}/documents`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setDocuments(response.data);
    } catch (error) {
      console.error('Error fetching all documents:', error);
      setDocuments([]);
    }
  };

  const deleteDocument = async (documentId) => {
    try {
      await axios.delete(`${API}/documents/${documentId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      toast.success('Document succesvol verwijderd');
      // Refresh documents
      if (showDocumentUpload) {
        fetchEntityDocuments(showDocumentUpload.type, showDocumentUpload.id);
      }
    } catch (error) {
      console.error('Error deleting document:', error);
      toast.error(error.response?.data?.detail || 'Fout bij verwijderen document');
    }
  };

  const fetchLicenses = async () => {
    try {
      const response = await axios.get(`${API}/licenses?bedrijf_id=${selectedBedrijf}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setLicenses(response.data);
    } catch (error) {
      console.error('Error fetching licenses:', error);
      setLicenses([]);
    }
  };

  const fetchContracts = async () => {
    try {
      const response = await axios.get(`${API}/contracts?bedrijf_id=${selectedBedrijf}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setContracts(response.data);
    } catch (error) {
      console.error('Error fetching contracts:', error);
      setContracts([]);
    }
  };

  const fetchUserFamilies = async () => {
    try {
      console.log('Fetching families for', users.length, 'users');
      const familyPromises = users.map(async (user) => {
        try {
          const response = await axios.get(`${API}/users/${user.id}/family`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          console.log(`Family for ${user.naam}:`, response.data);
          return { userId: user.id, family: response.data };
        } catch (error) {
          console.log(`No family data for ${user.naam}:`, error.response?.status);
          return { userId: user.id, family: [] };
        }
      });
      
      const familyData = await Promise.all(familyPromises);
      const familyMap = {};
      familyData.forEach(({ userId, family }) => {
        familyMap[userId] = family;
      });
      console.log('Final family map:', familyMap);
      setUserFamilies(familyMap);
    } catch (error) {
      console.error('Error fetching user families:', error);
      setUserFamilies({});
    }
  };

  const handleEditFamilyMember = async (member) => {
    console.log('Edit family member clicked:', member);
    try {
      const newName = prompt(`Nieuwe naam voor ${member.naam}:`, member.naam);
      if (!newName || newName.trim() === '') {
        console.log('Edit cancelled - no name provided');
        return;
      }
      
      const newRelation = prompt(`Nieuwe relatie voor ${member.naam} (man/vrouw/zoon/dochter):`, member.relatie);
      if (!newRelation || !['man', 'vrouw', 'zoon', 'dochter'].includes(newRelation)) {
        console.log('Edit cancelled - invalid relation');
        toast.error('Ongeldige relatie. Kies uit: man, vrouw, zoon, dochter');
        return;
      }

      console.log('Updating family member:', member.id, 'with data:', { naam: newName.trim(), relatie: newRelation });
      
      const response = await axios.put(`${API}/users/${editingUserFamily.id}/family/${member.id}`, {
        naam: newName.trim(),
        relatie: newRelation
      }, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      console.log('Family member updated successfully:', response.data);
      await fetchUserFamilies();
      toast.success('Familielid bijgewerkt');
    } catch (error) {
      console.error('Error updating family member:', error);
      toast.error(error.response?.data?.detail || 'Fout bij bijwerken familielid');
    }
  };

  const handleDeleteFamilyMember = async (member) => {
    console.log('Delete family member clicked:', member);
    try {
      const confirmed = window.confirm(`${member.naam} verwijderen als familielid?`);
      if (!confirmed) {
        console.log('Delete cancelled by user');
        return;
      }

      console.log('Deleting family member:', member.id, 'for user:', editingUserFamily.id);
      
      const response = await axios.delete(`${API}/users/${editingUserFamily.id}/family/${member.id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      console.log('Family member deleted successfully:', response.data);
      await fetchUserFamilies();
      toast.success('Familielid verwijderd');
    } catch (error) {
      console.error('Error deleting family member:', error);
      toast.error(error.response?.data?.detail || 'Fout bij verwijderen familielid');
    }
  };

  // Test function for debugging
  const testButtonClick = () => {
    console.log('TEST BUTTON CLICKED - This proves buttons can be clicked');
    alert('Test button werkt!');
  };

  // Claim Comments Functions
  const fetchClaimComments = async (claimId) => {
    try {
      const response = await axios.get(`${API}/claims/${claimId}/comments`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setClaimComments(response.data);
    } catch (error) {
      console.error('Error fetching claim comments:', error);
      setClaimComments([]);
    }
  };

  const addClaimComment = async (claimId) => {
    if (!newClaimComment.trim()) {
      toast.error('Voer eerst een opmerking in');
      return;
    }

    try {
      await axios.post(`${API}/claims/${claimId}/comments`, {
        opmerking: newClaimComment
      }, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      setNewClaimComment('');
      await fetchClaimComments(claimId);
      toast.success('Opmerking toegevoegd');
    } catch (error) {
      console.error('Error adding comment:', error);
      toast.error(error.response?.data?.detail || 'Fout bij toevoegen opmerking');
    }
  };

  const updateClaimComment = async (claimId, commentId, newText) => {
    try {
      await axios.put(`${API}/claims/${claimId}/comments/${commentId}`, {
        opmerking: newText
      }, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      setEditingComment(null);
      await fetchClaimComments(claimId);
      toast.success('Opmerking bijgewerkt');
    } catch (error) {
      console.error('Error updating comment:', error);
      toast.error(error.response?.data?.detail || 'Fout bij bijwerken opmerking');
    }
  };

  const deleteClaimComment = async (claimId, commentId) => {
    if (!window.confirm('Deze opmerking verwijderen?')) {
      return;
    }

    try {
      await axios.delete(`${API}/claims/${claimId}/comments/${commentId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      await fetchClaimComments(claimId);
      toast.success('Opmerking verwijderd');
    } catch (error) {
      console.error('Error deleting comment:', error);
      toast.error(error.response?.data?.detail || 'Fout bij verwijderen opmerking');
    }
  };

  // Claim Contacts Functions
  const fetchClaimContacts = async (claimId) => {
    try {
      const response = await axios.get(`${API}/claims/${claimId}/contacts`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setClaimContacts(response.data);
    } catch (error) {
      console.error('Error fetching claim contacts:', error);
      setClaimContacts([]);
    }
  };

  const addClaimContact = async (claimId) => {
    if (!newClaimContact.naam.trim() || !newClaimContact.rol.trim()) {
      toast.error('Voer ten minste naam en rol in');
      return;
    }

    try {
      await axios.post(`${API}/claims/${claimId}/contacts`, newClaimContact, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      setNewClaimContact({ naam: '', rol: '', email: '', gsm: '' });
      await fetchClaimContacts(claimId);
      toast.success('Contact toegevoegd');
    } catch (error) {
      console.error('Error adding contact:', error);
      toast.error(error.response?.data?.detail || 'Fout bij toevoegen contact');
    }
  };

  const updateClaimContact = async (claimId, contactId, contactData) => {
    try {
      await axios.put(`${API}/claims/${claimId}/contacts/${contactId}`, contactData, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      setEditingContact(null);
      await fetchClaimContacts(claimId);
      toast.success('Contact bijgewerkt');
    } catch (error) {
      console.error('Error updating contact:', error);
      toast.error(error.response?.data?.detail || 'Fout bij bijwerken contact');
    }
  };

  const deleteClaimContact = async (claimId, contactId) => {
    if (!window.confirm('Dit contact verwijderen?')) {
      return;
    }

    try {
      await axios.delete(`${API}/claims/${claimId}/contacts/${contactId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      await fetchClaimContacts(claimId);
      toast.success('Contact verwijderd');
    } catch (error) {
      console.error('Error deleting contact:', error);
      toast.error(error.response?.data?.detail || 'Fout bij verwijderen contact');
    }
  };

  // Tab Info Functions
  const fetchTabInfo = async (tabName) => {
    try {
      const response = await axios.get(`${API}/tab-info/${tabName}?bedrijf_id=${selectedBedrijf}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setTabInfos(prev => ({ ...prev, [tabName]: response.data.info_text || '' }));
    } catch (error) {
      console.error('Error fetching tab info:', error);
      setTabInfos(prev => ({ ...prev, [tabName]: '' }));
    }
  };

  const updateTabInfo = async (tabName, infoText) => {
    try {
      await axios.put(`${API}/tab-info/${tabName}?bedrijf_id=${selectedBedrijf}`, {
        tab_name: tabName,
        info_text: infoText
      }, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      setTabInfos(prev => ({ ...prev, [tabName]: infoText }));
      setEditingTabInfo(null);
      toast.success('Tab informatie bijgewerkt');
    } catch (error) {
      console.error('Error updating tab info:', error);
      toast.error(error.response?.data?.detail || 'Fout bij bijwerken tab informatie');
    }
  };

  useEffect(() => {
    fetchData();
    fetchUpdates();
    fetchAllDocuments();
    if (selectedBedrijf) {
      fetchLicenses();
      fetchContracts();
    }
  }, [selectedBedrijf]);

  useEffect(() => {
    if (users.length > 0) {
      fetchUserFamilies();
    }
  }, [users]);

  const handleBedrijfChange = (bedrijfId) => {
    setSelectedBedrijf(bedrijfId);
    // Don't change tab when switching company
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const params = (selectedBedrijf && selectedBedrijf !== "alle") ? { bedrijf_id: selectedBedrijf } : {};
      
      const fetchPromises = [
        axios.get(`${API}/dashboard/stats`, { params }),
        axios.get(`${API}/bedrijven`),
        axios.get(`${API}/evaluaties`, { params }),
        axios.get(`${API}/evaluatie-templates`, { params }),
        axios.get(`${API}/users`, { params }),
        axios.get(`${API}/voertuigen`, { params }),
        axios.get(`${API}/voordelen`, { params }),
        axios.get(`${API}/verzekeringen`, { params }),
        axios.get(`${API}/claims`, { params }),
        axios.get(`${API}/herinneringen`, { params }),
        axios.get(`${API}/updates`)
      ];

      // Add admin-only endpoints if user is admin
      if (user?.rol === 'admin') {
        fetchPromises.push(
          axios.get(`${API}/admin/users`),
          axios.get(`${API}/admin/audit-logs`, { params: { limit: 50 } })
        );
      }

      const responses = await Promise.all(fetchPromises.map(promise => 
        promise.catch(error => ({ error: true, response: error }))
      ));

      // Handle basic responses
      const [statsRes, bedrijvenRes, evaluatiesRes, templatesRes, usersRes, voertuigenRes, voordelenRes, verzekeringenRes, claimsRes, herinneringenRes, updatesRes] = responses.slice(0, 11);

      if (!statsRes.error) setStats(statsRes.data);
      if (!bedrijvenRes.error) setBedrijven(bedrijvenRes.data);
      if (!evaluatiesRes.error) setEvaluaties(evaluatiesRes.data);
      if (!templatesRes.error) setTemplates(templatesRes.data);
      if (!usersRes.error) setUsers(usersRes.data);
      if (!voertuigenRes.error) setVoertuigen(voertuigenRes.data);
      if (!voordelenRes.error) setVoordelen(voordelenRes.data);
      if (!verzekeringenRes.error) setVerzekeringen(verzekeringenRes.data);
      if (!claimsRes.error) setClaims(claimsRes.data);
      if (!herinneringenRes.error) setHerinneringen(herinneringenRes.data);
      if (!updatesRes.error) setUpdates(updatesRes.data);

      // Handle admin responses
      if (user?.rol === 'admin' && responses.length > 11) {
        const [allUsersRes, auditLogsRes] = responses.slice(11);
        if (!allUsersRes.error) setAllUsers(allUsersRes.data);
        if (!auditLogsRes.error) setAuditLogs(auditLogsRes.data);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Fout bij ophalen van gegevens');
    } finally {
      setLoading(false);
    }
  };

  const fetchUpdates = async () => {
    try {
      const response = await axios.get(`${API}/updates`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setUpdates(response.data);
    } catch (error) {
      console.error('Error fetching updates:', error);
    }
  };

  const createBedrijf = async () => {
    try {
      await axios.post(`${API}/bedrijven`, newBedrijf);
      setNewBedrijf({ naam: '', beschrijving: '' });
      fetchData();
      toast.success('Bedrijf succesvol aangemaakt');
    } catch (error) {
      toast.error('Fout bij aanmaken bedrijf');
    }
  };

  const createTemplate = async () => {
    try {
      if (!newTemplate.bedrijf_id) {
        toast.error('Selecteer een bedrijf');
        return;
      }
      
      // Create default questions for demo
      const defaultVragen = [
        { tekst: "Voldoende kennis voor kleine projecten", sectie: "Kennis van de theorie", volgorde: 1 },
        { tekst: "Weet waar de kennis te vinden is", sectie: "Kennis van de theorie", volgorde: 2 },
        { tekst: "Begrijpt financiële impact van beslissingen", sectie: "Financieel bewustzijn", volgorde: 3 },
        { tekst: "Communiceert helder en effectief", sectie: "Communicatie", volgorde: 4 },
        { tekst: "Luistert actief naar collega's", sectie: "Communicatie", volgorde: 5 }
      ];
      
      const templateData = {
        ...newTemplate,
        vragen: defaultVragen
      };
      
      await axios.post(`${API}/evaluatie-templates`, templateData);
      setNewTemplate({ naam: '', beschrijving: '', type: 'periodiek', bedrijf_id: '', vragen: [] });
      fetchData();
      // Logging is handled by backend
      toast.success('Template succesvol aangemaakt');
    } catch (error) {
      toast.error('Fout bij aanmaken template');
    }
  };

  const createEvaluatie = async () => {
    try {
      const evaluatieData = {
        ...newEvaluatie,
        deadline: newEvaluatie.deadline.toISOString()
      };
      
      await axios.post(`${API}/evaluaties`, evaluatieData);
      setNewEvaluatie({ template_id: '', werknemer_id: '', manager_id: '', deadline: new Date(), type: 'periodiek' });
      fetchData();
      // Stay on current tab after creating
      toast.success('Evaluatie succesvol aangemaakt');
    } catch (error) {
      toast.error('Fout bij aanmaken evaluatie');
    }
  };

  const createVoertuig = async () => {
    try {
      if (!selectedBedrijf) {
        toast.error('Selecteer een bedrijf');
        return;
      }
      
      const voertuigData = {
        ...newVoertuig,
        eerste_inschrijving: newVoertuig.eerste_inschrijving.toISOString().split('T')[0]
      };
      const bedrijfId = newVoertuig.bedrijf_id || selectedBedrijf;
      
      await axios.post(`${API}/voertuigen?bedrijf_id=${bedrijfId}`, voertuigData);
      setNewVoertuig({
        kenteken: '',
        merk: '',
        model: '',
        type: 'lease',
        kilometerstand: 0,
        brandstoftype: 'benzine',
        eerste_inschrijving: new Date(),
        bestuurder_id: 'geen',
        lease_gegevens: {}
      });
      fetchData();
      toast.success('Voertuig succesvol aangemaakt');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fout bij aanmaken voertuig');
    }
  };

  const createVoordeel = async () => {
    try {
      if (!selectedBedrijf) {
        toast.error('Selecteer een bedrijf');
        return;
      }
      
      const voordeelData = {
        ...newVoordeel,
        startdatum: newVoordeel.startdatum.toISOString().split('T')[0],
        einddatum: newVoordeel.einddatum ? newVoordeel.einddatum.toISOString().split('T')[0] : null
      };
      
      await axios.post(`${API}/voordelen?bedrijf_id=${selectedBedrijf}`, voordeelData);
      setNewVoordeel({
        werknemer_id: '',
        type: 'fietslease',
        beschrijving: '',
        waarde: 0,
        startdatum: new Date(),
        einddatum: null,
        actief: true
      });
      fetchData();
      toast.success('Voordeel succesvol aangemaakt');
    } catch (error) {
      toast.error('Fout bij aanmaken voordeel');
    }
  };

  const createVerzekering = async () => {
    try {
      if (!selectedBedrijf) {
        toast.error('Selecteer een bedrijf');
        return;
      }
      
      // Prepare verzekering data with proper formatting
      const verzekeringData = {
        naam: newVerzekering.naam.trim(),
        type: newVerzekering.type,
        verzekeraar: newVerzekering.verzekeraar.trim(),
        polisnummer: newVerzekering.polisnummer.trim(),
        begindatum: newVerzekering.begindatum.toISOString().split('T')[0],
        einddatum: newVerzekering.einddatum ? newVerzekering.einddatum.toISOString().split('T')[0] : null,
        premie: typeof newVerzekering.premie === 'string' ? parseFloat(newVerzekering.premie) || 0 : newVerzekering.premie,
        dekking: newVerzekering.dekking.trim(),
        opmerking: newVerzekering.opmerking || '',
        voertuig_id: newVerzekering.voertuig_id || null
      };
      
      const response = await axios.post(`${API}/verzekeringen?bedrijf_id=${selectedBedrijf}`, verzekeringData);
      
      const verzekeringId = response.data.id;
      
      // Upload documents if any
      if (newVerzekeringsDocuments.length > 0) {
        for (const doc of newVerzekeringsDocuments) {
          await uploadDocument([doc.file], doc.description || `Document voor verzekering`, 'verzekering', verzekeringId);
        }
      }
      
      setNewVerzekering({
        naam: '',
        type: 'ba',
        verzekeraar: '',
        polisnummer: '',
        begindatum: new Date(),
        einddatum: null,
        premie: 0,
        dekking: '',
        opmerking: '',
        voertuig_id: ''
      });
      setNewVerzekeringsDocuments([]);
      
      fetchData();
      toast.success('Verzekering succesvol aangemaakt met documenten');
    } catch (error) {
      console.error('Error creating verzekering:', error);
      
      // Handle validation errors properly
      let errorMessage = 'Fout bij aanmaken verzekering';
      if (error.response?.data?.detail) {
        if (Array.isArray(error.response.data.detail)) {
          // Handle FastAPI validation errors
          errorMessage = error.response.data.detail.map(err => err.msg).join(', ');
        } else {
          errorMessage = error.response.data.detail;
        }
      }
      
      toast.error(errorMessage);
    }
  };

  const createClaim = async () => {
    try {
      const claimData = {
        ...newClaim,
        schade_datum: newClaim.schade_datum.toISOString().split('T')[0],
        gemeld_datum: newClaim.gemeld_datum.toISOString().split('T')[0]
      };
      
      const response = await axios.post(`${API}/claims`, claimData);
      
      const claimId = response.data.id;
      
      // Upload documents if any
      if (newClaimDocuments.length > 0) {
        for (const doc of newClaimDocuments) {
          await uploadDocument([doc.file], `Document voor claim`, 'claim', claimId);
        }
      }
      
      setNewClaim({
        polis_id: '',
        omschrijving: '',
        schade_datum: new Date(),
        gemeld_datum: new Date(),
        schadebedrag: 0,
        eigen_risico: 0,
        status: 'ingediend',
        opmerking: ''
      });
      setNewClaimDocuments([]);
      
      fetchData();
      toast.success('Claim succesvol ingediend met documenten');
    } catch (error) {
      console.error('Error creating claim:', error);
      toast.error(error.response?.data?.detail || 'Fout bij indienen claim');
    }
  };

  const createUser = async () => {
    try {
      if (!newUser.naam || !newUser.email || !newUser.password) {
        toast.error('Vul alle verplichte velden in');
        return;
      }
      
      const userData = {
        naam: newUser.naam,
        email: newUser.email,
        password: newUser.password,
        rol: newUser.rol,
        bedrijf_ids: newUser.bedrijf_ids
      };
      
      await axios.post(`${API}/auth/register`, userData);
      setNewUser({
        naam: '',
        email: '',
        password: '',
        rol: 'werknemer',
        bedrijf_ids: []
      });
      fetchData();
      // Fetch families for the updated user list
      setTimeout(() => fetchUserFamilies(), 500);
      toast.success('Gebruiker succesvol aangemaakt');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fout bij aanmaken gebruiker');
    }
  };

  const createMaintenance = async () => {
    try {
      const maintenanceData = { 
        ...newMaintenance, 
        voertuig_id: addingMaintenance,
        datum: newMaintenance.datum ? format(new Date(newMaintenance.datum), "yyyy-MM-dd") : format(new Date(), "yyyy-MM-dd")
      };
      await axios.post(`${API}/onderhoud?bedrijf_id=${selectedBedrijf}`, maintenanceData, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      setNewMaintenance({
        type: 'onderhoud',
        datum: new Date(),
        kilometerstand: 0,
        kosten: 0,
        omschrijving: '',
        garage: '',
        factuur_nummer: ''
      });
      
      setAddingMaintenance(null);
      // Refresh the maintenance view
      viewMaintenance(addingMaintenance);
      toast.success('Onderhoud succesvol toegevoegd');
    } catch (error) {
      console.error('Error creating maintenance:', error);
      
      // Handle validation errors
      let errorMessage = 'Fout bij toevoegen onderhoud';
      if (error.response?.data?.detail) {
        if (Array.isArray(error.response.data.detail)) {
          // Handle FastAPI validation errors
          errorMessage = error.response.data.detail.map(err => err.msg).join(', ');
        } else {
          errorMessage = error.response.data.detail;
        }
      }
      
      toast.error(errorMessage);
    }
  };

  const exportEvaluatiePDF = async (evaluatieId) => {
    try {
      toast.loading('PDF wordt gegenereerd...');
      
      const response = await axios.get(`${API}/evaluaties/${evaluatieId}/pdf`, {
        responseType: 'blob',
        headers: { 'Authorization': `Bearer ${token}` },
        timeout: 30000 // 30 second timeout
      });
      
      // Check if the response is actually a PDF
      if (response.headers['content-type'] !== 'application/pdf' && !response.headers['content-type']?.includes('pdf')) {
        throw new Error('Ongeldig bestandstype ontvangen');
      }
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      
      // Check if blob is not empty
      if (blob.size === 0) {
        throw new Error('Leeg PDF bestand ontvangen');
      }
      
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `evaluatie_${evaluatieId.slice(0, 8)}.pdf`;
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.dismiss(); // Remove loading toast
      toast.success('PDF succesvol gedownload');
    } catch (error) {
      toast.dismiss(); // Remove loading toast
      console.error('PDF export error:', error);
      
      if (error.code === 'ECONNABORTED') {
        toast.error('PDF generatie timeout - probeer opnieuw');
      } else if (error.response?.status === 404) {
        toast.error('Evaluatie niet gevonden');
      } else if (error.response?.status === 403) {
        toast.error('Geen toegang tot deze evaluatie');
      } else {
        toast.error(error.message || error.response?.data?.detail || 'Fout bij downloaden PDF');
      }
    }
  };

  // CRUD Functions
  const viewItem = async (type, id) => {
    try {
      let endpoint = '';
      switch (type) {
        case 'evaluatie':
          endpoint = `evaluaties/${id}`;
          break;
        case 'claim':
          endpoint = `claims/${id}`;
          break;
        case 'voertuig':
          endpoint = `voertuigen/${id}`;
          break;
        case 'voordeel':
          endpoint = `voordelen/${id}`;
          break;
        case 'verzekering':
          endpoint = `verzekeringen/${id}`;
          break;
        case 'contract':
          endpoint = `contracts/${id}`;
          break;
        case 'license':
          endpoint = `licenses/${id}`;
          break;
        case 'template':
          endpoint = `evaluatie-templates/${id}`;
          break;
        case 'bedrijf':
          endpoint = `bedrijven/${id}`;
          break;
        default:
          return;
      }

      const response = await axios.get(`${API}/${endpoint}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      let itemData = response.data;
      
      // For evaluations, also fetch the template to get question details
      if (type === 'evaluatie' && itemData.template_id) {
        try {
          const templateResponse = await axios.get(`${API}/evaluatie-templates/${itemData.template_id}`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          itemData.template = templateResponse.data;
        } catch (templateError) {
          console.error('Error fetching template:', templateError);
          // Continue without template data
        }
      }

      // For claims, also fetch comments and documents
      if (type === 'claim') {
        fetchClaimComments(id);
        fetchAllDocuments();
      }

      setViewingItem({ type, data: itemData });
    } catch (error) {
      console.error('Error viewing item:', error);
      toast.error("Kan item niet laden.");
    }
  };

  const editItem = (type, item) => {
    setEditingItem({ type, id: item.id });
    setEditData(item);
    
    // Fetch comments if editing a claim
    if (type === 'claim') {
      fetchClaimComments(item.id);
    }
  };

  const updateItem = async (type, id, data) => {
    try {
      let endpoint = '';
      let updateData = { ...data };
      
      switch (type) {
        case 'bedrijf':
          endpoint = `bedrijven/${id}`;
          updateData = { naam: data.naam, beschrijving: data.beschrijving };
          break;
        case 'evaluatie':
          endpoint = `evaluaties/${id}`;
          break;
        case 'voertuig':
          endpoint = `voertuigen/${id}`;
          delete updateData.id;
          delete updateData.bedrijf_id;
          delete updateData.aangemaakt_op;
          break;
        case 'voordeel':
          endpoint = `voordelen/${id}?bedrijf_id=${selectedBedrijf}`;
          delete updateData.id;
          delete updateData.bedrijf_id;
          delete updateData.aangemaakt_op;
          break;
        case 'verzekering':
          endpoint = `verzekeringen/${id}`;
          delete updateData.id;
          delete updateData.bedrijf_id;
          delete updateData.aangemaakt_op;
          break;
        case 'contract':
          endpoint = `contracts/${id}`;
          // Format contract data properly before updating
          updateData = {
            naam: data.naam?.trim(),
            type: data.type,
            partij: data.partij?.trim(),
            maandelijks_bedrag: typeof data.maandelijks_bedrag === 'string' ? parseFloat(data.maandelijks_bedrag) || 0 : data.maandelijks_bedrag || 0,
            jaarlijks_bedrag: typeof data.jaarlijks_bedrag === 'string' ? parseFloat(data.jaarlijks_bedrag) || 0 : data.jaarlijks_bedrag || 0,
            begindatum: data.begindatum ? new Date(data.begindatum).toISOString().split('T')[0] : null,
            einddatum: data.einddatum ? new Date(data.einddatum).toISOString().split('T')[0] : null,
            opmerkingen: data.opmerkingen || ''
          };
          break;
        case 'license':
          endpoint = `licenses/${id}`;
          // Format license data properly
          updateData = {
            naam: data.naam?.trim(),
            leverancier: data.leverancier?.trim(),
            type: data.type,
            licentie_sleutel: data.licentie_sleutel?.trim(),
            gebruikers_limiet: typeof data.gebruikers_limiet === 'string' ? parseInt(data.gebruikers_limiet) || 0 : data.gebruikers_limiet || 0,
            kosten_per_jaar: typeof data.kosten_per_jaar === 'string' ? parseFloat(data.kosten_per_jaar) || 0 : data.kosten_per_jaar || 0,
            verloopdatum: data.verloopdatum ? new Date(data.verloopdatum).toISOString().split('T')[0] : null,
            opmerkingen: data.opmerkingen || ''
          };
          break;
        case 'template':
          endpoint = `evaluatie-templates/${id}`;
          break;
        case 'claim':
          endpoint = `claims/${id}`;
          delete updateData.id;
          delete updateData.claimnummer;
          delete updateData.behandelaar;
          delete updateData.documenten;
          delete updateData.aangemaakt_op;
          delete updateData.laatste_update;
          break;
        case 'gebruiker':
          endpoint = `users/${id}`;
          break;
        default:
          return;
      }

      await axios.put(`${API}/${endpoint}`, updateData, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      setEditingItem(null);
      setEditData({});
      fetchData();

      toast.success("Item succesvol bijgewerkt.");
    } catch (error) {
      console.error('Error updating item:', error);
      toast.error(error.response?.data?.detail || "Kan item niet bijwerken.");
    }
  };

  const deleteItem = async (type, id) => {
    try {
      let endpoint = '';
      switch (type) {
        case 'bedrijf':
          endpoint = `bedrijven/${id}`;
          break;
        case 'evaluatie':
          endpoint = `evaluaties/${id}`;
          break;
        case 'voertuig':
          endpoint = `voertuigen/${id}`;
          break;
        case 'voordeel':
          endpoint = `voordelen/${id}`;
          break;
        case 'verzekering':
          endpoint = `verzekeringen/${id}`;
          break;
        case 'claim':
          endpoint = `claims/${id}`;
          break;
        case 'license':
          endpoint = `licenses/${id}`;
          break;
        case 'template':
          endpoint = `evaluatie-templates/${id}`;
          break;
        case 'gebruiker':
          endpoint = `users/${id}`;
          break;
        default:
          return;
      }

      await axios.delete(`${API}/${endpoint}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      setDeleteConfirm(null);
      
      // Refresh specific data based on type
      switch (type) {
        case 'license':
          await fetchLicenses();
          break;
        case 'contract':
          await fetchContracts();
          break;
        case 'gebruiker':
          setAllUsers(prevUsers => prevUsers.filter(u => u.id !== id));
          break;
        default:
          fetchData(); // General refresh for other types
          break;
      }

      toast.success("Item succesvol verwijderd.");
    } catch (error) {
      console.error('Error deleting item:', error);
      toast.error(error.response?.data?.detail || "Kan item niet verwijderen.");
    }
  };

  const viewMaintenance = async (voertuigId) => {
    try {
      const response = await axios.get(`${API}/onderhoud/voertuig/${voertuigId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      setViewingItem({ 
        type: 'onderhoud', 
        data: {
          voertuig_id: voertuigId,
          onderhoud: response.data
        }
      });
    } catch (error) {
      console.error('Error viewing maintenance:', error);
      toast.error("Kan onderhoud niet laden.");
    }
  };

  const viewInsuranceClaims = async (polisId) => {
    try {
      const response = await axios.get(`${API}/claims?polis_id=${polisId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      setViewingItem({ 
        type: 'insurance_claims', 
        data: {
          polis_id: polisId,
          claims: response.data
        }
      });
    } catch (error) {
      console.error('Error viewing insurance claims:', error);
      toast.error("Kan claims niet laden.");
    }
  };

  const startEditingUserRights = (targetUser) => {
    console.log('startEditingUserRights called for:', targetUser.naam);
    setEditingUserRights(targetUser);
    setUserRightsData({
      naam: targetUser.naam,
      rol: targetUser.rol,
      bedrijf_ids: targetUser.bedrijf_ids || [],
      toegestane_tabs: targetUser.toegestane_tabs || ["evaluaties", "voertuigen", "voordelen", "verzekeringen", "claims"],
      kan_tab_info_bewerken: targetUser.kan_tab_info_bewerken || false,
      opmerking: ''
    });
    console.log('editingUserRights state set to:', targetUser);
  };

  const updateUserRights = async () => {
    try {
      const updateData = {
        gebruiker_id: editingUserRights.id,
        naam: userRightsData.naam,
        rol: userRightsData.rol,
        bedrijf_ids: userRightsData.bedrijf_ids,
        kan_tab_info_bewerken: userRightsData.kan_tab_info_bewerken,
        opmerking: userRightsData.opmerking
      };

      await axios.put(`${API}/admin/users/${editingUserRights.id}/rights`, updateData, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      // Update local state
      setAllUsers(prevUsers => 
        prevUsers.map(u => 
          u.id === editingUserRights.id 
            ? { ...u, rol: userRightsData.rol, bedrijf_ids: userRightsData.bedrijf_ids }
            : u
        )
      );

      setEditingUserRights(null);
      setUserRightsData({ naam: '', rol: '', bedrijf_ids: [], kan_tab_info_bewerken: false, opmerking: '' });
      
      toast.success('Gebruikersrechten succesvol bijgewerkt');
    } catch (error) {
      console.error('Error updating user rights:', error);
      toast.error(error.response?.data?.detail || 'Fout bij bijwerken gebruikersrechten');
    }
  };

  const kloonTemplate = async (template) => {
    try {
      const response = await axios.post(`${API}/evaluatie-templates/${template.id}/klonen?bedrijf_id=${selectedBedrijf}`, {}, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      fetchData(); // Refresh templates list
      toast.success(`Template "${template.naam}" succesvol gekloond`);
    } catch (error) {
      console.error('Error cloning template:', error);
      toast.error(error.response?.data?.detail || 'Fout bij klonen template');
    }
  };

  const startFillingEvaluation = async (evaluatie) => {
    try {
      // Get the template to show questions
      const templateResponse = await axios.get(`${API}/evaluatie-templates/${evaluatie.template_id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      setFillingEvaluation({
        ...evaluatie,
        template: templateResponse.data
      });
      
      // Reset evaluation mode to default when starting new evaluation
      setEvaluationMode('zelfevaluatie');
      
      // Initialize answers with existing data or empty (will be updated when mode changes)
      const loadAnswersForMode = (mode) => {
        const existingAnswers = mode === 'zelfevaluatie' ? 
          (evaluatie.zelfevaluatie || []) : 
          (evaluatie.manager_beoordeling || []);
        
        return templateResponse.data.vragen.map(vraag => {
          const existing = existingAnswers.find(a => a.vraag_id === vraag.id);
          return {
            vraag_id: vraag.id,
            score: existing?.score || 1,
            opmerking: existing?.opmerking || '',
            doelstelling: existing?.doelstelling || ''
          };
        });
      };
      
      const initialAnswers = loadAnswersForMode('zelfevaluatie');
      
      setEvaluationAnswers(initialAnswers);
    } catch (error) {
      console.error('Error starting evaluation:', error);
      toast.error("Kan evaluatie niet laden.");
    }
  };

  const editTemplateQuestions = (template) => {
    setEditingTemplateQuestions(template);
    setTemplateQuestions(template.vragen || []);
  };

  const addQuestion = () => {
    const newQuestion = {
      id: `temp-${Date.now()}`,
      tekst: '',
      sectie: '',
      volgorde: templateQuestions.length + 1,
      verplicht: true
    };
    setTemplateQuestions([...templateQuestions, newQuestion]);
  };

  const updateQuestion = (index, field, value) => {
    const updated = [...templateQuestions];
    updated[index][field] = value;
    setTemplateQuestions(updated);
  };

  const removeQuestion = (index) => {
    const updated = templateQuestions.filter((_, i) => i !== index);
    // Re-order the remaining questions
    updated.forEach((q, i) => q.volgorde = i + 1);
    setTemplateQuestions(updated);
  };
  const getStatusBadge = (status) => {
    const statusMap = {
      'nieuw': { label: 'Nieuw', color: 'bg-blue-100 text-blue-800' },
      'zelfevaluatie': { label: 'Zelfevaluatie', color: 'bg-yellow-100 text-yellow-800' },
      'manager_beoordeling': { label: 'Manager beoordeling', color: 'bg-orange-100 text-orange-800' },
      'goedgekeurd': { label: 'Goedgekeurd', color: 'bg-green-100 text-green-800' },
      'afgerond': { label: 'Afgerond', color: 'bg-gray-100 text-gray-800' }
    };
    
    const statusInfo = statusMap[status] || { label: status, color: 'bg-gray-100 text-gray-800' };
    return <Badge className={statusInfo.color}>{statusInfo.label}</Badge>;
  };

  const getVoertuigTypeBadge = (type) => {
    const typeMap = {
      'lease': { label: 'Lease', color: 'bg-blue-100 text-blue-800' },
      'eigen': { label: 'Eigen', color: 'bg-green-100 text-green-800' },
      'pool': { label: 'Pool', color: 'bg-purple-100 text-purple-800' }
    };
    
    const typeInfo = typeMap[type] || { label: type, color: 'bg-gray-100 text-gray-800' };
    return <Badge className={typeInfo.color}>{typeInfo.label}</Badge>;
  };

  const getVoordeelTypeBadge = (type) => {
    const typeMap = {
      'fietslease': { label: 'Fietslease', color: 'bg-green-100 text-green-800' },
      'groepsverzekering': { label: 'Groepsverzekering', color: 'bg-blue-100 text-blue-800' },
      'hospitaliteit': { label: 'Hospitaliteit', color: 'bg-purple-100 text-purple-800' },
      'mobiliteitsbudget': { label: 'Mobiliteitsbudget', color: 'bg-orange-100 text-orange-800' }
    };
    
    const typeInfo = typeMap[type] || { label: type, color: 'bg-gray-100 text-gray-800' };
    return <Badge className={typeInfo.color}>{typeInfo.label}</Badge>;
  };

  const getClaimStatusBadge = (status) => {
    const statusMap = {
      'ingediend': { label: 'Ingediend', color: 'bg-blue-100 text-blue-800' },
      'in_behandeling': { label: 'In behandeling', color: 'bg-yellow-100 text-yellow-800' },
      'goedgekeurd': { label: 'Goedgekeurd', color: 'bg-green-100 text-green-800' },
      'afgewezen': { label: 'Afgewezen', color: 'bg-red-100 text-red-800' },
      'uitbetaald': { label: 'Uitbetaald', color: 'bg-gray-100 text-gray-800' }
    };
    
    const statusInfo = statusMap[status] || { label: status, color: 'bg-gray-100 text-gray-800' };
    return <Badge className={statusInfo.color}>{statusInfo.label}</Badge>;
  };

  const getVerzekeringTypeBadge = (type) => {
    const typeMap = {
      'ba': { label: 'BA Verzekering', color: 'bg-blue-100 text-blue-800' },
      'brand': { label: 'Brandverzekering', color: 'bg-red-100 text-red-800' },
      'cyber': { label: 'Cyberverzekering', color: 'bg-purple-100 text-purple-800' },
      'voertuig': { label: 'Voertuigverzekering', color: 'bg-green-100 text-green-800' },
      'groepsverzekering': { label: 'Groepsverzekering', color: 'bg-orange-100 text-orange-800' },
      'aansprakelijkheid': { label: 'Aansprakelijkheid', color: 'bg-teal-100 text-teal-800' }
    };
    
    const typeInfo = typeMap[type] || { label: type, color: 'bg-gray-100 text-gray-800' };
    return <Badge className={typeInfo.color}>{typeInfo.label}</Badge>;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Gegevens laden...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-4">
              <h1 className="text-xl font-semibold text-gray-900">HR Evaluatiesysteem</h1>
              {bedrijven.length > 0 && (
                <Select value={selectedBedrijf} onValueChange={handleBedrijfChange}>
                  <SelectTrigger className="w-48">
                    <SelectValue placeholder="Selecteer bedrijf" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="alle">Alle bedrijven</SelectItem>
                    {bedrijven.map(bedrijf => (
                      <SelectItem key={bedrijf.id} value={bedrijf.id}>
                        {bedrijf.naam}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
            <div className="flex items-center space-x-4">
              <span className="text-sm text-gray-700">Welkom, {user?.naam}</span>
              <Button variant="outline" onClick={logout}>
                Uitloggen
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-full mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 2xl:grid-cols-8 gap-4 mb-8">
          <Card 
            className="hover:shadow-lg transition-all duration-200 cursor-pointer hover:scale-105" 
            onClick={() => setActiveTab('evaluaties')}
          >
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Evaluaties</CardTitle>
              <FileText className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.total_evaluaties || 0}</div>
              <p className="text-xs text-muted-foreground mt-1">Klik om te bekijken</p>
            </CardContent>
          </Card>
          
          {/* Hidden - "Actief" and "Afgerond" cards removed per user request */}
          {/*
          <Card 
            className="hover:shadow-lg transition-all duration-200 cursor-pointer hover:scale-105" 
            onClick={() => setActiveTab('evaluaties')}
          >
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Actief</CardTitle>
              <Clock className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.actieve_evaluaties || 0}</div>
              <p className="text-xs text-muted-foreground mt-1">Actieve evaluaties</p>
            </CardContent>
          </Card>
          
          <Card 
            className="hover:shadow-lg transition-all duration-200 cursor-pointer hover:scale-105" 
            onClick={() => setActiveTab('evaluaties')}
          >
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Afgerond</CardTitle>
              <CheckCircle className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.afgeronde_evaluaties || 0}</div>
              <p className="text-xs text-muted-foreground mt-1">Afgeronde evaluaties</p>
            </CardContent>
          </Card>
          */}
          
          {/* Hidden - "Templates" card moved to Instellingen per user request */}
          {/*
          <Card 
            className="hover:shadow-lg transition-all duration-200 cursor-pointer hover:scale-105" 
            onClick={() => setActiveTab('instellingen')}
          >
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Templates</CardTitle>
              <Star className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.templates_count || 0}</div>
              <p className="text-xs text-muted-foreground mt-1">Klik om te beheren</p>
            </CardContent>
          </Card>
          */}

          <Card 
            className="hover:shadow-lg transition-all duration-200 cursor-pointer hover:scale-105" 
            onClick={() => setActiveTab('voertuigen')}
          >
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Voertuigen</CardTitle>
              <Car className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.voertuigen_count || 0}</div>
              <p className="text-xs text-muted-foreground mt-1">Klik om te bekijken</p>
            </CardContent>
          </Card>

          <Card 
            className="hover:shadow-lg transition-all duration-200 cursor-pointer hover:scale-105" 
            onClick={() => setActiveTab('voordelen')}
          >
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Voordelen</CardTitle>
              <Gift className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.voordelen_count || 0}</div>
              <p className="text-xs text-muted-foreground mt-1">Klik om te bekijken</p>
            </CardContent>
          </Card>

          <Card 
            className="hover:shadow-lg transition-all duration-200 cursor-pointer hover:scale-105" 
            onClick={() => setActiveTab('verzekeringen')}
          >
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Verzekeringen</CardTitle>
              <Shield className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.verzekeringen_count || 0}</div>
              <p className="text-xs text-muted-foreground mt-1">Klik om te bekijken</p>
            </CardContent>
          </Card>

          <Card 
            className="hover:shadow-lg transition-all duration-200 cursor-pointer hover:scale-105" 
            onClick={() => setActiveTab('claims')}
          >
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Claims</CardTitle>
              <AlertTriangle className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.actieve_claims_count || 0}</div>
              <p className="text-xs text-muted-foreground mt-1">Klik om te bekijken</p>
            </CardContent>
          </Card>

          <Card 
            className="hover:shadow-lg transition-all duration-200 cursor-pointer hover:scale-105" 
            onClick={() => setActiveTab('updates')}
          >
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Updates</CardTitle>
              <Bell className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{updates.length}</div>
              <p className="text-xs text-muted-foreground mt-1">Klik om te bekijken</p>
            </CardContent>
          </Card>

          {/* New Cards for previously tab-only sections */}
          {(user?.rol === 'admin' || user?.rol === 'hr') && (
            <Card 
              className="hover:shadow-lg transition-all duration-200 cursor-pointer hover:scale-105" 
              onClick={() => setActiveTab('gebruikers')}
            >
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Gebruikers</CardTitle>
                <Users className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{users.length}</div>
                <p className="text-xs text-muted-foreground mt-1">Klik om te beheren</p>
              </CardContent>
            </Card>
          )}

          {(user?.rol === 'admin' || user?.rol === 'hr') && (
            <Card 
              className="hover:shadow-lg transition-all duration-200 cursor-pointer hover:scale-105" 
              onClick={() => setActiveTab('bedrijven')}
            >
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Bedrijven</CardTitle>
                <Building2 className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{bedrijven.length}</div>
                <p className="text-xs text-muted-foreground mt-1">Klik om te beheren</p>
              </CardContent>
            </Card>
          )}

          {user?.toegestane_tabs?.includes('licenties') && (
            <Card 
              className="hover:shadow-lg transition-all duration-200 cursor-pointer hover:scale-105" 
              onClick={() => setActiveTab('licenties')}
            >
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Licenties</CardTitle>
                <Key className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{licenses.length}</div>
                <p className="text-xs text-muted-foreground mt-1">Klik om te bekijken</p>
              </CardContent>
            </Card>
          )}

          {user?.toegestane_tabs?.includes('contracten') && (
            <Card 
              className="hover:shadow-lg transition-all duration-200 cursor-pointer hover:scale-105" 
              onClick={() => setActiveTab('contracten')}
            >
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Contracten</CardTitle>
                <FileCheck className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{contracts.length}</div>
                <p className="text-xs text-muted-foreground mt-1">Klik om te bekijken</p>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Main Content */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="grid w-full grid-cols-1">
            {user?.rol === 'admin' && <TabsTrigger value="instellingen">Instellingen</TabsTrigger>}
          </TabsList>

          {/* Evaluaties */}
          <TabsContent value="evaluaties" className="space-y-6">
            {/* Editable Tab Info */}
            <EditableTabInfo 
              tabName="evaluaties"
              user={user}
              tabInfos={tabInfos}
              editingTabInfo={editingTabInfo}
              setEditingTabInfo={setEditingTabInfo}
              updateTabInfo={updateTabInfo}
              fetchTabInfo={fetchTabInfo}
            />
            
            <div className="flex justify-between items-center">
              <div className="flex items-center gap-2">
                <h2 className="text-lg font-semibold">Evaluaties</h2>
                <InfoTooltip content="Evaluaties werken als volgt:
• Maak eerst een template aan met evaluatievragen
• Creëer een evaluatie voor een werknemer op basis van een template  
• De werknemer kan de evaluatie invullen (📄 knop) met scores, opmerkingen en doelstellingen
• De manager kan daarna ook de evaluatie invullen (📄 knop) voor zijn beoordeling
• Managers zien zowel de zelf-evaluatie als kunnen hun eigen scores en feedback geven
• Bekijk complete resultaten via het oog (👁️) icoon - toont beide evaluaties
• Exporteer naar PDF voor rapportage (↓ knop)
• Scores: 1-5 of 'Niet van toepassing' voor elke vraag">
                  <Info className="h-4 w-4 text-gray-400 cursor-help" />
                </InfoTooltip>
              </div>
              <Dialog>
                <DialogTrigger asChild>
                  <Button>
                    <Plus className="h-4 w-4 mr-2" />
                    Nieuwe Evaluatie
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Nieuwe Evaluatie Aanmaken</DialogTitle>
                    <DialogDescription>
                      Maak een nieuwe evaluatie aan voor een werknemer.
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div>
                      <Label>Template</Label>
                      <Select value={newEvaluatie.template_id} onValueChange={(value) => setNewEvaluatie({...newEvaluatie, template_id: value})}>
                        <SelectTrigger>
                          <SelectValue placeholder="Selecteer template" />
                        </SelectTrigger>
                        <SelectContent>
                          {templates.map(template => (
                            <SelectItem key={template.id} value={template.id}>
                              {template.naam}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Werknemer</Label>
                      <Select value={newEvaluatie.werknemer_id} onValueChange={(value) => setNewEvaluatie({...newEvaluatie, werknemer_id: value})}>
                        <SelectTrigger>
                          <SelectValue placeholder="Selecteer werknemer" />
                        </SelectTrigger>
                        <SelectContent>
                          {users.filter(u => u.rol === 'werknemer' || u.rol === 'manager').map(user => (
                            <SelectItem key={user.id} value={user.id}>
                              {user.naam} ({user.email})
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Manager</Label>
                      <Select value={newEvaluatie.manager_id} onValueChange={(value) => setNewEvaluatie({...newEvaluatie, manager_id: value})}>
                        <SelectTrigger>
                          <SelectValue placeholder="Selecteer manager" />
                        </SelectTrigger>
                        <SelectContent>
                          {users.filter(u => u.rol === 'manager' || u.rol === 'hr' || u.rol === 'admin').map(user => (
                            <SelectItem key={user.id} value={user.id}>
                              {user.naam} ({user.email})
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Type</Label>
                      <Select value={newEvaluatie.type} onValueChange={(value) => setNewEvaluatie({...newEvaluatie, type: value})}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="initieel">Initiële evaluatie</SelectItem>
                          <SelectItem value="periodiek">Periodieke evaluatie</SelectItem>
                          <SelectItem value="tussentijds">Tussentijdse feedback</SelectItem>
                          <SelectItem value="verbeterplan">Verbeterplan</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Deadline</Label>
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button variant="outline" className="w-full justify-start text-left font-normal">
                            <CalendarIcon className="mr-2 h-4 w-4" />
                            {newEvaluatie.deadline ? format(newEvaluatie.deadline, "PPP", { locale: nl }) : "Selecteer datum"}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0">
                          <Calendar
                            mode="single"
                            selected={newEvaluatie.deadline}
                            onSelect={(date) => setNewEvaluatie({...newEvaluatie, deadline: date})}
                            initialFocus
                          />
                        </PopoverContent>
                      </Popover>
                    </div>
                  </div>
                  <DialogFooter>
                    <Button onClick={createEvaluatie}>Aanmaken</Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>

            <div className="grid gap-4">
              {evaluaties.map(evaluatie => (
                <Card key={evaluatie.id}>
                  <CardContent className="p-6">
                    <div className="flex justify-between items-start">
                      <div className="space-y-2">
                        <div className="flex items-center space-x-2">
                          <h3 className="font-semibold">Evaluatie #{evaluatie.id.slice(0, 8)}</h3>
                          {getStatusBadge(evaluatie.status)}
                        </div>
                        <p className="text-sm text-gray-600">
                          Type: {evaluatie.type} | Deadline: {format(new Date(evaluatie.deadline), "PPP", { locale: nl })}
                        </p>
                      </div>
                      <div className="flex space-x-2">
                        <Button 
                          variant="outline" 
                          size="sm" 
                          onClick={() => exportEvaluatiePDF(evaluatie.id)}
                        >
                          <Download className="h-4 w-4" />
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => viewItem('evaluatie', evaluatie.id)}>
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => startFillingEvaluation(evaluatie)}>
                          <FileText className="h-4 w-4" />
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => editItem('evaluatie', evaluatie)}>
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => setDeleteConfirm({ type: 'evaluatie', id: evaluatie.id, name: `Evaluatie #${evaluatie.id.slice(0, 8)}` })}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          {/* Voertuigen */}
          <TabsContent value="voertuigen" className="space-y-6">
            {/* Editable Tab Info */}
            <EditableTabInfo 
              tabName="voertuigen"
              user={user}
              tabInfos={tabInfos}
              editingTabInfo={editingTabInfo}
              setEditingTabInfo={setEditingTabInfo}
              updateTabInfo={updateTabInfo}
              fetchTabInfo={fetchTabInfo}
            />
            
            <div className="flex justify-between items-center">
              <div className="flex items-center space-x-2">
                <h2 className="text-lg font-semibold">Voertuigen</h2>
                <InfoTooltip content="Beheer het wagenpark van het bedrijf. Registreer lease-, eigen- en poolvoertuigen. Houd kilometerstand bij en wijs bestuurders toe. Gebruik 'Geen bestuurder' voor poolwagens.">
                  <Info className="h-4 w-4 text-gray-400 cursor-help" />
                </InfoTooltip>
              </div>
              <Dialog>
                <DialogTrigger asChild>
                  <Button>
                    <Plus className="h-4 w-4 mr-2" />
                    Nieuw Voertuig
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl">
                  <DialogHeader>
                    <DialogTitle>Nieuw Voertuig Aanmaken</DialogTitle>
                    <DialogDescription>
                      Voeg een nieuw voertuig toe aan het wagenpark.
                    </DialogDescription>
                  </DialogHeader>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Bedrijf</Label>
                      <Select value={newVoertuig.bedrijf_id || selectedBedrijf} onValueChange={(value) => setNewVoertuig({...newVoertuig, bedrijf_id: value})}>
                        <SelectTrigger>
                          <SelectValue placeholder="Selecteer bedrijf" />
                        </SelectTrigger>
                        <SelectContent>
                          {bedrijven.map(bedrijf => (
                            <SelectItem key={bedrijf.id} value={bedrijf.id}>
                              {bedrijf.naam}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Kenteken</Label>
                      <Input
                        value={newVoertuig.kenteken}
                        onChange={(e) => setNewVoertuig({...newVoertuig, kenteken: e.target.value.toUpperCase()})}
                        placeholder="XX-XX-XX"
                      />
                    </div>
                    <div>
                      <Label>Merk</Label>
                      <Input
                        value={newVoertuig.merk}
                        onChange={(e) => setNewVoertuig({...newVoertuig, merk: e.target.value})}
                        placeholder="Toyota"
                      />
                    </div>
                    <div>
                      <Label>Model</Label>
                      <Input
                        value={newVoertuig.model}
                        onChange={(e) => setNewVoertuig({...newVoertuig, model: e.target.value})}
                        placeholder="Corolla"
                      />
                    </div>
                    <div>
                      <Label>Type</Label>
                      <Select value={newVoertuig.type} onValueChange={(value) => setNewVoertuig({...newVoertuig, type: value})}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="lease">Lease</SelectItem>
                          <SelectItem value="eigen">Eigen</SelectItem>
                          <SelectItem value="pool">Pool</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Kilometerstand</Label>
                      <Input
                        type="number"
                        value={newVoertuig.kilometerstand}
                        onChange={(e) => setNewVoertuig({...newVoertuig, kilometerstand: parseInt(e.target.value) || 0})}
                        placeholder="50000"
                      />
                    </div>
                    <div>
                      <Label>Brandstoftype</Label>
                      <Select value={newVoertuig.brandstoftype} onValueChange={(value) => setNewVoertuig({...newVoertuig, brandstoftype: value})}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="benzine">Benzine</SelectItem>
                          <SelectItem value="diesel">Diesel</SelectItem>
                          <SelectItem value="elektrisch">Elektrisch</SelectItem>
                          <SelectItem value="hybride">Hybride</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Bestuurder</Label>
                      <Select value={newVoertuig.bestuurder_id} onValueChange={(value) => setNewVoertuig({...newVoertuig, bestuurder_id: value})}>
                        <SelectTrigger>
                          <SelectValue placeholder="Selecteer bestuurder" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="geen">Geen bestuurder</SelectItem>
                          {users.map(user => (
                            <SelectItem key={user.id} value={user.id}>
                              {user.naam}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Eerste Inschrijving</Label>
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button variant="outline" className="w-full justify-start text-left font-normal">
                            <CalendarIcon className="mr-2 h-4 w-4" />
                            {newVoertuig.eerste_inschrijving ? format(newVoertuig.eerste_inschrijving, "PPP", { locale: nl }) : "Selecteer datum"}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0">
                          <Calendar
                            mode="single"
                            selected={newVoertuig.eerste_inschrijving}
                            onSelect={(date) => setNewVoertuig({...newVoertuig, eerste_inschrijving: date})}
                            initialFocus
                          />
                        </PopoverContent>
                      </Popover>
                    </div>
                  </div>
                  <DialogFooter>
                    <Button onClick={createVoertuig}>Aanmaken</Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>

            <div className="grid gap-4">
              {voertuigen.map(voertuig => (
                <Card key={voertuig.id}>
                  <CardContent className="p-6">
                    <div className="flex justify-between items-start">
                      <div className="space-y-2">
                        <div className="flex items-center space-x-2">
                          <h3 className="font-semibold">{voertuig.kenteken}</h3>
                          {getVoertuigTypeBadge(voertuig.type)}
                        </div>
                        <p className="text-sm text-gray-600">
                          {voertuig.merk} {voertuig.model} | {voertuig.brandstoftype}
                          {(() => {
                            const bestuurder = users.find(user => user.id === voertuig.bestuurder_id);
                            return bestuurder ? ` | ${bestuurder.naam}` : '';
                          })()}
                        </p>
                        <p className="text-xs text-gray-500">
                          Kilometerstand: {voertuig.kilometerstand.toLocaleString()} km
                        </p>
                      </div>
                      <div className="flex space-x-2">
                        <Button variant="outline" size="sm" onClick={() => viewItem('voertuig', voertuig.id)}>
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => viewMaintenance(voertuig.id)}>
                          <Wrench className="h-4 w-4" />
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => editItem('voertuig', voertuig)}>
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => setDeleteConfirm({ type: 'voertuig', id: voertuig.id, name: voertuig.kenteken })}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          {/* Voordelen */}
          <TabsContent value="voordelen" className="space-y-6">
            {/* Editable Tab Info */}
            <EditableTabInfo 
              tabName="voordelen"
              user={user}
              tabInfos={tabInfos}
              editingTabInfo={editingTabInfo}
              setEditingTabInfo={setEditingTabInfo}
              updateTabInfo={updateTabInfo}
              fetchTabInfo={fetchTabInfo}
            />
            
            <div className="flex justify-between items-center">
              <h2 className="text-lg font-semibold">Voordelen</h2>
              <Dialog>
                <DialogTrigger asChild>
                  <Button>
                    <Plus className="h-4 w-4 mr-2" />
                    Nieuw Voordeel
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Nieuw Voordeel Aanmaken</DialogTitle>
                    <DialogDescription>
                      Wijs een voordeel toe aan een werknemer.
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div>
                      <Label>Werknemer</Label>
                      <Select value={newVoordeel.werknemer_id} onValueChange={(value) => setNewVoordeel({...newVoordeel, werknemer_id: value})}>
                        <SelectTrigger>
                          <SelectValue placeholder="Selecteer werknemer" />
                        </SelectTrigger>
                        <SelectContent>
                          {users.map(user => (
                            <SelectItem key={user.id} value={user.id}>
                              {user.naam} ({user.email})
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Type Voordeel</Label>
                      <Select value={newVoordeel.type} onValueChange={(value) => setNewVoordeel({...newVoordeel, type: value, voertuig_id: value === 'wagen' ? '' : null})}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="fietslease">Fietslease</SelectItem>
                          <SelectItem value="groepsverzekering">Groepsverzekering</SelectItem>
                          <SelectItem value="hospitaliteit">Hospitaliteit</SelectItem>
                          <SelectItem value="mobiliteitsbudget">Mobiliteitsbudget</SelectItem>
                          <SelectItem value="wagen">Wagen</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    {newVoordeel.type === 'wagen' && (
                      <div>
                        <Label>Selecteer Voertuig</Label>
                        <Select value={newVoordeel.voertuig_id} onValueChange={(value) => setNewVoordeel({...newVoordeel, voertuig_id: value})}>
                          <SelectTrigger>
                            <SelectValue placeholder="Kies een voertuig" />
                          </SelectTrigger>
                          <SelectContent>
                            {voertuigen.map(voertuig => (
                              <SelectItem key={voertuig.id} value={voertuig.id}>
                                {voertuig.kenteken} - {voertuig.merk} {voertuig.model}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                    <div>
                      <Label>Beschrijving</Label>
                      <Textarea
                        value={newVoordeel.beschrijving}
                        onChange={(e) => setNewVoordeel({...newVoordeel, beschrijving: e.target.value})}
                        placeholder="Beschrijving van het voordeel"
                      />
                    </div>
                    <div>
                      <Label>Waarde (€)</Label>
                      <Input
                        type="number"
                        value={newVoordeel.waarde}
                        onChange={(e) => setNewVoordeel({...newVoordeel, waarde: parseFloat(e.target.value) || 0})}
                        placeholder="250"
                      />
                    </div>
                    <div>
                      <Label>Startdatum</Label>
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button variant="outline" className="w-full justify-start text-left font-normal">
                            <CalendarIcon className="mr-2 h-4 w-4" />
                            {newVoordeel.startdatum ? format(newVoordeel.startdatum, "PPP", { locale: nl }) : "Selecteer datum"}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0">
                          <Calendar
                            mode="single"
                            selected={newVoordeel.startdatum}
                            onSelect={(date) => setNewVoordeel({...newVoordeel, startdatum: date})}
                            initialFocus
                          />
                        </PopoverContent>
                      </Popover>
                    </div>
                  </div>
                  <DialogFooter>
                    <Button onClick={createVoordeel}>Aanmaken</Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>

            <div className="grid gap-4">
              {voordelen.map(voordeel => {
                const werknemer = users.find(u => u.id === voordeel.werknemer_id);
                return (
                  <Card key={voordeel.id}>
                    <CardContent className="p-6">
                      <div className="flex justify-between items-start">
                        <div className="space-y-2">
                          <div className="flex items-center space-x-2">
                            <h3 className="font-semibold">{werknemer?.naam || 'Onbekend'}</h3>
                            {getVoordeelTypeBadge(voordeel.type)}
                          </div>
                          <p className="text-sm text-gray-600">{voordeel.beschrijving}</p>
                          <p className="text-xs text-gray-500">
                            Waarde: €{voordeel.waarde?.toFixed(2) || '0.00'} | 
                            Start: {format(new Date(voordeel.startdatum), "PPP", { locale: nl })}
                          </p>
                        </div>
                        <div className="flex space-x-2">
                          <Button variant="outline" size="sm" onClick={() => viewItem('voordeel', voordeel.id)}>
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button variant="outline" size="sm" onClick={() => editItem('voordeel', voordeel)}>
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button variant="outline" size="sm" onClick={() => setDeleteConfirm({ type: 'voordeel', id: voordeel.id, name: `${werknemer?.naam} - ${voordeel.type}` })}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </TabsContent>

          {/* Templates */}
          <TabsContent value="templates" className="space-y-6">
            <div className="flex justify-between items-center">
              <h2 className="text-lg font-semibold">Evaluatie Templates</h2>
              <Dialog>
                <DialogTrigger asChild>
                  <Button>
                    <Plus className="h-4 w-4 mr-2" />
                    Nieuwe Template
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Nieuwe Template Aanmaken</DialogTitle>
                    <DialogDescription>
                      Maak een nieuwe evaluatie template aan met voorgedefinieerde vragen.
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div>
                      <Label>Naam</Label>
                      <Input
                        value={newTemplate.naam}
                        onChange={(e) => setNewTemplate({...newTemplate, naam: e.target.value})}
                        placeholder="Template naam"
                      />
                    </div>
                    <div>
                      <Label>Beschrijving</Label>
                      <Textarea
                        value={newTemplate.beschrijving}
                        onChange={(e) => setNewTemplate({...newTemplate, beschrijving: e.target.value})}
                        placeholder="Template beschrijving"
                      />
                    </div>
                    <div>
                      <Label>Type</Label>
                      <Select value={newTemplate.type} onValueChange={(value) => setNewTemplate({...newTemplate, type: value})}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="initieel">Initiële evaluatie</SelectItem>
                          <SelectItem value="periodiek">Periodieke evaluatie</SelectItem>
                          <SelectItem value="tussentijds">Tussentijdse feedback</SelectItem>
                          <SelectItem value="verbeterplan">Verbeterplan</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Bedrijf</Label>
                      <Select value={newTemplate.bedrijf_id} onValueChange={(value) => setNewTemplate({...newTemplate, bedrijf_id: value})}>
                        <SelectTrigger>
                          <SelectValue placeholder="Selecteer bedrijf" />
                        </SelectTrigger>
                        <SelectContent>
                          {bedrijven.map(bedrijf => (
                            <SelectItem key={bedrijf.id} value={bedrijf.id}>
                              {bedrijf.naam}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <DialogFooter>
                    <Button onClick={createTemplate}>Aanmaken</Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>

            <div className="grid gap-4">
              {templates.map(template => (
                <Card key={template.id}>
                  <CardContent className="p-6">
                    <div className="flex justify-between items-start">
                      <div className="space-y-2">
                        <h3 className="font-semibold">{template.naam}</h3>
                        <p className="text-sm text-gray-600">{template.beschrijving}</p>
                        <div className="flex items-center space-x-2 text-sm text-gray-500">
                          <span>Type: {template.type}</span>
                          <span>•</span>
                          <span>{template.vragen?.length || 0} vragen</span>
                        </div>
                      </div>
                      <div className="flex space-x-2">
                        <Button variant="outline" size="sm" onClick={() => viewItem('template', template.id)}>
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => editTemplateQuestions(template)}>
                          <FileText className="h-4 w-4" />
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => editItem('template', template)}>
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => kloonTemplate(template)}>
                          <Copy className="h-4 w-4" />
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => setDeleteConfirm({ type: 'template', id: template.id, name: template.naam })}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          {/* Verzekeringen */}
          <TabsContent value="verzekeringen" className="space-y-6">
            {/* Editable Tab Info */}
            <EditableTabInfo 
              tabName="verzekeringen"
              user={user}
              tabInfos={tabInfos}
              editingTabInfo={editingTabInfo}
              setEditingTabInfo={setEditingTabInfo}
              updateTabInfo={updateTabInfo}
              fetchTabInfo={fetchTabInfo}
            />
            
            <div className="flex justify-between items-center">
              <div className="flex items-center space-x-2">
                <h2 className="text-lg font-semibold">Verzekeringen</h2>
                <InfoTooltip content="Beheer alle bedrijfsverzekeringen zoals BA, brand, cyber en voertuigverzekeringen. Het systeem stuurt automatisch herinneringen 30 dagen voor vervaldatum.">
                  <Info className="h-4 w-4 text-gray-400 cursor-help" />
                </InfoTooltip>
              </div>
              <Dialog>
                <DialogTrigger asChild>
                  <Button>
                    <Plus className="h-4 w-4 mr-2" />
                    Nieuwe Verzekering
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl">
                  <DialogHeader>
                    <DialogTitle>Nieuwe Verzekering Aanmaken</DialogTitle>
                    <DialogDescription>
                      Voeg een nieuwe verzekeringspolis toe.
                    </DialogDescription>
                  </DialogHeader>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Naam <span className="text-red-500">*</span></Label>
                      <Input
                        value={newVerzekering.naam}
                        onChange={(e) => setNewVerzekering({...newVerzekering, naam: e.target.value})}
                        placeholder="Bedrijfsaansprakelijkheid"
                      />
                    </div>
                    <div>
                      <Label>Type <span className="text-red-500">*</span></Label>
                      <Select value={newVerzekering.type} onValueChange={(value) => setNewVerzekering({...newVerzekering, type: value})}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="ba">BA Verzekering</SelectItem>
                          <SelectItem value="brand">Brandverzekering</SelectItem>
                          <SelectItem value="cyber">Cyberverzekering</SelectItem>
                          <SelectItem value="voertuig">Voertuigverzekering</SelectItem>
                          <SelectItem value="groepsverzekering">Groepsverzekering</SelectItem>
                          <SelectItem value="aansprakelijkheid">Aansprakelijkheid</SelectItem>
                          <SelectItem value="arbeidsongevallen">Arbeidsongevallen</SelectItem>
                          <SelectItem value="rechtsbijstand">Rechtsbijstand</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Verzekeraar <span className="text-red-500">*</span></Label>
                      <Input
                        value={newVerzekering.verzekeraar}
                        onChange={(e) => setNewVerzekering({...newVerzekering, verzekeraar: e.target.value})}
                        placeholder="Allianz"
                      />
                    </div>
                    <div>
                      <Label>Polisnummer <span className="text-red-500">*</span></Label>
                      <Input
                        value={newVerzekering.polisnummer}
                        onChange={(e) => setNewVerzekering({...newVerzekering, polisnummer: e.target.value})}
                        placeholder="POL-2024-001"
                      />
                    </div>
                    <div>
                      <Label>Jaarlijkse Premie (€) <span className="text-red-500">*</span></Label>
                      <Input
                        type="number"
                        value={newVerzekering.premie}
                        onChange={(e) => setNewVerzekering({...newVerzekering, premie: parseFloat(e.target.value) || 0})}
                        placeholder="1500"
                      />
                    </div>
                    <div>
                      <Label>Dekking <span className="text-red-500">*</span></Label>
                      <Input
                        value={newVerzekering.dekking}
                        onChange={(e) => setNewVerzekering({...newVerzekering, dekking: e.target.value})}
                        placeholder="€ 2.500.000"
                      />
                    </div>
                    <div>
                      <Label>Begindatum <span className="text-red-500">*</span></Label>
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button variant="outline" className="w-full justify-start text-left font-normal">
                            <CalendarIcon className="mr-2 h-4 w-4" />
                            {newVerzekering.begindatum ? format(newVerzekering.begindatum, "PPP", { locale: nl }) : "Selecteer datum"}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0">
                          <Calendar
                            mode="single"
                            selected={newVerzekering.begindatum}
                            onSelect={(date) => setNewVerzekering({...newVerzekering, begindatum: date})}
                            initialFocus
                          />
                        </PopoverContent>
                      </Popover>
                    </div>
                    <div>
                      <Label>Einddatum (optioneel)</Label>
                      <div className="flex gap-2">
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button variant="outline" className="flex-1 justify-start text-left font-normal">
                              <CalendarIcon className="mr-2 h-4 w-4" />
                              {newVerzekering.einddatum ? format(newVerzekering.einddatum, "PPP", { locale: nl }) : "Selecteer datum"}
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0">
                            <Calendar
                              mode="single"
                              selected={newVerzekering.einddatum}
                              onSelect={(date) => setNewVerzekering({...newVerzekering, einddatum: date})}
                              initialFocus
                            />
                          </PopoverContent>
                        </Popover>
                        {newVerzekering.einddatum && (
                          <Button 
                            variant="outline" 
                            size="sm" 
                            onClick={() => setNewVerzekering({...newVerzekering, einddatum: null})}
                          >
                            ×
                          </Button>
                        )}
                      </div>
                    </div>
                    <div className="col-span-2">
                      <Label>Opmerkingen</Label>
                      <Textarea
                        value={newVerzekering.opmerking}
                        onChange={(e) => setNewVerzekering({...newVerzekering, opmerking: e.target.value})}
                        placeholder="Optionele opmerkingen"
                      />
                    </div>
                    
                    {/* Document Upload Section */}
                    <div className="col-span-2">
                      <Label>Documenten Toevoegen</Label>
                      <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 mt-2">
                        <div className="text-center">
                          <Upload className="mx-auto h-8 w-8 text-gray-400" />
                          <div className="mt-2">
                            <Label htmlFor="verzekering-file-upload" className="cursor-pointer">
                              <span className="text-sm font-medium text-gray-900">
                                Klik om documenten te uploaden
                              </span>
                              <span className="block text-xs text-gray-500">
                                PDF, Word, afbeeldingen (max 10MB per bestand)
                              </span>
                            </Label>
                            <Input
                              id="verzekering-file-upload"
                              type="file"
                              multiple
                              accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.txt"
                              className="sr-only"
                              onChange={(e) => {
                                if (e.target.files && e.target.files.length > 0) {
                                  handleFormDocuments(e.target.files, newVerzekeringsDocuments, setNewVerzekeringsDocuments);
                                }
                              }}
                            />
                          </div>
                        </div>
                        
                        {/* Show selected documents */}
                        {newVerzekeringsDocuments.length > 0 && (
                          <div className="mt-4 space-y-2">
                            <p className="text-sm font-medium">Geselecteerde documenten:</p>
                            {newVerzekeringsDocuments.map((doc) => (
                              <div key={doc.id} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                                <div className="flex-1">
                                  <p className="text-sm font-medium">{doc.naam}</p>
                                  <p className="text-xs text-gray-500">
                                    {(doc.size / 1024).toFixed(1)} KB
                                  </p>
                                </div>
                                <Button 
                                  variant="outline" 
                                  size="sm"
                                  onClick={() => removeFormDocument(doc.id, newVerzekeringsDocuments, setNewVerzekeringsDocuments)}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  <DialogFooter>
                    <Button onClick={createVerzekering}>Aanmaken</Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>

            <div className="grid gap-4">
              {verzekeringen.map(verzekering => (
                <Card key={verzekering.id}>
                  <CardContent className="p-6">
                    <div className="flex justify-between items-start">
                      <div className="space-y-2">
                        <div className="flex items-center space-x-2">
                          <h3 className="font-semibold">{verzekering.naam}</h3>
                          {selectedBedrijf === 'alle' && (
                            <Badge variant="secondary" className="text-xs">
                              {getBedrijfNaam(verzekering.bedrijf_id)}
                            </Badge>
                          )}
                          {getVerzekeringTypeBadge(verzekering.type)}
                        </div>
                        <p className="text-sm text-gray-600">{verzekering.verzekeraar} | {verzekering.polisnummer}</p>
                        <p className="text-xs text-gray-500">
                          Premie: €{verzekering.premie?.toFixed(2) || '0.00'}
                          {verzekering.einddatum && ` | Loopt tot: ${format(new Date(verzekering.einddatum), "PPP", { locale: nl })}`}
                        </p>
                        <p className="text-xs text-gray-500">
                          Dekking: {(() => {
                            const dekkingsWaarde = parseInt(verzekering.dekking);
                            return !isNaN(dekkingsWaarde) 
                              ? `€${dekkingsWaarde.toLocaleString('nl-NL')}`
                              : verzekering.dekking;
                          })()}
                        </p>
                      </div>
                      <div className="flex space-x-2">
                        <Button variant="outline" size="sm" onClick={() => viewItem('verzekering', verzekering.id)}>
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => viewInsuranceClaims(verzekering.id)}>
                          <Shield className="h-4 w-4" />
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => editItem('verzekering', verzekering)}>
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => {
                          setShowDocumentUpload({ type: 'verzekering', id: verzekering.id, name: verzekering.naam });
                          fetchEntityDocuments('verzekering', verzekering.id);
                        }}>
                          <Upload className="h-4 w-4" />
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => setDeleteConfirm({ type: 'verzekering', id: verzekering.id, name: verzekering.naam })}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          {/* Claims */}
          <TabsContent value="claims" className="space-y-6">
            {/* Editable Tab Info */}
            <EditableTabInfo 
              tabName="claims"
              user={user}
              tabInfos={tabInfos}
              editingTabInfo={editingTabInfo}
              setEditingTabInfo={setEditingTabInfo}
              updateTabInfo={updateTabInfo}
              fetchTabInfo={fetchTabInfo}
            />
            
            <div className="flex justify-between items-center">
              <div className="flex items-center space-x-2">
                <h2 className="text-lg font-semibold">Claims</h2>
                <InfoTooltip content="Dien verzekeringsclaims in en volg de status op. Claims doorlopen verschillende fases: ingediend → in behandeling → goedgekeurd/afgewezen → uitbetaald.">
                  <Info className="h-4 w-4 text-gray-400 cursor-help" />
                </InfoTooltip>
              </div>
              <Dialog>
                <DialogTrigger asChild>
                  <Button>
                    <Plus className="h-4 w-4 mr-2" />
                    Nieuwe Claim
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl">
                  <DialogHeader>
                    <DialogTitle>Nieuwe Claim Aanmaken</DialogTitle>
                    <DialogDescription>
                      Dien een nieuwe verzekeringsclaim in.
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div>
                      <Label>Verzekeringspolis</Label>
                      <Select value={newClaim.polis_id} onValueChange={(value) => setNewClaim({...newClaim, polis_id: value})}>
                        <SelectTrigger>
                          <SelectValue placeholder="Selecteer polis" />
                        </SelectTrigger>
                        <SelectContent>
                          {verzekeringen.map(verzekering => (
                            <SelectItem key={verzekering.id} value={verzekering.id}>
                              {verzekering.naam} ({verzekering.polisnummer})
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Omschrijving</Label>
                      <Textarea
                        value={newClaim.omschrijving}
                        onChange={(e) => setNewClaim({...newClaim, omschrijving: e.target.value})}
                        placeholder="Beschrijf de schade of het incident"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Schade Datum</Label>
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button variant="outline" className="w-full justify-start text-left font-normal">
                              <CalendarIcon className="mr-2 h-4 w-4" />
                              {newClaim.schade_datum ? format(newClaim.schade_datum, "PPP", { locale: nl }) : "Selecteer datum"}
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0">
                            <Calendar
                              mode="single"
                              selected={newClaim.schade_datum}
                              onSelect={(date) => setNewClaim({...newClaim, schade_datum: date})}
                              initialFocus
                            />
                          </PopoverContent>
                        </Popover>
                      </div>
                      <div>
                        <Label>Gemeld Datum</Label>
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button variant="outline" className="w-full justify-start text-left font-normal">
                              <CalendarIcon className="mr-2 h-4 w-4" />
                              {newClaim.gemeld_datum ? format(newClaim.gemeld_datum, "PPP", { locale: nl }) : "Selecteer datum"}
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0">
                            <Calendar
                              mode="single"
                              selected={newClaim.gemeld_datum}
                              onSelect={(date) => setNewClaim({...newClaim, gemeld_datum: date})}
                              initialFocus
                            />
                          </PopoverContent>
                        </Popover>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Schadebedrag (€)</Label>
                        <Input
                          type="number"
                          value={newClaim.schadebedrag}
                          onChange={(e) => setNewClaim({...newClaim, schadebedrag: parseFloat(e.target.value) || 0})}
                          placeholder="5000"
                        />
                      </div>
                      <div>
                        <Label>Eigen Risico (€)</Label>
                        <Input
                          type="number"
                          value={newClaim.eigen_risico}
                          onChange={(e) => setNewClaim({...newClaim, eigen_risico: parseFloat(e.target.value) || 0})}
                          placeholder="500"
                        />
                      </div>
                    </div>
                    <div>
                      <Label>Opmerkingen</Label>
                      <Textarea
                        value={newClaim.opmerking}
                        onChange={(e) => setNewClaim({...newClaim, opmerking: e.target.value})}
                        placeholder="Aanvullende informatie"
                      />
                    </div>
                    
                    {/* Document Upload Section */}
                    <div>
                      <Label>Documenten Toevoegen</Label>
                      <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 mt-2">
                        <div className="text-center">
                          <Upload className="mx-auto h-8 w-8 text-gray-400" />
                          <div className="mt-2">
                            <Label htmlFor="claim-file-upload" className="cursor-pointer">
                              <span className="text-sm font-medium text-gray-900">
                                Klik om documenten te uploaden
                              </span>
                              <span className="block text-xs text-gray-500">
                                PDF, Word, afbeeldingen (max 10MB per bestand)
                              </span>
                            </Label>
                            <Input
                              id="claim-file-upload"
                              type="file"
                              multiple
                              accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.txt"
                              className="sr-only"
                              onChange={(e) => {
                                if (e.target.files && e.target.files.length > 0) {
                                  handleFormDocuments(e.target.files, newClaimDocuments, setNewClaimDocuments);
                                }
                              }}
                            />
                          </div>
                        </div>
                        
                        {/* Show selected documents */}
                        {newClaimDocuments.length > 0 && (
                          <div className="mt-4 space-y-2">
                            <p className="text-sm font-medium">Geselecteerde documenten:</p>
                            {newClaimDocuments.map((doc) => (
                              <div key={doc.id} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                                <div className="flex-1">
                                  <p className="text-sm font-medium">{doc.naam}</p>
                                  <p className="text-xs text-gray-500">
                                    {(doc.size / 1024).toFixed(1)} KB
                                  </p>
                                </div>
                                <Button 
                                  variant="outline" 
                                  size="sm"
                                  onClick={() => removeFormDocument(doc.id, newClaimDocuments, setNewClaimDocuments)}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  <DialogFooter>
                    <Button onClick={createClaim}>Claim Indienen</Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>

            <div className="grid gap-4">
              {claims.map(claim => {
                const polis = verzekeringen.find(v => v.id === claim.polis_id);
                return (
                  <Card key={claim.id}>
                    <CardContent className="p-6">
                      <div className="flex justify-between items-start">
                        <div className="space-y-2">
                          <div className="flex items-center space-x-2">
                            <h3 className="font-semibold">Claim</h3>
                            {selectedBedrijf === 'alle' && polis && (
                              <Badge variant="secondary" className="text-xs">
                                {getBedrijfNaam(polis.bedrijf_id)}
                              </Badge>
                            )}
                            {getClaimStatusBadge(claim.status)}
                          </div>
                          <p className="text-sm text-gray-600">{claim.omschrijving}</p>
                          <p className="text-xs text-gray-500">
                            Polis: {polis?.naam || 'Onbekend'} | 
                            Schade: €{claim.schadebedrag?.toFixed(2) || '0.00'}
                          </p>
                          <p className="text-xs text-gray-500">
                            Schade datum: {format(new Date(claim.schade_datum), "PPP", { locale: nl })}
                          </p>
                        </div>
                        <div className="flex space-x-2">
                          <Button variant="outline" size="sm" onClick={() => viewItem('claim', claim.id)}>
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button variant="outline" size="sm" onClick={() => editItem('claim', claim)}>
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button variant="outline" size="sm" onClick={() => {
                            setShowDocumentUpload({ type: 'claim', id: claim.id, name: `Claim` });
                            fetchEntityDocuments('claim', claim.id);
                          }}>
                            <Upload className="h-4 w-4" />
                          </Button>
                          <Button variant="outline" size="sm" onClick={() => setDeleteConfirm({ type: 'claim', id: claim.id, name: `Claim` })}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </TabsContent>

          {/* Bedrijven */}
          <TabsContent value="bedrijven" className="space-y-6">
            <div className="flex justify-between items-center">
              <h2 className="text-lg font-semibold">Bedrijven</h2>
              <Dialog>
                <DialogTrigger asChild>
                  <Button>
                    <Plus className="h-4 w-4 mr-2" />
                    Nieuw Bedrijf
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Nieuw Bedrijf Aanmaken</DialogTitle>
                    <DialogDescription>
                      Voeg een nieuw bedrijf toe aan het systeem.
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div>
                      <Label>Bedrijfsnaam</Label>
                      <Input
                        value={newBedrijf.naam}
                        onChange={(e) => setNewBedrijf({...newBedrijf, naam: e.target.value})}
                        placeholder="Voer bedrijfsnaam in"
                      />
                    </div>
                    <div>
                      <Label>Beschrijving</Label>
                      <Textarea
                        value={newBedrijf.beschrijving}
                        onChange={(e) => setNewBedrijf({...newBedrijf, beschrijving: e.target.value})}
                        placeholder="Optionele beschrijving"
                      />
                    </div>
                  </div>
                  <DialogFooter>
                    <Button onClick={createBedrijf}>Aanmaken</Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>

            <div className="grid gap-4">
              {bedrijven.map(bedrijf => (
                <Card key={bedrijf.id}>
                  <CardContent className="p-6">
                    <div className="flex justify-between items-start">
                      <div className="space-y-2">
                        <h3 className="font-semibold">{bedrijf.naam}</h3>
                        {bedrijf.beschrijving && (
                          <p className="text-sm text-gray-600">{bedrijf.beschrijving}</p>
                        )}
                        <p className="text-xs text-gray-500">
                          Aangemaakt: {format(new Date(bedrijf.aangemaakt_op), "PPP", { locale: nl })}
                        </p>
                      </div>
                      <div className="flex space-x-2">
                        <Button variant="outline" size="sm" onClick={() => viewItem('bedrijf', bedrijf.id)}>
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => editItem('bedrijf', bedrijf)}>
                          <Edit className="h-4 w-4" />
                        </Button>
                        {user?.rol === 'admin' && (
                          <Button variant="outline" size="sm" onClick={() => setDeleteConfirm({ type: 'bedrijf', id: bedrijf.id, name: bedrijf.naam })}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          {/* Gebruikers */}
          <TabsContent value="gebruikers" className="space-y-6">
            <div className="flex justify-between items-center">
              <h2 className="text-lg font-semibold">Gebruikers</h2>
              <Dialog>
                <DialogTrigger asChild>
                  <Button>
                    <Plus className="h-4 w-4 mr-2" />
                    Nieuwe Gebruiker
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Nieuwe Gebruiker Toevoegen</DialogTitle>
                    <DialogDescription>
                      Voeg een nieuwe gebruiker toe aan het systeem.
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div>
                      <Label>Naam</Label>
                      <Input
                        value={newUser.naam}
                        onChange={(e) => setNewUser({...newUser, naam: e.target.value})}
                        placeholder="Voor- en achternaam"
                      />
                    </div>
                    <div>
                      <Label>Email</Label>
                      <Input
                        type="email"
                        value={newUser.email}
                        onChange={(e) => setNewUser({...newUser, email: e.target.value})}
                        placeholder="gebruiker@bedrijf.nl"
                      />
                    </div>
                    <div>
                      <Label>Wachtwoord</Label>
                      <Input
                        type="password"
                        value={newUser.password}
                        onChange={(e) => setNewUser({...newUser, password: e.target.value})}
                        placeholder="Minimaal 8 karakters"
                      />
                    </div>
                    <div>
                      <Label>Rol</Label>
                      <Select value={newUser.rol} onValueChange={(value) => setNewUser({...newUser, rol: value})}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="werknemer">Werknemer</SelectItem>
                          <SelectItem value="manager">Manager</SelectItem>
                          <SelectItem value="hr">HR</SelectItem>
                          {user?.rol === 'admin' && <SelectItem value="admin">Admin</SelectItem>}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Bedrijfstoegang</Label>
                      <div className="mt-2 space-y-2 max-h-40 overflow-y-auto">
                        {bedrijven.map(bedrijf => (
                          <div key={bedrijf.id} className="flex items-center space-x-2">
                            <input
                              type="checkbox"
                              id={`new-bedrijf-${bedrijf.id}`}
                              checked={newUser.bedrijf_ids?.includes(bedrijf.id) || false}
                              onChange={(e) => {
                                const updatedBedrijfIds = e.target.checked
                                  ? [...(newUser.bedrijf_ids || []), bedrijf.id]
                                  : (newUser.bedrijf_ids || []).filter(id => id !== bedrijf.id);
                                setNewUser({...newUser, bedrijf_ids: updatedBedrijfIds});
                              }}
                              className="rounded"
                            />
                            <Label htmlFor={`new-bedrijf-${bedrijf.id}`} className="text-sm">
                              {bedrijf.naam}
                            </Label>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                  <DialogFooter>
                    <Button onClick={createUser}>Gebruiker Toevoegen</Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>

            <div className="grid gap-4">
              {users.map(user => (
                <Card key={user.id}>
                  <CardContent className="p-6">
                    <div className="flex justify-between items-start">
                      <div className="space-y-2">
                        <div className="flex items-center space-x-2">
                          <h3 className="font-semibold">{user.naam}</h3>
                          <Badge variant="outline">{user.rol}</Badge>
                        </div>
                        <p className="text-sm text-gray-600">{user.email}</p>
                        {userFamilies[user.id] && userFamilies[user.id].length > 0 && (
                          <p className="text-sm text-blue-600">
                            {userFamilies[user.id].map(member => `${member.relatie}: ${member.naam}`).join(', ')}
                          </p>
                        )}
                      </div>
                      <div className="flex space-x-2">
                        <Button 
                          variant="outline" 
                          size="sm" 
                          onClick={() => {
                            setEditingUserFamily(user);
                            setNewFamilyMember({ naam: '', relatie: 'man' });
                          }}
                        >
                          <Plus className="h-4 w-4 mr-1" />
                          Familie
                        </Button>
                        {(user?.rol === 'admin' || user?.rol === 'hr') && (
                          <Button 
                            variant="outline" 
                            size="sm" 
                            onClick={() => setDeleteConfirm({ type: 'user', id: user.id, name: user.naam })}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          {/* Admin Rechten */}
          {user?.rol === 'admin' && (
            <TabsContent value="rechten" className="space-y-6">
              <div className="flex justify-between items-center">
                <h2 className="text-lg font-semibold">Gebruikersrechten Beheer</h2>
              </div>

              <div className="grid gap-4">
                {allUsers.map(targetUser => (
                  <Card key={targetUser.id}>
                    <CardContent className="p-6">
                      <div className="flex justify-between items-start">
                        <div className="space-y-2">
                          <div className="flex items-center space-x-2">
                            <h3 className="font-semibold">{targetUser.naam}</h3>
                            <Badge variant="outline">{targetUser.rol}</Badge>
                          </div>
                          <p className="text-sm text-gray-600">{targetUser.email}</p>
                          <p className="text-xs text-gray-500">
                            Toegang tot {targetUser.bedrijf_ids?.length || 0} bedrijven
                          </p>
                        </div>
                        <div className="flex space-x-2">
                          <Button 
                            variant="outline" 
                            size="sm" 
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              console.log('Button clicked for user:', targetUser.naam);
                              startEditingUserRights(targetUser);
                            }}
                          >
                            <UserCheck className="h-4 w-4 mr-1" />
                            Rechten
                          </Button>
                          <Button 
                            variant="outline" 
                            size="sm" 
                            onClick={() => setDeleteConfirm({ type: 'gebruiker', id: targetUser.id, name: targetUser.naam })}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </TabsContent>
          )}

          {/* User Rights Edit Dialog */}
          {editingUserRights && (
            <Dialog open={!!editingUserRights} onOpenChange={() => setEditingUserRights(null)}>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Rechten Wijzigen - {editingUserRights.naam}</DialogTitle>
                  <DialogDescription>
                    Pas de rol en bedrijfstoegang aan voor deze gebruiker.
                  </DialogDescription>
                </DialogHeader>
                              <div className="space-y-4">
                                <div>
                                  <Label>Naam</Label>
                                  <Input
                                    value={userRightsData.naam || ''}
                                    onChange={(e) => setUserRightsData({...userRightsData, naam: e.target.value})}
                                    placeholder="Gebruikersnaam"
                                  />
                                </div>
                                <div>
                                  <Label>Rol</Label>
                                  <Select value={userRightsData.rol} onValueChange={(value) => setUserRightsData({...userRightsData, rol: value})}>
                                    <SelectTrigger>
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="werknemer">Werknemer</SelectItem>
                                      <SelectItem value="manager">Manager</SelectItem>
                                      <SelectItem value="hr">HR</SelectItem>
                                      <SelectItem value="admin">Admin</SelectItem>
                                    </SelectContent>
                                  </Select>
                                </div>
                                <div>
                                  <Label>Bedrijfstoegang</Label>
                                  <div className="mt-2 space-y-2">
                                    {bedrijven.map(bedrijf => (
                                      <div key={bedrijf.id} className="flex items-center space-x-2">
                                        <input
                                          type="checkbox"
                                          id={`bedrijf-${bedrijf.id}`}
                                          checked={userRightsData.bedrijf_ids.includes(bedrijf.id)}
                                          onChange={(e) => {
                                            const updatedBedrijfIds = e.target.checked
                                              ? [...userRightsData.bedrijf_ids, bedrijf.id]
                                              : userRightsData.bedrijf_ids.filter(id => id !== bedrijf.id);
                                            setUserRightsData({...userRightsData, bedrijf_ids: updatedBedrijfIds});
                                          }}
                                          className="rounded"
                                        />
                                        <Label htmlFor={`bedrijf-${bedrijf.id}`} className="text-sm">
                                          {bedrijf.naam}
                                        </Label>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                                <div>
                                  <Label>Toegestane Tabs</Label>
                                  <p className="text-sm text-gray-600 mb-2">Selecteer welke tabs deze gebruiker kan zien</p>
                                  <div className="grid grid-cols-2 gap-2">
                                    {[
                                      { value: 'evaluaties', label: 'Evaluaties' },
                                      { value: 'voertuigen', label: 'Voertuigen' },
                                      { value: 'voordelen', label: 'Voordelen' },
                                      { value: 'verzekeringen', label: 'Verzekeringen' },
                                      { value: 'claims', label: 'Claims' },
                                      { value: 'licenties', label: 'Licenties' },
                                      { value: 'contracten', label: 'Contracten' }
                                    ].map(tab => (
                                      <div key={tab.value} className="flex items-center space-x-2">
                                        <input
                                          type="checkbox"
                                          id={`tab-${tab.value}`}
                                          checked={userRightsData.toegestane_tabs?.includes(tab.value) || false}
                                          onChange={(e) => {
                                            const currentTabs = userRightsData.toegestane_tabs || [];
                                            const updatedTabs = e.target.checked
                                              ? [...currentTabs, tab.value]
                                              : currentTabs.filter(t => t !== tab.value);
                                            setUserRightsData({...userRightsData, toegestane_tabs: updatedTabs});
                                          }}
                                          className="rounded"
                                        />
                                        <Label htmlFor={`tab-${tab.value}`} className="text-sm">
                                          {tab.label}
                                        </Label>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                                <div>
                                  <Label>Extra Rechten</Label>
                                  <div className="mt-2">
                                    <div className="flex items-center space-x-2">
                                      <input
                                        type="checkbox"
                                        id="kan-tab-info-bewerken"
                                        checked={userRightsData.kan_tab_info_bewerken || false}
                                        onChange={(e) => setUserRightsData({...userRightsData, kan_tab_info_bewerken: e.target.checked})}
                                        className="rounded"
                                      />
                                      <Label htmlFor="kan-tab-info-bewerken" className="text-sm">
                                        Kan tab informatie bewerken
                                      </Label>
                                    </div>
                                    <p className="text-xs text-gray-500 mt-1">
                                      Staat toe om de info boxen bovenaan elke tab te bewerken
                                    </p>
                                  </div>
                                </div>
                                <div>
                                  <Label>Opmerking</Label>
                                  <Textarea 
                                    value={userRightsData.opmerking}
                                    onChange={(e) => setUserRightsData({...userRightsData, opmerking: e.target.value})}
                                    placeholder="Reden voor wijziging (optioneel)" 
                                  />
                                </div>
                              </div>
                              <DialogFooter>
                                <Button variant="outline" onClick={() => setEditingUserRights(null)}>
                                  Annuleren
                                </Button>
                                <Button onClick={updateUserRights}>
                                  Rechten Bijwerken
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )}

          {/* Admin Logs */}
          {user?.rol === 'admin' && (
            <TabsContent value="logs" className="space-y-6">
              <div className="flex justify-between items-center">
                <h2 className="text-lg font-semibold">Audit Logs</h2>
                <div className="flex gap-2">
                  <Select value={selectedBedrijf} onValueChange={handleBedrijfChange}>
                    <SelectTrigger className="w-48">
                      <SelectValue placeholder="Selecteer bedrijf" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="alle">Alle bedrijven</SelectItem>
                      {bedrijven.map(bedrijf => (
                        <SelectItem key={bedrijf.id} value={bedrijf.id}>
                          {bedrijf.naam}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select value={logsFilter} onValueChange={setLogsFilter}>
                    <SelectTrigger className="w-48">
                      <SelectValue placeholder="Filter op type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="alle_types">Alle types</SelectItem>
                      <SelectItem value="evaluatie">Evaluaties</SelectItem>
                      <SelectItem value="evaluatie_template">Templates</SelectItem>
                      <SelectItem value="voertuig">Voertuigen</SelectItem>
                      <SelectItem value="onderhoud">Onderhoud</SelectItem>
                      <SelectItem value="voordeel">Voordelen</SelectItem>
                      <SelectItem value="verzekeringspolis">Verzekeringen</SelectItem>
                      <SelectItem value="claim">Claims</SelectItem>
                      <SelectItem value="bedrijf">Bedrijven</SelectItem>
                      <SelectItem value="gebruiker">Gebruikers</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid gap-4">
                {auditLogs
                  .filter(log => {
                    // Filter by company
                    if (selectedBedrijf !== 'alle' && log.bedrijf_id !== selectedBedrijf) {
                      return false;
                    }
                    // Filter by entity type
                    if (logsFilter && logsFilter !== 'alle_types' && log.entiteit_type !== logsFilter) {
                      return false;
                    }
                    return true;
                  })
                  .map(log => (
                  <Card key={log.id}>
                    <CardContent className="p-4">
                      <div className="flex justify-between items-start">
                        <div className="space-y-1">
                          <div className="flex items-center space-x-2">
                            <Badge variant="outline">{log.actie}</Badge>
                            <Badge variant="secondary">{log.entiteit_type}</Badge>
                            <span className="text-sm font-medium">{log.gebruiker_naam}</span>
                          </div>
                          <p className="text-sm text-gray-600">
                            {log.opmerking || `${log.entiteit_type} ${log.actie}`}
                          </p>
                          <p className="text-xs text-gray-500">
                            {format(new Date(log.tijdstip), "PPP p", { locale: nl })}
                          </p>
                        </div>
                        <Button variant="ghost" size="sm">
                          <Eye className="h-4 w-4" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </TabsContent>
          )}

          {/* Updates */}
          <TabsContent value="updates" className="space-y-6">
            <div className="flex justify-between items-center">
              <h2 className="text-lg font-semibold">Updates</h2>
              {(user?.rol === 'admin' || user?.rol === 'hr' || user?.rol === 'manager') && (
                <div className="flex gap-2">
                  <Input
                    placeholder="Beschrijf de nieuwe functie of wijziging..."
                    value={newUpdate}
                    onChange={(e) => setNewUpdate(e.target.value)}
                    className="w-96"
                  />
                  <Button 
                    onClick={async () => {
                      if (!newUpdate.trim()) return;
                      try {
                        await axios.post(`${API}/updates`, {
                          beschrijving: newUpdate
                        }, {
                          headers: { 'Authorization': `Bearer ${token}` }
                        });
                        setNewUpdate('');
                        fetchUpdates();
                        toast.success('Update toegevoegd');
                      } catch (error) {
                        toast.error('Fout bij toevoegen update');
                      }
                    }}
                    disabled={!newUpdate.trim()}
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Toevoegen
                  </Button>
                </div>
              )}
            </div>

            <div className="space-y-4">
              {updates.map(update => (
                <Card key={update.id}>
                  <CardContent className="p-4">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <p className="text-sm">{update.beschrijving}</p>
                        <p className="text-xs text-gray-500 mt-2">
                          Toegevoegd door {update.toegevoegd_door_naam} op {format(new Date(update.aangemaakt_op), "PPp", { locale: nl })}
                        </p>
                      </div>
                      {user?.rol === 'admin' && (
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={async () => {
                            try {
                              await axios.delete(`${API}/updates/${update.id}`, {
                                headers: { 'Authorization': `Bearer ${token}` }
                              });
                              fetchUpdates();
                              toast.success('Update verwijderd');
                            } catch (error) {
                              toast.error('Fout bij verwijderen update');
                            }
                          }}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
              {updates.length === 0 && (
                <div className="text-center py-8">
                  <p className="text-gray-500">Nog geen updates toegevoegd</p>
                </div>
              )}
            </div>
          </TabsContent>

          {/* Instellingen */}
          {user?.rol === 'admin' && (
            <TabsContent value="instellingen" className="space-y-6">
              <div className="flex justify-between items-center">
                <h2 className="text-lg font-semibold">Instellingen</h2>
              </div>

              <Tabs defaultValue="templates" className="space-y-4">
                <TabsList className="grid w-full grid-cols-3">
                  <TabsTrigger value="templates">Templates</TabsTrigger>
                  <TabsTrigger value="rechten">Rechten</TabsTrigger>
                  <TabsTrigger value="logs">Logs</TabsTrigger>
                </TabsList>

                {/* Templates Sub-tab */}
                <TabsContent value="templates" className="space-y-6">
                  <div className="flex justify-between items-center">
                    <div className="flex items-center space-x-2">
                      <h3 className="text-md font-semibold">Evaluatie Templates</h3>
                    </div>
                    <Dialog>
                      <DialogTrigger asChild>
                        <Button>
                          <Plus className="h-4 w-4 mr-2" />
                          Nieuwe Template
                        </Button>
                      </DialogTrigger>
                      <DialogContent>
                        <DialogHeader>
                          <DialogTitle>Nieuwe Template Aanmaken</DialogTitle>
                        </DialogHeader>
                        <div className="space-y-4">
                          <div>
                            <Label>Template Naam</Label>
                            <Input
                              value={newTemplate.naam}
                              onChange={(e) => setNewTemplate({...newTemplate, naam: e.target.value})}
                              placeholder="Jaarlijkse evaluatie"
                            />
                          </div>
                          <div>
                            <Label>Beschrijving</Label>
                            <Textarea
                              value={newTemplate.beschrijving}
                              onChange={(e) => setNewTemplate({...newTemplate, beschrijving: e.target.value})}
                              placeholder="Template voor jaarlijkse medewerkerevaluaties"
                            />
                          </div>
                          <div>
                            <Label>Type</Label>
                            <Select value={newTemplate.type} onValueChange={(value) => setNewTemplate({...newTemplate, type: value})}>
                              <SelectTrigger>
                                <SelectValue placeholder="Selecteer type" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="initieel">Initiële evaluatie</SelectItem>
                                <SelectItem value="periodiek">Periodieke evaluatie</SelectItem>
                                <SelectItem value="tussentijds">Tussentijdse feedback</SelectItem>
                                <SelectItem value="verbeterplan">Verbeterplan</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                        <DialogFooter>
                          <Button onClick={createTemplate}>Aanmaken</Button>
                        </DialogFooter>
                      </DialogContent>
                    </Dialog>
                  </div>

                  <div className="grid gap-4">
                    {templates.map(template => (
                      <Card key={template.id}>
                        <CardContent className="p-6">
                          <div className="flex justify-between items-start">
                            <div className="space-y-2">
                              <h3 className="font-semibold">{template.naam}</h3>
                              <p className="text-sm text-gray-600">{template.beschrijving}</p>
                              <div className="flex items-center space-x-2">
                                <Badge variant="outline">{template.type}</Badge>
                                <span className="text-xs text-gray-500">
                                  {template.vragen?.length || 0} vragen
                                </span>
                              </div>
                            </div>
                            <div className="flex space-x-2">
                              <Button variant="outline" size="sm" onClick={() => viewItem('template', template.id)}>
                                <Eye className="h-4 w-4" />
                              </Button>
                              <Button variant="outline" size="sm" onClick={() => editItem('template', template)}>
                                <Edit className="h-4 w-4" />
                              </Button>
                              <Button variant="outline" size="sm" onClick={() => kloonTemplate(template)}>
                                <Copy className="h-4 w-4" />
                              </Button>
                              <Button variant="outline" size="sm" onClick={() => setDeleteConfirm({ type: 'template', id: template.id, name: template.naam })}>
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </TabsContent>

                {/* Rechten Sub-tab */}
                <TabsContent value="rechten" className="space-y-6">
                  <div className="flex justify-between items-center">
                    <h3 className="text-md font-semibold">Gebruikers Rechten</h3>
                  </div>

                  <div className="grid gap-4">
                    {users.map(user => (
                      <Card key={user.id}>
                        <CardContent className="p-6">
                          <div className="flex justify-between items-start">
                            <div className="space-y-2">
                              <h3 className="font-semibold">{user.naam}</h3>
                              <p className="text-sm text-gray-600">{user.email}</p>
                              <div className="flex items-center space-x-2">
                                <Badge variant="outline">{user.rol}</Badge>
                                <span className="text-xs text-gray-500">
                                  {user.bedrijf_ids?.length || 0} bedrijven
                                </span>
                              </div>
                              <div className="flex flex-wrap gap-1 mt-2">
                                {user.toegestane_tabs?.map(tab => (
                                  <Badge key={tab} variant="secondary" className="text-xs">
                                    {tab}
                                  </Badge>
                                ))}
                              </div>
                            </div>
                            <div className="flex space-x-2">
                              <Button 
                                variant="outline" 
                                size="sm" 
                                onClick={() => startEditingUserRights(user)}
                              >
                                <Edit className="h-4 w-4" />
                                Rechten
                              </Button>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </TabsContent>

                {/* Logs Sub-tab */}
                <TabsContent value="logs" className="space-y-6">
                  <div className="flex justify-between items-center">
                    <h3 className="text-md font-semibold">Audit Logs</h3>
                    <div className="flex gap-2">
                      <Select value={selectedBedrijf} onValueChange={handleBedrijfChange}>
                        <SelectTrigger className="w-48">
                          <SelectValue placeholder="Selecteer bedrijf" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="alle">Alle bedrijven</SelectItem>
                          {bedrijven.map(bedrijf => (
                            <SelectItem key={bedrijf.id} value={bedrijf.id}>
                              {bedrijf.naam}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Select value={logsFilter} onValueChange={setLogsFilter}>
                        <SelectTrigger className="w-48">
                          <SelectValue placeholder="Filter op type" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="alle_types">Alle types</SelectItem>
                          <SelectItem value="evaluatie">Evaluaties</SelectItem>
                          <SelectItem value="evaluatie_template">Templates</SelectItem>
                          <SelectItem value="voertuig">Voertuigen</SelectItem>
                          <SelectItem value="onderhoud">Onderhoud</SelectItem>
                          <SelectItem value="voordeel">Voordelen</SelectItem>
                          <SelectItem value="verzekeringspolis">Verzekeringen</SelectItem>
                          <SelectItem value="claim">Claims</SelectItem>
                          <SelectItem value="bedrijf">Bedrijven</SelectItem>
                          <SelectItem value="gebruiker">Gebruikers</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="grid gap-4">
                    {auditLogs
                      .filter(log => {
                        if (selectedBedrijf !== 'alle' && log.bedrijf_id !== selectedBedrijf) {
                          return false;
                        }
                        if (logsFilter && logsFilter !== 'alle_types' && log.entiteit_type !== logsFilter) {
                          return false;
                        }
                        return true;
                      })
                      .map(log => (
                      <Card key={log.id}>
                        <CardContent className="p-4">
                          <div className="flex justify-between items-start">
                            <div className="space-y-1">
                              <div className="flex items-center space-x-2">
                                <Badge variant="outline">{log.entiteit_type}</Badge>
                                <Badge variant={log.actie === 'verwijderd' ? 'destructive' : log.actie === 'aangemaakt' ? 'default' : 'secondary'}>
                                  {log.actie}
                                </Badge>
                              </div>
                              <p className="font-medium">{log.gebruiker_naam}</p>
                              <p className="text-sm text-gray-600">{log.opmerking}</p>
                            </div>
                            <p className="text-xs text-gray-500">
                              {format(new Date(log.tijdstip), "PPp", { locale: nl })}
                            </p>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </TabsContent>

              </Tabs>
            </TabsContent>
          )}

          {/* Licenties */}
          {user?.toegestane_tabs?.includes('licenties') && (
            <TabsContent value="licenties" className="space-y-6">
              {/* Editable Tab Info */}
              <EditableTabInfo 
                tabName="licenties"
                user={user}
                tabInfos={tabInfos}
                editingTabInfo={editingTabInfo}
                setEditingTabInfo={setEditingTabInfo}
                updateTabInfo={updateTabInfo}
                fetchTabInfo={fetchTabInfo}
              />
              
              <div className="flex justify-between items-center">
                <h2 className="text-lg font-semibold">Licenties</h2>
                <Dialog open={showAddLicenseDialog} onOpenChange={setShowAddLicenseDialog}>
                  <DialogTrigger asChild>
                    <Button onClick={() => setShowAddLicenseDialog(true)}>
                      <Plus className="h-4 w-4 mr-2" />
                      Nieuwe Licentie
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-2xl">
                    <DialogHeader>
                      <DialogTitle>Nieuwe Licentie Toevoegen</DialogTitle>
                    </DialogHeader>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Software Naam</Label>
                        <Input
                          value={newLicense.naam}
                          onChange={(e) => setNewLicense({...newLicense, naam: e.target.value})}
                          placeholder="Microsoft Office"
                        />
                      </div>
                      <div>
                        <Label>Licentie Code</Label>
                        <Input
                          value={newLicense.code}
                          onChange={(e) => setNewLicense({...newLicense, code: e.target.value})}
                          placeholder="XXXXX-XXXXX-XXXXX"
                        />
                      </div>
                      <div>
                        <Label>E-mail Adres</Label>
                        <Input
                          type="email"
                          value={newLicense.email}
                          onChange={(e) => setNewLicense({...newLicense, email: e.target.value})}
                          placeholder="admin@bedrijf.nl"
                        />
                      </div>
                      <div>
                        <Label>Wachtwoord</Label>
                        <Input
                          type="password"
                          value={newLicense.wachtwoord}
                          onChange={(e) => setNewLicense({...newLicense, wachtwoord: e.target.value})}
                          placeholder="********"
                        />
                      </div>
                      <div>
                        <Label>Gebruiker</Label>
                        <Input
                          value={newLicense.gebruiker}
                          onChange={(e) => setNewLicense({...newLicense, gebruiker: e.target.value})}
                          placeholder="Jan Janssen"
                        />
                      </div>
                      <div>
                        <Label>Kostprijs (€)</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={newLicense.kostprijs}
                          onChange={(e) => setNewLicense({...newLicense, kostprijs: e.target.value})}
                          placeholder="99.99"
                        />
                      </div>
                      <div>
                        <Label>Website</Label>
                        <Input
                          value={newLicense.website}
                          onChange={(e) => setNewLicense({...newLicense, website: e.target.value})}
                          placeholder="https://www.microsoft.com"
                        />
                      </div>
                      <div>
                        <Label>Vervaldatum</Label>
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button variant="outline" className="w-full justify-start text-left font-normal">
                              <CalendarIcon className="mr-2 h-4 w-4" />
                              {newLicense.vervaldatum ? format(new Date(newLicense.vervaldatum), "PPP", { locale: nl }) : "Selecteer datum"}
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent>
                            <Calendar
                              mode="single"
                              selected={newLicense.vervaldatum ? new Date(newLicense.vervaldatum) : null}
                              onSelect={(date) => setNewLicense({...newLicense, vervaldatum: date})}
                            />
                          </PopoverContent>
                        </Popover>
                      </div>
                    </div>
                    <DialogFooter>
                      <Button onClick={async () => {
                        if (!selectedBedrijf) {
                          toast.error('Selecteer eerst een bedrijf');
                          return;
                        }
                        
                        try {
                          await axios.post(`${API}/licenses?bedrijf_id=${selectedBedrijf}`, newLicense, {
                            headers: { 'Authorization': `Bearer ${token}` }
                          });
                          
                          // Reset form and close dialog
                          setNewLicense({
                            naam: '', code: '', email: '', wachtwoord: '', vervaldatum: null,
                            gebruiker: '', kostprijs: '', website: '', aangekocht: null
                          });
                          
                          // Refresh data and close dialog
                          await fetchLicenses();
                          setShowAddLicenseDialog(false); // Close the dialog
                          toast.success('Licentie toegevoegd');
                        } catch (error) {
                          console.error('Error creating license:', error);
                          
                          // Handle validation errors properly
                          let errorMessage = 'Fout bij toevoegen licentie';
                          if (error.response?.data?.detail) {
                            if (Array.isArray(error.response.data.detail)) {
                              // Handle FastAPI validation errors
                              errorMessage = error.response.data.detail.map(err => err.msg).join(', ');
                            } else {
                              errorMessage = error.response.data.detail;
                            }
                          }
                          
                          toast.error(errorMessage);
                        }
                      }}>
                        Toevoegen
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </div>

              <div className="grid gap-4">
                {licenses.map(license => (
                  <Card key={license.id}>
                    <CardContent className="p-6">
                      <div className="flex justify-between items-start">
                        <div className="space-y-2">
                          <div className="flex items-center space-x-2">
                            <h3 className="font-semibold">{license.naam || 'Onbekende software'}</h3>
                            {selectedBedrijf === 'alle' && (
                              <Badge variant="secondary" className="text-xs">
                                {getBedrijfNaam(license.bedrijf_id)}
                              </Badge>
                            )}
                          </div>
                          <div className="grid grid-cols-2 gap-4 text-sm">
                            {license.code && <p><span className="font-medium">Code:</span> {license.code}</p>}
                            {license.email && <p><span className="font-medium">Email:</span> {license.email}</p>}
                            {license.gebruiker && <p><span className="font-medium">Gebruiker:</span> {license.gebruiker}</p>}
                            {license.kostprijs && <p><span className="font-medium">Kostprijs:</span> €{license.kostprijs}</p>}
                            {license.vervaldatum && (
                              <p><span className="font-medium">Vervalt:</span> {format(new Date(license.vervaldatum), "PPP", { locale: nl })}</p>
                            )}
                            {license.website && (
                              <p><span className="font-medium">Website:</span> <a href={license.website} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">{license.website}</a></p>
                            )}
                          </div>
                        </div>
                        <div className="flex space-x-2">
                          <Button variant="outline" size="sm" onClick={() => editItem('license', license)}>
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button variant="outline" size="sm" onClick={() => setDeleteConfirm({ type: 'license', id: license.id, name: license.naam || 'Licentie' })}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
                {licenses.length === 0 && (
                  <div className="text-center py-8">
                    <p className="text-gray-500">Nog geen licenties toegevoegd</p>
                  </div>
                )}
              </div>
            </TabsContent>
          )}

          {/* Contracten */}
          {user?.toegestane_tabs?.includes('contracten') && (
            <TabsContent value="contracten" className="space-y-6">
              {/* Editable Tab Info */}
              <EditableTabInfo 
                tabName="contracten"
                user={user}
                tabInfos={tabInfos}
                editingTabInfo={editingTabInfo}
                setEditingTabInfo={setEditingTabInfo}
                updateTabInfo={updateTabInfo}
                fetchTabInfo={fetchTabInfo}
              />
              
              <div className="flex justify-between items-center">
                <h2 className="text-lg font-semibold">Contracten</h2>
                <Dialog>
                  <DialogTrigger asChild>
                    <Button>
                      <Plus className="h-4 w-4 mr-2" />
                      Nieuw Contract
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle>Nieuw Contract Toevoegen</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label>Contract Naam <span className="text-red-500">*</span></Label>
                          <Input
                            value={newContract.naam}
                            onChange={(e) => setNewContract({...newContract, naam: e.target.value})}
                            placeholder="Huurcontract kantoor"
                          />
                        </div>
                        <div>
                          <Label>Type <span className="text-red-500">*</span></Label>
                          <Select value={newContract.type} onValueChange={(value) => setNewContract({...newContract, type: value})}>
                            <SelectTrigger>
                              <SelectValue placeholder="Selecteer type" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="huur">Huurcontract</SelectItem>
                              <SelectItem value="lease">Leasecontract</SelectItem>
                              <SelectItem value="service">Servicecontract</SelectItem>
                              <SelectItem value="leverancier">Leverancierscontract</SelectItem>
                              <SelectItem value="onderhoud">Onderhoudscontract</SelectItem>
                              <SelectItem value="verzekering">Verzekeringscontract</SelectItem>
                              <SelectItem value="overig">Overig</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label>Partij <span className="text-red-500">*</span></Label>
                          <Input
                            value={newContract.partij}
                            onChange={(e) => setNewContract({...newContract, partij: e.target.value})}
                            placeholder="ABC Verhuur BV"
                          />
                        </div>
                        <div>
                          <Label>Maandelijks Bedrag (€)</Label>
                          <Input
                            type="number"
                            step="0.01"
                            value={newContract.maandelijks_bedrag}
                            onChange={(e) => setNewContract({...newContract, maandelijks_bedrag: parseFloat(e.target.value) || 0})}
                            placeholder="1500.00"
                          />
                        </div>
                        <div>
                          <Label>Jaarlijks Bedrag (€)</Label>
                          <Input
                            type="number"
                            step="0.01"
                            value={newContract.jaarlijks_bedrag}
                            onChange={(e) => setNewContract({...newContract, jaarlijks_bedrag: parseFloat(e.target.value) || 0})}
                            placeholder="18000.00"
                          />
                        </div>
                        <div>
                          <Label>Begindatum</Label>
                          <Popover>
                            <PopoverTrigger asChild>
                              <Button variant="outline" className="w-full justify-start text-left font-normal">
                                <CalendarIcon className="mr-2 h-4 w-4" />
                                {format(newContract.begindatum, "PPP", { locale: nl })}
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent>
                              <Calendar
                                mode="single"
                                selected={newContract.begindatum}
                                onSelect={(date) => setNewContract({...newContract, begindatum: date})}
                              />
                            </PopoverContent>
                          </Popover>
                        </div>
                      </div>

                      <div>
                        <Label>Opmerkingen</Label>
                        <Textarea
                          value={newContract.opmerkingen}
                          onChange={(e) => setNewContract({...newContract, opmerkingen: e.target.value})}
                          placeholder="Aanvullende informatie over het contract"
                        />
                      </div>

                      {/* Document Upload Section */}
                      <div>
                        <Label>Documenten Toevoegen</Label>
                        <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 mt-2">
                          <div className="text-center">
                            <Upload className="mx-auto h-8 w-8 text-gray-400" />
                            <div className="mt-2">
                              <Label htmlFor="contract-file-upload" className="cursor-pointer">
                                <span className="text-sm font-medium text-gray-900">
                                  Klik om contractdocumenten te uploaden
                                </span>
                                <span className="block text-xs text-gray-500">
                                  PDF, Word, afbeeldingen (max 10MB per bestand)
                                </span>
                              </Label>
                              <Input
                                id="contract-file-upload"
                                type="file"
                                multiple
                                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.txt"
                                className="sr-only"
                                onChange={(e) => {
                                  if (e.target.files && e.target.files.length > 0) {
                                    handleFormDocuments(e.target.files, newContractDocuments, setNewContractDocuments);
                                  }
                                }}
                              />
                            </div>
                          </div>

                          {/* Show selected documents */}
                          {newContractDocuments.length > 0 && (
                            <div className="mt-4 space-y-2">
                              <p className="text-sm font-medium">Geselecteerde documenten:</p>
                              {newContractDocuments.map((doc) => (
                                <div key={doc.id} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                                  <div className="flex-1">
                                    <p className="text-sm font-medium">{doc.naam}</p>
                                    <p className="text-xs text-gray-500">
                                      {(doc.size / 1024).toFixed(1)} KB
                                    </p>
                                  </div>
                                  <Button 
                                    variant="outline" 
                                    size="sm"
                                    onClick={() => removeFormDocument(doc.id, newContractDocuments, setNewContractDocuments)}
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    <DialogFooter>
                      <Button onClick={async () => {
                        if (!selectedBedrijf) {
                          toast.error('Selecteer eerst een bedrijf');
                          return;
                        }
                        
                        if (!newContract.naam.trim() || !newContract.type.trim() || !newContract.partij.trim()) {
                          toast.error('Vul alle verplichte velden in (naam, type, partij)');
                          return;
                        }
                        
                        try {
                          console.log('Creating contract with data:', newContract);
                          console.log('Selected bedrijf:', selectedBedrijf);
                          
                          // Prepare contract data with proper formatting
                          const contractData = {
                            naam: newContract.naam.trim(),
                            type: newContract.type,
                            partij: newContract.partij.trim(),
                            maandelijks_bedrag: newContract.maandelijks_bedrag ? parseFloat(newContract.maandelijks_bedrag) : 0,
                            jaarlijks_bedrag: newContract.jaarlijks_bedrag ? parseFloat(newContract.jaarlijks_bedrag) : 0,
                            begindatum: newContract.begindatum ? new Date(newContract.begindatum).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
                            einddatum: newContract.einddatum ? new Date(newContract.einddatum).toISOString().split('T')[0] : null,
                            opmerkingen: newContract.opmerkingen || ''
                          };
                          
                          console.log('Formatted contract data:', contractData);
                          
                          const response = await axios.post(`${API}/contracts?bedrijf_id=${selectedBedrijf}`, contractData, {
                            headers: { 'Authorization': `Bearer ${token}` }
                          });

                          const contractId = response.data.id;

                          // Upload documents if any
                          if (newContractDocuments.length > 0) {
                            for (const doc of newContractDocuments) {
                              await uploadDocument([doc.file], doc.description || `Document voor contract`, 'contract', contractId);
                            }
                          }

                          setNewContract({
                            naam: '', type: '', partij: '', maandelijks_bedrag: 0, jaarlijks_bedrag: 0,
                            begindatum: new Date(), einddatum: null, opmerkingen: ''
                          });
                          setNewContractDocuments([]);
                          fetchContracts();
                          toast.success('Contract toegevoegd met documenten');
                        } catch (error) {
                          console.error('Error creating contract:', error);
                          console.error('Error response:', error.response?.data);
                          
                          // Handle validation errors properly
                          let errorMessage = 'Fout bij toevoegen contract';
                          if (error.response?.data?.detail) {
                            if (Array.isArray(error.response.data.detail)) {
                              // Handle FastAPI validation errors
                              errorMessage = error.response.data.detail.map(err => err.msg).join(', ');
                            } else {
                              errorMessage = error.response.data.detail;
                            }
                          }
                          
                          toast.error(errorMessage);
                        }
                      }}>
                        Toevoegen
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </div>

              <div className="grid gap-4">
                {contracts.map(contract => (
                  <Card key={contract.id}>
                    <CardContent className="p-6">
                      <div className="flex justify-between items-start">
                        <div className="space-y-2">
                          <div className="flex items-center space-x-2">
                            <h3 className="font-semibold">{contract.naam}</h3>
                            {selectedBedrijf === 'alle' && (
                              <Badge variant="secondary" className="text-xs">
                                {getBedrijfNaam(contract.bedrijf_id)}
                              </Badge>
                            )}
                          </div>
                          <div className="grid grid-cols-2 gap-4 text-sm">
                            <p><span className="font-medium">Type:</span> {contract.type}</p>
                            <p><span className="font-medium">Partij:</span> {contract.partij}</p>
                            {contract.maandelijks_bedrag && <p><span className="font-medium">Maandelijks:</span> €{contract.maandelijks_bedrag}</p>}
                            {contract.jaarlijks_bedrag && <p><span className="font-medium">Jaarlijks:</span> €{contract.jaarlijks_bedrag}</p>}
                            <p><span className="font-medium">Start:</span> {format(new Date(contract.begindatum), "PPP", { locale: nl })}</p>
                            {contract.einddatum && (
                              <p><span className="font-medium">Einde:</span> {format(new Date(contract.einddatum), "PPP", { locale: nl })}</p>
                            )}
                          </div>
                          {contract.opmerkingen && (
                            <p className="text-sm text-gray-600 mt-2">{contract.opmerkingen}</p>
                          )}
                        </div>
                        <div className="flex space-x-2">
                          <Button variant="outline" size="sm" onClick={() => viewItem('contract', contract.id)}>
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button variant="outline" size="sm" onClick={() => editItem('contract', contract)}>
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button variant="outline" size="sm" onClick={() => {
                            setShowDocumentUpload({ type: 'contract', id: contract.id, name: contract.naam });
                            fetchEntityDocuments('contract', contract.id);
                          }}>
                            <Upload className="h-4 w-4" />
                          </Button>
                          <Button variant="outline" size="sm" onClick={() => setDeleteConfirm({ type: 'contract', id: contract.id, name: contract.naam })}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
                {contracts.length === 0 && (
                  <div className="text-center py-8">
                    <p className="text-gray-500">Nog geen contracten toegevoegd</p>
                  </div>
                )}
              </div>
            </TabsContent>
          )}
        </Tabs>
      </div>

      {/* View Dialog */}
      <Dialog open={!!viewingItem} onOpenChange={() => {
        setViewingItem(null);
        setClaimComments([]);
      }}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Details bekijken</DialogTitle>
          </DialogHeader>
          {viewingItem && (
            <div className="space-y-4">
              {viewingItem.type === 'evaluatie' ? (
                <div className="space-y-6">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-sm font-medium">Status</Label>
                      <Badge className="ml-2">{viewingItem.data.status}</Badge>
                    </div>
                    <div>
                      <Label className="text-sm font-medium">Type</Label>
                      <p className="text-sm">{viewingItem.data.type}</p>
                    </div>
                    <div>
                      <Label className="text-sm font-medium">Deadline</Label>
                      <p className="text-sm">{format(new Date(viewingItem.data.deadline), "PPP", { locale: nl })}</p>
                    </div>
                    <div>
                      <Label className="text-sm font-medium">Aangemaakt</Label>
                      <p className="text-sm">{format(new Date(viewingItem.data.aangemaakt_op), "PPP", { locale: nl })}</p>
                    </div>
                  </div>
                  
                  <Separator />
                  
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold">Evaluatie Vragen & Antwoorden</h3>
                    
                    {viewingItem.data.template && viewingItem.data.template.vragen ? (
                      <div className="space-y-4">
                        {viewingItem.data.template.vragen
                          .sort((a, b) => a.volgorde - b.volgorde)
                          .map((vraag, index) => {
                            // Find answers for this question
                            const zelfAntwoord = viewingItem.data.zelfevaluatie?.find(a => a.vraag_id === vraag.id);
                            const managerAntwoord = viewingItem.data.manager_beoordeling?.find(a => a.vraag_id === vraag.id);
                            
                            return (
                              <div key={vraag.id} className="border rounded-lg p-4 bg-gray-50">
                                {/* Question Header */}
                                <div className="mb-3">
                                  <div className="flex items-center gap-2 mb-2">
                                    <Badge variant="outline">#{vraag.volgorde}</Badge>
                                    <Label className="text-sm font-medium text-gray-700">{vraag.sectie}</Label>
                                    {vraag.verplicht && <Badge variant="destructive" className="text-xs">Verplicht</Badge>}
                                  </div>
                                  <p className="text-sm font-medium text-gray-900">{vraag.tekst}</p>
                                </div>
                                
                                {/* Answers */}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                  {/* Self evaluation */}
                                  <div className="bg-blue-50 rounded-lg p-3">
                                    <h5 className="font-medium text-blue-700 mb-2">Zelfevaluatie</h5>
                                    {zelfAntwoord ? (
                                      <>
                                        <div className="mb-2">
                                          <Badge variant="outline" className="bg-blue-100">
                                            Score: {zelfAntwoord.score || 'Niet van toepassing'}
                                          </Badge>
                                        </div>
                                        {zelfAntwoord.opmerking && (
                                          <div className="mb-2">
                                            <Label className="text-xs font-medium text-blue-600">Opmerking:</Label>
                                            <p className="text-sm text-gray-700 mt-1">{zelfAntwoord.opmerking}</p>
                                          </div>
                                        )}
                                        {zelfAntwoord.doelstelling && (
                                          <div>
                                            <Label className="text-xs font-medium text-purple-600">Doelstelling:</Label>
                                            <p className="text-sm text-gray-700 mt-1">{zelfAntwoord.doelstelling}</p>
                                          </div>
                                        )}
                                      </>
                                    ) : (
                                      <p className="text-sm text-gray-500 italic">Nog niet ingevuld</p>
                                    )}
                                  </div>
                                  
                                  {/* Manager evaluation */}
                                  <div className="bg-green-50 rounded-lg p-3">
                                    <h5 className="font-medium text-green-700 mb-2">Manager Beoordeling</h5>
                                    {managerAntwoord ? (
                                      <>
                                        <div className="mb-2">
                                          <Badge variant="outline" className="bg-green-100">
                                            Score: {managerAntwoord.score || 'Niet van toepassing'}
                                          </Badge>
                                        </div>
                                        {managerAntwoord.opmerking && (
                                          <div className="mb-2">
                                            <Label className="text-xs font-medium text-green-600">Opmerking:</Label>
                                            <p className="text-sm text-gray-700 mt-1">{managerAntwoord.opmerking}</p>
                                          </div>
                                        )}
                                        {managerAntwoord.doelstelling && (
                                          <div>
                                            <Label className="text-xs font-medium text-purple-600">Doelstelling:</Label>
                                            <p className="text-sm text-gray-700 mt-1">{managerAntwoord.doelstelling}</p>
                                          </div>
                                        )}
                                      </>
                                    ) : (
                                      <p className="text-sm text-gray-500 italic">Nog niet ingevuld</p>
                                    )}
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {/* Fallback: Show old format if template is not available */}
                        {(viewingItem.data.zelfevaluatie && viewingItem.data.zelfevaluatie.length > 0) || 
                         (viewingItem.data.manager_beoordeling && viewingItem.data.manager_beoordeling.length > 0) ? (
                          <>
                            {viewingItem.data.zelfevaluatie && viewingItem.data.zelfevaluatie.length > 0 && (
                              <div className="space-y-3">
                                <h4 className="font-medium text-blue-700">Zelfevaluatie</h4>
                                {viewingItem.data.zelfevaluatie.map((antwoord, index) => (
                                  <div key={index} className="border rounded-lg p-4 bg-blue-50">
                                    <div className="flex justify-between items-start mb-2">
                                      <Label className="text-sm font-medium">Vraag {index + 1}</Label>
                                      <Badge variant="outline">Score: {antwoord.score || 'Niet van toepassing'}</Badge>
                                    </div>
                                    {antwoord.opmerking && (
                                      <div className="mt-2">
                                        <Label className="text-xs font-medium text-blue-600">Evaluatie/Opmerking:</Label>
                                        <p className="text-sm text-gray-700 mt-1">{antwoord.opmerking}</p>
                                      </div>
                                    )}
                                    {antwoord.doelstelling && (
                                      <div className="mt-2">
                                        <Label className="text-xs font-medium text-purple-600">Doelstelling:</Label>
                                        <p className="text-sm text-gray-700 mt-1">{antwoord.doelstelling}</p>
                                      </div>
                                    )}
                                  </div>
                                ))}
                              </div>
                            )}
                            
                            {viewingItem.data.manager_beoordeling && viewingItem.data.manager_beoordeling.length > 0 && (
                              <div className="space-y-3">
                                <h4 className="font-medium text-green-700">Manager Beoordeling</h4>
                                {viewingItem.data.manager_beoordeling.map((antwoord, index) => (
                                  <div key={index} className="border rounded-lg p-4 bg-green-50">
                                    <div className="flex justify-between items-start mb-2">
                                      <Label className="text-sm font-medium">Vraag {index + 1}</Label>
                                      <Badge variant="outline">Score: {antwoord.score || 'Niet van toepassing'}</Badge>
                                    </div>
                                    {antwoord.opmerking && (
                                      <div className="mt-2">
                                        <Label className="text-xs font-medium text-green-600">Manager Evaluatie/Opmerking:</Label>
                                        <p className="text-sm text-gray-700 mt-1">{antwoord.opmerking}</p>
                                      </div>
                                    )}
                                    {antwoord.doelstelling && (
                                      <div className="mt-2">
                                        <Label className="text-xs font-medium text-purple-600">Manager Doelstelling:</Label>
                                        <p className="text-sm text-gray-700 mt-1">{antwoord.doelstelling}</p>
                                      </div>
                                    )}
                                  </div>
                                ))}
                              </div>
                            )}
                          </>
                        ) : (
                          <p className="text-gray-500 italic">Nog geen evaluatieantwoorden beschikbaar</p>
                        )}
                      </div>
                    )}
                    
                    {/* Goals section */}
                    {viewingItem.data.doelen && viewingItem.data.doelen.length > 0 && (
                      <div className="space-y-3">
                        <h4 className="font-medium text-purple-700">Doelstellingen</h4>
                        <div className="space-y-2">
                          {viewingItem.data.doelen.map((doel, index) => (
                            <div key={index} className="border rounded-lg p-3 bg-purple-50">
                              <p className="text-sm">{doel}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ) : viewingItem.type === 'claim' ? (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-sm font-medium">Claim Nummer</Label>
                      <p className="text-sm font-mono">{viewingItem.data.claimnummer}</p>
                    </div>
                    <div>
                      <Label className="text-sm font-medium">Status</Label>
                      <Badge className="ml-2">{viewingItem.data.status}</Badge>
                    </div>
                    <div>
                      <Label className="text-sm font-medium">Schade Datum</Label>
                      <p className="text-sm">{format(new Date(viewingItem.data.schade_datum), "PPP", { locale: nl })}</p>
                    </div>
                    <div>
                      <Label className="text-sm font-medium">Gemeld Datum</Label>
                      <p className="text-sm">{format(new Date(viewingItem.data.gemeld_datum), "PPP", { locale: nl })}</p>
                    </div>
                    <div>
                      <Label className="text-sm font-medium">Schadebedrag</Label>
                      <p className="text-sm">€{viewingItem.data.schadebedrag?.toFixed(2) || '0.00'}</p>
                    </div>
                    <div>
                      <Label className="text-sm font-medium">Eigen Risico</Label>
                      <p className="text-sm">€{viewingItem.data.eigen_risico?.toFixed(2) || '0.00'}</p>
                    </div>
                  </div>
                  <div>
                    <Label className="text-sm font-medium">Omschrijving</Label>
                    <p className="text-sm mt-1 p-3 bg-gray-50 rounded">{viewingItem.data.omschrijving}</p>
                  </div>
                  
                  {/* Messageboard/Timeline Section - Read Only */}
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <Label className="text-sm font-medium flex items-center gap-2">
                        <MessageSquare className="h-4 w-4" />
                        Opmerkingen Timeline
                      </Label>
                      <Badge variant="outline">{claimComments.length} berichten</Badge>
                    </div>
                    
                    {/* Comments Timeline - Read Only */}
                    <div className="border rounded-lg p-4 max-h-80 overflow-y-auto space-y-3">
                      {claimComments.length === 0 ? (
                        <div className="text-center text-gray-500 py-6">
                          <MessageSquare className="h-8 w-8 mx-auto mb-2 opacity-50" />
                          <p>Nog geen opmerkingen toegevoegd</p>
                        </div>
                      ) : (
                        claimComments.map((comment) => (
                          <div key={comment.id} className="bg-gray-50 rounded-lg p-3 border-l-4 border-blue-200">
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-1">
                                  <span className="font-medium text-sm">{comment.gebruiker_naam}</span>
                                  <span className="text-xs text-gray-500">
                                    {new Date(comment.aangemaakt_op).toLocaleString('nl-NL', {
                                      day: '2-digit',
                                      month: '2-digit',
                                      year: 'numeric',
                                      hour: '2-digit',
                                      minute: '2-digit'
                                    })}
                                  </span>
                                  {comment.bijgewerkt_op && comment.bijgewerkt_op !== comment.aangemaakt_op && (
                                    <span className="text-xs text-gray-400 italic">(bewerkt)</span>
                                  )}
                                </div>
                                <p className="text-sm whitespace-pre-wrap">{comment.opmerking}</p>
                              </div>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                  
                  {/* Documenten/Bijlages Sectie - Read Only */}
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <Label className="text-sm font-medium flex items-center gap-2">
                        <Upload className="h-4 w-4" />
                        Bijlages
                      </Label>
                      <div className="flex items-center gap-2">
                        <Badge variant="outline">{documents.filter(doc => doc.entity_type === 'claim' && doc.entity_id === viewingItem.data.id).length} bestanden</Badge>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setShowDocumentUpload({ 
                              type: 'claim', 
                              id: viewingItem.data.id, 
                              name: `Claim ${viewingItem.data.claimnummer}` 
                            });
                            fetchEntityDocuments('claim', viewingItem.data.id);
                          }}
                        >
                          <Upload className="h-4 w-4 mr-1" />
                          Documenten Beheren
                        </Button>
                      </div>
                    </div>
                    
                    {/* Documents List - Read Only */}
                    <div className="border rounded-lg p-4 max-h-40 overflow-y-auto">
                      {documents.filter(doc => doc.entity_type === 'claim' && doc.entity_id === viewingItem.data.id).length === 0 ? (
                        <div className="text-center text-gray-500 py-4">
                          <Upload className="h-8 w-8 mx-auto mb-2 opacity-50" />
                          <p>Geen bijlages toegevoegd</p>
                        </div>
                      ) : (
                        <div className="space-y-2">
                          {documents.filter(doc => doc.entity_type === 'claim' && doc.entity_id === viewingItem.data.id).map((document) => (
                            <div key={document.id} className="flex items-center justify-between p-2 border rounded bg-gray-50">
                              <div className="flex-1">
                                <p className="text-sm font-medium">{document.original_name}</p>
                                <p className="text-xs text-gray-500">
                                  {document.description} • Geüpload op {new Date(document.upload_date).toLocaleDateString('nl-NL')}
                                </p>
                              </div>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => window.open(`${BACKEND_URL}/api/documents/download/${document.id}`, '_blank')}
                              >
                                <Download className="h-4 w-4" />
                              </Button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {/* Contacten Sectie - Read Only */}
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <Label className="text-sm font-medium flex items-center gap-2">
                        <Users className="h-4 w-4" />
                        Contacten
                      </Label>
                      <div className="flex items-center gap-2">
                        <Badge variant="outline">{claimContacts.length} contacten</Badge>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setShowContactDialog({ claimId: viewingItem.data.id });
                            fetchClaimContacts(viewingItem.data.id);
                          }}
                        >
                          <Users className="h-4 w-4 mr-1" />
                          Beheren
                        </Button>
                      </div>
                    </div>
                    
                    {/* Contacts List - Read Only */}
                    <div className="border rounded-lg p-4 max-h-40 overflow-y-auto">
                      {claimContacts.length === 0 ? (
                        <div className="text-center text-gray-500 py-4">
                          <Users className="h-8 w-8 mx-auto mb-2 opacity-50" />
                          <p>Nog geen contacten toegevoegd</p>
                        </div>
                      ) : (
                        <div className="space-y-2">
                          {claimContacts.map((contact) => (
                            <div key={contact.id} className="flex items-center justify-between p-2 border rounded bg-gray-50">
                              <div className="flex-1">
                                <p className="text-sm font-medium">{contact.naam}</p>
                                <p className="text-xs text-gray-500">
                                  {contact.rol} 
                                  {contact.email && ` • ${contact.email}`}
                                  {contact.gsm && ` • ${contact.gsm}`}
                                </p>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ) : viewingItem.type === 'voertuig' ? (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-sm font-medium">Kenteken</Label>
                      <p className="text-sm font-mono">{viewingItem.data.kenteken}</p>
                    </div>
                    <div>
                      <Label className="text-sm font-medium">Merk & Model</Label>
                      <p className="text-sm">{viewingItem.data.merk} {viewingItem.data.model}</p>
                    </div>
                    <div>
                      <Label className="text-sm font-medium">Type</Label>
                      <Badge className="ml-2">{viewingItem.data.type}</Badge>
                    </div>
                    <div>
                      <Label className="text-sm font-medium">Brandstoftype</Label>
                      <p className="text-sm">{viewingItem.data.brandstoftype}</p>
                    </div>
                    <div>
                      <Label className="text-sm font-medium">Kilometerstand</Label>
                      <p className="text-sm">{viewingItem.data.kilometerstand?.toLocaleString()} km</p>
                    </div>
                    <div>
                      <Label className="text-sm font-medium">Eerste Inschrijving</Label>
                      <p className="text-sm">{format(new Date(viewingItem.data.eerste_inschrijving), "PPP", { locale: nl })}</p>
                    </div>
                  </div>
                </div>
              ) : viewingItem.type === 'onderhoud' ? (
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <h3 className="text-lg font-semibold">Onderhoudshistorie</h3>
                    <Button onClick={() => setAddingMaintenance(viewingItem.data.voertuig_id)}>
                      <Plus className="h-4 w-4 mr-2" />
                      Onderhoud Toevoegen
                    </Button>
                  </div>
                  {viewingItem.data.onderhoud && viewingItem.data.onderhoud.length > 0 ? (
                    <div className="space-y-3">
                      {viewingItem.data.onderhoud.map((onderhoud, index) => (
                        <div key={index} className="border rounded-lg p-4">
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <Label className="text-sm font-medium">Type</Label>
                              <Badge className="ml-2">{onderhoud.type}</Badge>
                            </div>
                            <div>
                              <Label className="text-sm font-medium">Datum</Label>
                              <p className="text-sm">{format(new Date(onderhoud.datum), "PPP", { locale: nl })}</p>
                            </div>
                            <div>
                              <Label className="text-sm font-medium">Kilometerstand</Label>
                              <p className="text-sm">{onderhoud.kilometerstand?.toLocaleString()} km</p>
                            </div>
                            <div>
                              <Label className="text-sm font-medium">Kosten</Label>
                              <p className="text-sm">€{onderhoud.kosten?.toFixed(2)}</p>
                            </div>
                          </div>
                          <div className="mt-2">
                            <Label className="text-sm font-medium">Omschrijving</Label>
                            <p className="text-sm mt-1 p-2 bg-gray-50 rounded">{onderhoud.omschrijving}</p>
                          </div>
                          {onderhoud.garage && (
                            <div className="mt-2">
                              <Label className="text-sm font-medium">Garage</Label>
                              <p className="text-sm">{onderhoud.garage}</p>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-500">Geen onderhoudshistorie beschikbaar</p>
                  )}
                </div>
              ) : viewingItem.type === 'insurance_claims' ? (
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold">Claims voor deze polis</h3>
                  {viewingItem.data.claims && viewingItem.data.claims.length > 0 ? (
                    <div className="space-y-3">
                      {viewingItem.data.claims.map((claim, index) => (
                        <div key={index} className="border rounded-lg p-4">
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <Label className="text-sm font-medium">Claim Nummer</Label>
                              <p className="text-sm font-mono">{claim.claimnummer}</p>
                            </div>
                            <div>
                              <Label className="text-sm font-medium">Status</Label>
                              <Badge className="ml-2">{claim.status}</Badge>
                            </div>
                            <div>
                              <Label className="text-sm font-medium">Schade Datum</Label>
                              <p className="text-sm">{format(new Date(claim.schade_datum), "PPP", { locale: nl })}</p>
                            </div>
                            <div>
                              <Label className="text-sm font-medium">Schadebedrag</Label>
                              <p className="text-sm">€{claim.schadebedrag?.toFixed(2) || '0.00'}</p>
                            </div>
                          </div>
                          <div className="mt-2">
                            <Label className="text-sm font-medium">Omschrijving</Label>
                            <p className="text-sm mt-1 p-2 bg-gray-50 rounded">{claim.omschrijving}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-500">Geen claims voor deze polis</p>
                  )}
                </div>
              ) : viewingItem.type === 'verzekering' ? (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-sm font-medium">Polisnummer</Label>
                      <p className="text-sm font-mono">{viewingItem.data.polisnummer}</p>
                    </div>
                    <div>
                      <Label className="text-sm font-medium">Type</Label>
                      <Badge className="ml-2">{viewingItem.data.type}</Badge>
                    </div>
                    <div>
                      <Label className="text-sm font-medium">Verzekeraar</Label>
                      <p className="text-sm">{viewingItem.data.verzekeraar}</p>
                    </div>
                    <div>
                      <Label className="text-sm font-medium">Jaarlijkse Premie</Label>
                      <p className="text-sm">€{viewingItem.data.premie?.toFixed(2) || '0.00'}</p>
                    </div>
                    <div>
                      <Label className="text-sm font-medium">Begindatum</Label>
                      <p className="text-sm">{format(new Date(viewingItem.data.begindatum), "PPP", { locale: nl })}</p>
                    </div>
                    <div>
                      <Label className="text-sm font-medium">Einddatum</Label>
                      <p className="text-sm">
                        {viewingItem.data.einddatum 
                          ? format(new Date(viewingItem.data.einddatum), "PPP", { locale: nl })
                          : "Geen einddatum ingesteld"
                        }
                      </p>
                    </div>
                  </div>
                  <div>
                    <Label className="text-sm font-medium">Dekking</Label>
                    <p className="text-sm mt-1 p-3 bg-gray-50 rounded">
                      {(() => {
                        const dekkingsWaarde = parseInt(viewingItem.data.dekking);
                        return !isNaN(dekkingsWaarde) 
                          ? `€${dekkingsWaarde.toLocaleString('nl-NL')}`
                          : viewingItem.data.dekking;
                      })()}
                    </p>
                  </div>
                  {viewingItem.data.opmerking && (
                    <div>
                      <Label className="text-sm font-medium">Opmerkingen</Label>
                      <p className="text-sm mt-1 p-3 bg-gray-50 rounded">{viewingItem.data.opmerking}</p>
                    </div>
                  )}
                </div>
              ) : viewingItem.type === 'contract' ? (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-sm font-medium">Contract Naam</Label>
                      <p className="text-sm font-semibold">{viewingItem.data.naam}</p>
                    </div>
                    <div>
                      <Label className="text-sm font-medium">Type</Label>
                      <Badge className="ml-2">{viewingItem.data.type}</Badge>
                    </div>
                    <div>
                      <Label className="text-sm font-medium">Partij</Label>
                      <p className="text-sm">{viewingItem.data.partij}</p>
                    </div>
                    <div>
                      <Label className="text-sm font-medium">Status</Label>
                      <Badge variant="outline" className="ml-2">
                        {viewingItem.data.einddatum && new Date(viewingItem.data.einddatum) < new Date() ? 'Verlopen' : 'Actief'}
                      </Badge>
                    </div>
                    <div>
                      <Label className="text-sm font-medium">Begindatum</Label>
                      <p className="text-sm">{format(new Date(viewingItem.data.begindatum), "PPP", { locale: nl })}</p>
                    </div>
                    <div>
                      <Label className="text-sm font-medium">Einddatum</Label>
                      <p className="text-sm">
                        {viewingItem.data.einddatum 
                          ? format(new Date(viewingItem.data.einddatum), "PPP", { locale: nl })
                          : "Geen einddatum ingesteld"
                        }
                      </p>
                    </div>
                  </div>
                  
                  {/* Financiële informatie */}
                  {(viewingItem.data.maandelijks_bedrag > 0 || viewingItem.data.jaarlijks_bedrag > 0) && (
                    <div>
                      <Label className="text-sm font-medium">Financiële Details</Label>
                      <div className="mt-2 p-3 bg-gray-50 rounded space-y-1">
                        {viewingItem.data.maandelijks_bedrag > 0 && (
                          <p className="text-sm"><span className="font-medium">Maandelijks:</span> €{viewingItem.data.maandelijks_bedrag?.toFixed(2) || '0.00'}</p>
                        )}
                        {viewingItem.data.jaarlijks_bedrag > 0 && (
                          <p className="text-sm"><span className="font-medium">Jaarlijks:</span> €{viewingItem.data.jaarlijks_bedrag?.toFixed(2) || '0.00'}</p>
                        )}
                      </div>
                    </div>
                  )}
                  
                  {/* Opmerkingen */}
                  {viewingItem.data.opmerkingen && (
                    <div>
                      <Label className="text-sm font-medium">Opmerkingen</Label>
                      <p className="text-sm mt-1 p-3 bg-gray-50 rounded whitespace-pre-wrap">{viewingItem.data.opmerkingen}</p>
                    </div>
                  )}
                  
                  {/* Documenten Sectie - Read Only */}
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <Label className="text-sm font-medium flex items-center gap-2">
                        <Upload className="h-4 w-4" />
                        Bijlages
                      </Label>
                      <div className="flex items-center gap-2">
                        <Badge variant="outline">{documents.filter(doc => doc.entity_type === 'contract' && doc.entity_id === viewingItem.data.id).length} bestanden</Badge>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setShowDocumentUpload({ 
                              type: 'contract', 
                              id: viewingItem.data.id, 
                              name: `Contract ${viewingItem.data.naam}` 
                            });
                            fetchEntityDocuments('contract', viewingItem.data.id);
                          }}
                        >
                          <Upload className="h-4 w-4 mr-1" />
                          Documenten Beheren
                        </Button>
                      </div>
                    </div>
                    
                    {/* Documents List - Read Only */}
                    <div className="border rounded-lg p-4 max-h-40 overflow-y-auto">
                      {documents.filter(doc => doc.entity_type === 'contract' && doc.entity_id === viewingItem.data.id).length === 0 ? (
                        <div className="text-center text-gray-500 py-4">
                          <Upload className="h-8 w-8 mx-auto mb-2 opacity-50" />
                          <p>Geen bijlages toegevoegd</p>
                        </div>
                      ) : (
                        <div className="space-y-2">
                          {documents.filter(doc => doc.entity_type === 'contract' && doc.entity_id === viewingItem.data.id).map((document) => (
                            <div key={document.id} className="flex items-center justify-between p-2 border rounded bg-gray-50">
                              <div className="flex-1">
                                <p className="text-sm font-medium">{document.original_name}</p>
                                <p className="text-xs text-gray-500">
                                  {document.description} • Geüpload op {new Date(document.upload_date).toLocaleDateString('nl-NL')}
                                </p>
                              </div>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => window.open(`${BACKEND_URL}/api/documents/download/${document.id}`, '_blank')}
                              >
                                <Download className="h-4 w-4" />
                              </Button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ) : viewingItem.type === 'template' ? (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-sm font-medium">Template Naam</Label>
                      <p className="text-sm font-semibold">{viewingItem.data.naam}</p>
                    </div>
                    <div>
                      <Label className="text-sm font-medium">Type</Label>
                      <Badge className="ml-2">{viewingItem.data.type}</Badge>
                    </div>
                  </div>
                  <div>
                    <Label className="text-sm font-medium">Beschrijving</Label>
                    <p className="text-sm mt-1 p-3 bg-gray-50 rounded">{viewingItem.data.beschrijving}</p>
                  </div>
                  
                  <Separator />
                  
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold">Evaluatievragen ({viewingItem.data.vragen?.length || 0})</h3>
                    
                    {viewingItem.data.vragen && viewingItem.data.vragen.length > 0 ? (
                      <div className="space-y-3">
                        {viewingItem.data.vragen
                          .sort((a, b) => a.volgorde - b.volgorde)
                          .map((vraag, index) => (
                            <div key={vraag.id} className="border rounded-lg p-4 bg-blue-50">
                              <div className="flex justify-between items-start mb-2">
                                <div className="flex-1">
                                  <div className="flex items-center gap-2 mb-1">
                                    <Badge variant="outline">#{vraag.volgorde}</Badge>
                                    <Label className="text-sm font-medium text-blue-700">{vraag.sectie}</Label>
                                    {vraag.verplicht && <Badge variant="destructive" className="text-xs">Verplicht</Badge>}
                                  </div>
                                  <p className="text-sm text-gray-700">{vraag.tekst}</p>
                                </div>
                              </div>
                            </div>
                          ))}
                      </div>
                    ) : (
                      <p className="text-gray-500">Geen vragen toegevoegd aan deze template</p>
                    )}
                  </div>
                </div>
              ) : (
                <pre className="bg-gray-100 p-4 rounded-lg overflow-auto text-sm">
                  {JSON.stringify(viewingItem.data, null, 2)}
                </pre>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setViewingItem(null)}>
              Sluiten
            </Button>
            {viewingItem?.type === 'evaluatie' && (
              <Button onClick={() => exportEvaluatiePDF(viewingItem.data.id)}>
                <Download className="h-4 w-4 mr-2" />
                PDF Downloaden
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={!!editingItem} onOpenChange={() => {
        setEditingItem(null);
        setClaimComments([]);
        setNewClaimComment('');
        setEditingComment(null);
      }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {editingItem?.type === 'bedrijf' ? 'Bedrijf bewerken' :
               editingItem?.type === 'evaluatie' ? 'Evaluatie bewerken' :
               editingItem?.type === 'voertuig' ? 'Voertuig bewerken' :
               editingItem?.type === 'voordeel' ? 'Voordeel bewerken' :
               editingItem?.type === 'verzekering' ? 'Verzekering bewerken' :
               editingItem?.type === 'template' ? 'Template bewerken' :
               'Item bewerken'}
            </DialogTitle>
          </DialogHeader>
          {editingItem && (
            <div className="space-y-4">
              {editingItem.type === 'bedrijf' && (
                <>
                  <div>
                    <Label>Bedrijfsnaam</Label>
                    <Input
                      value={editData.naam || ''}
                      onChange={(e) => setEditData({...editData, naam: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label>Beschrijving</Label>
                    <Textarea
                      value={editData.beschrijving || ''}
                      onChange={(e) => setEditData({...editData, beschrijving: e.target.value})}
                    />
                  </div>
                </>
              )}
              {editingItem.type === 'voertuig' && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Kenteken</Label>
                    <Input
                      value={editData.kenteken || ''}
                      onChange={(e) => setEditData({...editData, kenteken: e.target.value.toUpperCase()})}
                    />
                  </div>
                  <div>
                    <Label>Merk</Label>
                    <Input
                      value={editData.merk || ''}
                      onChange={(e) => setEditData({...editData, merk: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label>Model</Label>
                    <Input
                      value={editData.model || ''}
                      onChange={(e) => setEditData({...editData, model: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label>Kilometerstand</Label>
                    <Input
                      type="number"
                      value={editData.kilometerstand || 0}
                      onChange={(e) => setEditData({...editData, kilometerstand: parseInt(e.target.value) || 0})}
                    />
                  </div>
                  <div>
                    <Label>Type</Label>
                    <Select value={editData.type} onValueChange={(value) => setEditData({...editData, type: value})}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="lease">Lease</SelectItem>
                        <SelectItem value="eigen">Eigen</SelectItem>
                        <SelectItem value="pool">Pool</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Bestuurder</Label>
                    <Select value={editData.bestuurder_id} onValueChange={(value) => setEditData({...editData, bestuurder_id: value})}>
                      <SelectTrigger>
                        <SelectValue placeholder="Selecteer bestuurder" />
                      </SelectTrigger>
                      <SelectContent>
                        {users.filter(u => u.bedrijf_ids?.includes(selectedBedrijf) || selectedBedrijf === 'alle').map(user => (
                          <SelectItem key={user.id} value={user.id}>
                            {user.naam} ({user.email})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}
              {editingItem.type === 'evaluatie' && (
                <>
                  <div>
                    <Label>Template</Label>
                    <Select value={editData.template_id} onValueChange={(value) => setEditData({...editData, template_id: value})}>
                      <SelectTrigger>
                        <SelectValue placeholder="Selecteer template" />
                      </SelectTrigger>
                      <SelectContent>
                        {templates.map(template => (
                          <SelectItem key={template.id} value={template.id}>
                            {template.naam}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Werknemer</Label>
                    <Select value={editData.werknemer_id} onValueChange={(value) => setEditData({...editData, werknemer_id: value})}>
                      <SelectTrigger>
                        <SelectValue placeholder="Selecteer werknemer" />
                      </SelectTrigger>
                      <SelectContent>
                        {users.filter(u => u.rol === 'werknemer' || u.rol === 'manager').map(user => (
                          <SelectItem key={user.id} value={user.id}>
                            {user.naam} ({user.email})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Manager</Label>
                    <Select value={editData.manager_id} onValueChange={(value) => setEditData({...editData, manager_id: value})}>
                      <SelectTrigger>
                        <SelectValue placeholder="Selecteer manager" />
                      </SelectTrigger>
                      <SelectContent>
                        {users.filter(u => u.rol === 'manager' || u.rol === 'hr' || u.rol === 'admin').map(user => (
                          <SelectItem key={user.id} value={user.id}>
                            {user.naam} ({user.email})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Type</Label>
                    <Select value={editData.type} onValueChange={(value) => setEditData({...editData, type: value})}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="initieel">Initiële evaluatie</SelectItem>
                        <SelectItem value="periodiek">Periodieke evaluatie</SelectItem>
                        <SelectItem value="tussentijds">Tussentijdse feedback</SelectItem>
                        <SelectItem value="verbeterplan">Verbeterplan</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Deadline</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className="w-full justify-start text-left font-normal">
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {editData.deadline ? format(new Date(editData.deadline), "PPP", { locale: nl }) : "Selecteer datum"}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0">
                        <Calendar
                          mode="single"
                          selected={editData.deadline ? new Date(editData.deadline) : null}
                          onSelect={(date) => setEditData({...editData, deadline: date})}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                </>
              )}
              {editingItem.type === 'template' && (
                <>
                  <div>
                    <Label>Template Naam</Label>
                    <Input
                      value={editData.naam || ''}
                      onChange={(e) => setEditData({...editData, naam: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label>Beschrijving</Label>
                    <Textarea
                      value={editData.beschrijving || ''}
                      onChange={(e) => setEditData({...editData, beschrijving: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label>Type</Label>
                    <Select value={editData.type} onValueChange={(value) => setEditData({...editData, type: value})}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="initieel">Initiële evaluatie</SelectItem>
                        <SelectItem value="periodiek">Periodieke evaluatie</SelectItem>
                        <SelectItem value="tussentijds">Tussentijdse feedback</SelectItem>
                        <SelectItem value="verbeterplan">Verbeterplan</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </>
              )}
              {editingItem.type === 'claim' && (
                <>
                  <div>
                    <Label>Omschrijving</Label>
                    <Textarea
                      value={editData.omschrijving || ''}
                      onChange={(e) => setEditData({...editData, omschrijving: e.target.value})}
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Schadebedrag (€)</Label>
                      <Input
                        type="number"
                        value={editData.schadebedrag || 0}
                        onChange={(e) => setEditData({...editData, schadebedrag: parseFloat(e.target.value) || 0})}
                      />
                    </div>
                    <div>
                      <Label>Eigen Risico (€)</Label>
                      <Input
                        type="number"
                        value={editData.eigen_risico || 0}
                        onChange={(e) => setEditData({...editData, eigen_risico: parseFloat(e.target.value) || 0})}
                      />
                    </div>
                  </div>
                  <div>
                    <Label>Status</Label>
                    <Select value={editData.status} onValueChange={(value) => setEditData({...editData, status: value})}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="ingediend">Ingediend</SelectItem>
                        <SelectItem value="in_behandeling">In behandeling</SelectItem>
                        <SelectItem value="goedgekeurd">Goedgekeurd</SelectItem>
                        <SelectItem value="afgewezen">Afgewezen</SelectItem>
                        <SelectItem value="uitbetaald">Uitbetaald</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {/* Messageboard/Timeline Section */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <Label className="flex items-center gap-2">
                        <MessageSquare className="h-4 w-4" />
                        Opmerkingen Messageboard
                      </Label>
                      <Badge variant="outline">{claimComments.length} berichten</Badge>
                    </div>
                    
                    {/* Comments Timeline */}
                    <div className="border rounded-lg p-4 max-h-80 overflow-y-auto space-y-3 mb-4">
                      {claimComments.length === 0 ? (
                        <div className="text-center text-gray-500 py-4">
                          <MessageSquare className="h-8 w-8 mx-auto mb-2 opacity-50" />
                          <p>Nog geen opmerkingen toegevoegd</p>
                        </div>
                      ) : (
                        claimComments.map((comment) => (
                          <div key={comment.id} className="bg-gray-50 rounded-lg p-3 border-l-4 border-blue-200">
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-1">
                                  <span className="font-medium text-sm">{comment.gebruiker_naam}</span>
                                  <span className="text-xs text-gray-500">
                                    {new Date(comment.aangemaakt_op).toLocaleString('nl-NL', {
                                      day: '2-digit',
                                      month: '2-digit',
                                      year: 'numeric',
                                      hour: '2-digit',
                                      minute: '2-digit'
                                    })}
                                  </span>
                                </div>
                                {editingComment?.id === comment.id ? (
                                  <div className="space-y-2">
                                    <Textarea
                                      value={editingComment.text}
                                      onChange={(e) => setEditingComment({...editingComment, text: e.target.value})}
                                      className="min-h-[60px]"
                                    />
                                    <div className="flex gap-2">
                                      <Button
                                        size="sm"
                                        onClick={() => updateClaimComment(editingItem.id, comment.id, editingComment.text)}
                                      >
                                        Opslaan
                                      </Button>
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() => setEditingComment(null)}
                                      >
                                        Annuleren
                                      </Button>
                                    </div>
                                  </div>
                                ) : (
                                  <p className="text-sm whitespace-pre-wrap">{comment.opmerking}</p>
                                )}
                              </div>
                              {!editingComment && comment.gebruiker_id === user?.id && (
                                <div className="flex gap-1 ml-2">
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => setEditingComment({ id: comment.id, text: comment.opmerking })}
                                  >
                                    <Edit className="h-3 w-3" />
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => deleteClaimComment(editingItem.id, comment.id)}
                                  >
                                    <Trash2 className="h-3 w-3" />
                                  </Button>
                                </div>
                              )}
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                    
                    {/* Add New Comment */}
                    <div className="space-y-2">
                      <Textarea
                        placeholder="Voeg een nieuwe opmerking toe..."
                        value={newClaimComment}
                        onChange={(e) => setNewClaimComment(e.target.value)}
                        className="min-h-[80px]"
                      />
                      <div className="flex justify-between items-center">
                        <span className="text-xs text-gray-500">
                          Tip: Druk Shift+Enter voor een nieuwe regel
                        </span>
                        <Button
                          size="sm"
                          onClick={() => addClaimComment(editingItem.id)}
                          disabled={!newClaimComment.trim()}
                        >
                          <Plus className="h-4 w-4 mr-1" />
                          Opmerking Toevoegen
                        </Button>
                      </div>
                    </div>
                  </div>
                  
                  {/* Document Upload Section for Claims */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <Label>Documenten</Label>
                      <Button 
                        type="button"
                        variant="outline" 
                        size="sm" 
                        onClick={() => {
                          setShowDocumentUpload({ 
                            type: 'claim', 
                            id: editingItem.id, 
                            name: `Claim` 
                          });
                          fetchEntityDocuments('claim', editingItem.id);
                        }}
                      >
                        <Upload className="h-4 w-4 mr-2" />
                        Documenten Beheren
                      </Button>
                    </div>
                    <p className="text-sm text-gray-600">
                      Klik op "Documenten Beheren" om bestanden te uploaden of bestaande documenten te bekijken
                    </p>
                  </div>
                  
                  {/* Contacten Sectie voor Claims */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <Label className="flex items-center gap-2">
                        <Users className="h-4 w-4" />
                        Contacten
                      </Label>
                      <Button 
                        type="button"
                        variant="outline" 
                        size="sm" 
                        onClick={() => {
                          setShowContactDialog({ claimId: editingItem.id });
                          fetchClaimContacts(editingItem.id);
                        }}
                      >
                        <Users className="h-4 w-4 mr-2" />
                        Contacten Beheren
                      </Button>
                    </div>
                    <p className="text-sm text-gray-600">
                      Beheer contactpersonen zoals experts, makelaars, advocaten, etc.
                    </p>
                  </div>
                </>
              )}

              {editingItem.type === 'license' && (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Software Naam <span className="text-red-500">*</span></Label>
                      <Input
                        value={editData.naam || ''}
                        onChange={(e) => setEditData({...editData, naam: e.target.value})}
                        placeholder="Microsoft Office 365"
                      />
                    </div>
                    <div>
                      <Label>Leverancier <span className="text-red-500">*</span></Label>
                      <Input
                        value={editData.leverancier || ''}
                        onChange={(e) => setEditData({...editData, leverancier: e.target.value})}
                        placeholder="Microsoft"
                      />
                    </div>
                    <div>
                      <Label>Type</Label>
                      <Select value={editData.type || ''} onValueChange={(value) => setEditData({...editData, type: value})}>
                        <SelectTrigger>
                          <SelectValue placeholder="Selecteer type" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="software">Software</SelectItem>
                          <SelectItem value="saas">SaaS</SelectItem>
                          <SelectItem value="cloud">Cloud Service</SelectItem>
                          <SelectItem value="desktop">Desktop Applicatie</SelectItem>
                          <SelectItem value="mobile">Mobile App</SelectItem>
                          <SelectItem value="ontwikkeling">Ontwikkeltools</SelectItem>
                          <SelectItem value="security">Security Software</SelectItem>
                          <SelectItem value="overig">Overig</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Bedrijf <span className="text-red-500">*</span></Label>
                      <Select value={editData.bedrijf_id || ''} onValueChange={(value) => setEditData({...editData, bedrijf_id: value})}>
                        <SelectTrigger>
                          <SelectValue placeholder="Selecteer bedrijf" />
                        </SelectTrigger>
                        <SelectContent>
                          {bedrijven.map(bedrijf => (
                            <SelectItem key={bedrijf.id} value={bedrijf.id}>{bedrijf.naam}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Licentie Sleutel</Label>
                      <Input
                        value={editData.licentie_sleutel || ''}
                        onChange={(e) => setEditData({...editData, licentie_sleutel: e.target.value})}
                        placeholder="XXXXX-XXXXX-XXXXX-XXXXX"
                        type="password"
                      />
                    </div>
                    <div>
                      <Label>Gebruikers Limiet</Label>
                      <Input
                        type="number"
                        value={editData.gebruikers_limiet || 0}
                        onChange={(e) => setEditData({...editData, gebruikers_limiet: parseInt(e.target.value) || 0})}
                        placeholder="50"
                      />
                    </div>
                    <div>
                      <Label>Kosten per Jaar (€)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={editData.kosten_per_jaar || 0}
                        onChange={(e) => setEditData({...editData, kosten_per_jaar: parseFloat(e.target.value) || 0})}
                        placeholder="1200.00"
                      />
                    </div>
                    <div>
                      <Label>Verloop Datum</Label>
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            className="w-full justify-start text-left font-normal"
                          >
                            <CalendarIcon className="mr-2 h-4 w-4" />
                            {editData.verloopdatum ? format(new Date(editData.verloopdatum), "PPP", { locale: nl }) : "Selecteer datum (optioneel)"}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0">
                          <Calendar
                            mode="single"
                            selected={editData.verloopdatum ? new Date(editData.verloopdatum) : null}
                            onSelect={(date) => setEditData({...editData, verloopdatum: date})}
                            initialFocus
                          />
                        </PopoverContent>
                      </Popover>
                    </div>
                  </div>
                  
                  <div>
                    <Label>Opmerkingen</Label>
                    <Textarea
                      value={editData.opmerkingen || ''}
                      onChange={(e) => setEditData({...editData, opmerkingen: e.target.value})}
                      placeholder="Optionele opmerkingen over de licentie"
                    />
                  </div>
                </>
              )}

              {editingItem.type === 'verzekering' && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Naam</Label>
                    <Input
                      value={editData.naam || ''}
                      onChange={(e) => setEditData({...editData, naam: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label>Type</Label>
                    <Select value={editData.type} onValueChange={(value) => setEditData({...editData, type: value})}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="ba">BA Verzekering</SelectItem>
                        <SelectItem value="brand">Brandverzekering</SelectItem>
                        <SelectItem value="cyber">Cyberverzekering</SelectItem>
                        <SelectItem value="voertuig">Voertuigverzekering</SelectItem>
                        <SelectItem value="groepsverzekering">Groepsverzekering</SelectItem>
                        <SelectItem value="aansprakelijkheid">Aansprakelijkheid</SelectItem>
                        <SelectItem value="arbeidsongevallen">Arbeidsongevallen</SelectItem>
                        <SelectItem value="rechtsbijstand">Rechtsbijstand</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Verzekeraar</Label>
                    <Input
                      value={editData.verzekeraar || ''}
                      onChange={(e) => setEditData({...editData, verzekeraar: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label>Polisnummer</Label>
                    <Input
                      value={editData.polisnummer || ''}
                      onChange={(e) => setEditData({...editData, polisnummer: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label>Jaarlijkse Premie (€)</Label>
                    <Input
                      type="number"
                      value={editData.premie || 0}
                      onChange={(e) => setEditData({...editData, premie: parseFloat(e.target.value) || 0})}
                    />
                  </div>
                  <div>
                    <Label>Dekking</Label>
                    <Input
                      value={editData.dekking || ''}
                      onChange={(e) => setEditData({...editData, dekking: e.target.value})}
                    />
                  </div>
                  <div className="col-span-2">
                    <Label>Einddatum (optioneel)</Label>
                    <div className="flex gap-2">
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button variant="outline" className="flex-1 justify-start text-left font-normal">
                            <CalendarIcon className="mr-2 h-4 w-4" />
                            {editData.einddatum ? format(new Date(editData.einddatum), "PPP", { locale: nl }) : "Selecteer datum"}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0">
                          <Calendar
                            mode="single"
                            selected={editData.einddatum ? new Date(editData.einddatum) : null}
                            onSelect={(date) => setEditData({...editData, einddatum: date})}
                            initialFocus
                          />
                        </PopoverContent>
                      </Popover>
                      {editData.einddatum && (
                        <Button 
                          variant="outline" 
                          size="sm" 
                          onClick={() => setEditData({...editData, einddatum: null})}
                        >
                          ×
                        </Button>
                      )}
                    </div>
                  </div>
                  
                  {/* Opmerkingen */}
                  <div className="col-span-2">
                    <Label>Opmerkingen</Label>
                    <Textarea
                      value={editData.opmerking || ''}
                      onChange={(e) => setEditData({...editData, opmerking: e.target.value})}
                      placeholder="Optionele opmerkingen"
                    />
                  </div>
                  
                  {/* Document Upload Section for Insurance */}
                  <div className="col-span-2">
                    <div className="flex items-center justify-between mb-2">
                      <Label>Documenten</Label>
                      <Button 
                        type="button"
                        variant="outline" 
                        size="sm" 
                        onClick={() => {
                          setShowDocumentUpload({ 
                            type: 'verzekering', 
                            id: editingItem.id, 
                            name: editData.naam || editingItem.data?.naam || `Verzekering ${editingItem.id.slice(0, 8)}`
                          });
                          fetchEntityDocuments('verzekering', editingItem.id);
                        }}
                      >
                        <Upload className="h-4 w-4 mr-2" />
                        Documenten Beheren
                      </Button>
                    </div>
                    <p className="text-sm text-gray-600">
                      Klik op "Documenten Beheren" om bestanden te uploaden of bestaande documenten te bekijken
                    </p>
                  </div>
                </div>
              )}
              {editingItem.type === 'voordeel' && (
                <>
                  <div>
                    <Label>Type Voordeel</Label>
                    <Select value={editData.type} onValueChange={(value) => setEditData({...editData, type: value})}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="fietslease">Fietslease</SelectItem>
                        <SelectItem value="groepsverzekering">Groepsverzekering</SelectItem>
                        <SelectItem value="hospitaliteit">Hospitaliteit</SelectItem>
                        <SelectItem value="mobiliteitsbudget">Mobiliteitsbudget</SelectItem>
                        <SelectItem value="wagen">Wagen</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {editData.type === 'wagen' && (
                    <div>
                      <Label>Selecteer Voertuig</Label>
                      <Select value={editData.voertuig_id || ''} onValueChange={(value) => setEditData({...editData, voertuig_id: value})}>
                        <SelectTrigger>
                          <SelectValue placeholder="Kies een voertuig" />
                        </SelectTrigger>
                        <SelectContent>
                          {voertuigen.map(voertuig => (
                            <SelectItem key={voertuig.id} value={voertuig.id}>
                              {voertuig.kenteken} - {voertuig.merk} {voertuig.model}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  <div>
                    <Label>Beschrijving</Label>
                    <Textarea
                      value={editData.beschrijving || ''}
                      onChange={(e) => setEditData({...editData, beschrijving: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label>Waarde (€)</Label>
                    <Input
                      type="number"
                      value={editData.waarde || 0}
                      onChange={(e) => setEditData({...editData, waarde: parseFloat(e.target.value) || 0})}
                    />
                  </div>
                </>
              )}

              {editingItem.type === 'contract' && (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Contract Naam <span className="text-red-500">*</span></Label>
                      <Input
                        value={editData.naam || ''}
                        onChange={(e) => setEditData({...editData, naam: e.target.value})}
                        placeholder="Huurcontract kantoor"
                      />
                    </div>
                    <div>
                      <Label>Type <span className="text-red-500">*</span></Label>
                      <Select value={editData.type || ''} onValueChange={(value) => setEditData({...editData, type: value})}>
                        <SelectTrigger>
                          <SelectValue placeholder="Selecteer type" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="huur">Huurcontract</SelectItem>
                          <SelectItem value="lease">Leasecontract</SelectItem>
                          <SelectItem value="service">Servicecontract</SelectItem>
                          <SelectItem value="leverancier">Leverancierscontract</SelectItem>
                          <SelectItem value="onderhoud">Onderhoudscontract</SelectItem>
                          <SelectItem value="verzekering">Verzekeringscontract</SelectItem>
                          <SelectItem value="software">Softwarecontract</SelectItem>
                          <SelectItem value="overig">Overig</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Partij <span className="text-red-500">*</span></Label>
                      <Input
                        value={editData.partij || ''}
                        onChange={(e) => setEditData({...editData, partij: e.target.value})}
                        placeholder="ABC Verhuur BV"
                      />
                    </div>
                    <div>
                      <Label>Maandelijks Bedrag (€)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={editData.maandelijks_bedrag || 0}
                        onChange={(e) => setEditData({...editData, maandelijks_bedrag: parseFloat(e.target.value) || 0})}
                        placeholder="1500.00"
                      />
                    </div>
                    <div>
                      <Label>Jaarlijks Bedrag (€)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={editData.jaarlijks_bedrag || 0}
                        onChange={(e) => setEditData({...editData, jaarlijks_bedrag: parseFloat(e.target.value) || 0})}
                        placeholder="18000.00"
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Begindatum</Label>
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            className="w-full justify-start text-left font-normal"
                          >
                            <CalendarIcon className="mr-2 h-4 w-4" />
                            {editData.begindatum ? format(new Date(editData.begindatum), "PPP", { locale: nl }) : "Selecteer datum"}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0">
                          <Calendar
                            mode="single"
                            selected={editData.begindatum ? new Date(editData.begindatum) : null}
                            onSelect={(date) => setEditData({...editData, begindatum: date})}
                            initialFocus
                          />
                        </PopoverContent>
                      </Popover>
                    </div>
                    <div>
                      <Label>Einddatum</Label>
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            className="w-full justify-start text-left font-normal"
                          >
                            <CalendarIcon className="mr-2 h-4 w-4" />
                            {editData.einddatum ? format(new Date(editData.einddatum), "PPP", { locale: nl }) : "Selecteer datum (optioneel)"}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0">
                          <Calendar
                            mode="single"
                            selected={editData.einddatum ? new Date(editData.einddatum) : null}
                            onSelect={(date) => setEditData({...editData, einddatum: date})}
                            initialFocus
                          />
                        </PopoverContent>
                      </Popover>
                    </div>
                  </div>
                  
                  <div>
                    <Label>Opmerkingen</Label>
                    <Textarea
                      value={editData.opmerkingen || ''}
                      onChange={(e) => setEditData({...editData, opmerkingen: e.target.value})}
                      placeholder="Optionele opmerkingen over het contract"
                    />
                  </div>
                  
                  {/* Document beheer voor contracten */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <Label>Documenten</Label>
                      <Button 
                        type="button"
                        variant="outline" 
                        size="sm" 
                        onClick={() => {
                          setShowDocumentUpload({ 
                            type: 'contract', 
                            id: editingItem.id, 
                            name: `Contract ${editData.naam || ''}` 
                          });
                          fetchEntityDocuments('contract', editingItem.id);
                        }}
                      >
                        <Upload className="h-4 w-4 mr-2" />
                        Documenten Beheren
                      </Button>
                    </div>
                    <p className="text-sm text-gray-600">
                      Klik op "Documenten Beheren" om bestanden te uploaden of bestaande documenten te bekijken
                    </p>
                  </div>
                </>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setEditingItem(null);
              setClaimComments([]);
              setNewClaimComment('');
              setEditingComment(null);
            }}>
              Annuleren
            </Button>
            <Button onClick={() => updateItem(editingItem.type, editingItem.id, editData)}>
              Bijwerken
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Bevestig verwijdering</DialogTitle>
            <DialogDescription>
              Weet je zeker dat je "{deleteConfirm?.name}" wilt verwijderen? Deze actie kan niet ongedaan worden gemaakt.
              {deleteConfirm?.type === 'evaluatie' && user?.rol === 'admin' && (
                <div className="mt-2 p-3 bg-orange-50 border border-orange-200 rounded-md">
                  <p className="text-sm text-orange-800">
                    <strong>Admin-privilege:</strong> Als admin kun je evaluaties verwijderen, ook die met ingediende antwoorden. 
                    Alle evaluatiedata (scores, opmerkingen, doelstellingen) gaan verloren.
                  </p>
                </div>
              )}
              {deleteConfirm?.type === 'claim' && user?.rol === 'admin' && (
                <div className="mt-2 p-3 bg-orange-50 border border-orange-200 rounded-md">
                  <p className="text-sm text-orange-800">
                    <strong>Admin-privilege:</strong> Als admin kun je claims verwijderen, ook die al verwerkt zijn 
                    (goedgekeurd/uitbetaald). Alle claimdata en historiek gaan verloren.
                  </p>
                </div>
              )}
              {deleteConfirm?.type === 'gebruiker' && (
                <div className="mt-2 p-3 bg-red-50 border border-red-200 rounded-md">
                  <p className="text-sm text-red-800">
                    <strong>Waarschuwing:</strong> Het verwijderen van een gebruiker is onomkeerbaar. 
                    Alle gerelateerde evaluaties, claims en andere data blijven behouden maar zijn 
                    niet meer toegankelijk voor deze gebruiker.
                  </p>
                </div>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirm(null)}>
              Annuleren
            </Button>
            <Button variant="destructive" onClick={() => deleteItem(deleteConfirm.type, deleteConfirm.id)}>
              Verwijderen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Fill Evaluation Dialog */}
      <Dialog open={!!fillingEvaluation} onOpenChange={() => {
        setFillingEvaluation(null);
        setEvaluationMode('zelfevaluatie');
      }}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Evaluatie Invullen</DialogTitle>
            <DialogDescription>
              Vul de evaluatievragen in met scores, evaluaties en doelstellingen.
            </DialogDescription>
          </DialogHeader>
          {fillingEvaluation && (
            <div className="space-y-6">
              {/* Admin Role Selection */}
              {user?.rol === 'admin' && (
                <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
                  <Label className="text-sm font-medium text-yellow-800">Invul Modus (Admin)</Label>
                  <Select value={evaluationMode} onValueChange={setEvaluationMode}>
                    <SelectTrigger className="mt-2">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="zelfevaluatie">Als Gebruiker (Zelfevaluatie)</SelectItem>
                      <SelectItem value="manager">Als Manager (Manager Beoordeling)</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-yellow-700 mt-1">
                    {evaluationMode === 'zelfevaluatie' 
                      ? 'Je vult de evaluatie in namens de werknemer' 
                      : 'Je vult de manager beoordeling in'}
                  </p>
                </div>
              )}
              
              <div className="bg-blue-50 p-4 rounded-lg">
                <h3 className="font-semibold">{fillingEvaluation.template?.naam}</h3>
                <p className="text-sm text-gray-600">{fillingEvaluation.template?.beschrijving}</p>
              </div>
              
              {fillingEvaluation.template?.vragen?.map((vraag, index) => {
                const answer = evaluationAnswers.find(a => a.vraag_id === vraag.id) || {};
                return (
                  <div key={vraag.id} className="border rounded-lg p-4 space-y-4">
                    <div>
                      <h4 className="font-medium">{vraag.sectie}</h4>
                      <p className="text-sm text-gray-700 mt-1">{vraag.tekst}</p>
                    </div>
                    
                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <Label>Score (1-5)</Label>
                        <Select 
                          value={answer.score?.toString() || ''} 
                          onValueChange={(value) => {
                            const newAnswers = [...evaluationAnswers];
                            const answerIndex = newAnswers.findIndex(a => a.vraag_id === vraag.id);
                            const scoreValue = value === 'null' ? null : parseInt(value);
                            if (answerIndex >= 0) {
                              newAnswers[answerIndex].score = scoreValue;
                            } else {
                              newAnswers.push({
                                vraag_id: vraag.id,
                                score: scoreValue,
                                opmerking: '',
                                doelstelling: ''
                              });
                            }
                            setEvaluationAnswers(newAnswers);
                          }}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Selecteer score" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="null">Niet van toepassing</SelectItem>
                            <SelectItem value="1">1 - Onvoldoende</SelectItem>
                            <SelectItem value="2">2 - Matig</SelectItem>
                            <SelectItem value="3">3 - Voldoende</SelectItem>
                            <SelectItem value="4">4 - Goed</SelectItem>
                            <SelectItem value="5">5 - Uitstekend</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      
                      <div>
                        <Label>Evaluatie/Opmerking</Label>
                        <Textarea
                          value={answer.opmerking || ''}
                          onChange={(e) => {
                            const newAnswers = [...evaluationAnswers];
                            const answerIndex = newAnswers.findIndex(a => a.vraag_id === vraag.id);
                            if (answerIndex >= 0) {
                              newAnswers[answerIndex].opmerking = e.target.value;
                            } else {
                              newAnswers.push({
                                vraag_id: vraag.id,
                                score: 1,
                                opmerking: e.target.value,
                                doelstelling: ''
                              });
                            }
                            setEvaluationAnswers(newAnswers);
                          }}
                          placeholder="Uw evaluatie van dit punt..."
                          className="h-8 resize-y"
                          rows={1}
                        />
                      </div>
                      
                      <div>
                        <Label>Doelstelling</Label>
                        <Textarea
                          value={answer.doelstelling || ''}
                          onChange={(e) => {
                            const newAnswers = [...evaluationAnswers];
                            const answerIndex = newAnswers.findIndex(a => a.vraag_id === vraag.id);
                            if (answerIndex >= 0) {
                              newAnswers[answerIndex].doelstelling = e.target.value;
                            } else {
                              newAnswers.push({
                                vraag_id: vraag.id,
                                score: 1,
                                opmerking: '',
                                doelstelling: e.target.value
                              });
                            }
                            setEvaluationAnswers(newAnswers);
                          }}
                          placeholder="Doelstelling voor verbetering..."
                          className="h-8 resize-y"
                          rows={1}
                        />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setFillingEvaluation(null);
              setEvaluationMode('zelfevaluatie');
            }}>
              Annuleren
            </Button>
            <Button onClick={async () => {
              try {
                const answersForApi = evaluationAnswers.map(a => ({
                  vraag_id: a.vraag_id,
                  score: a.score, // Can be null for "Niet van toepassing"
                  opmerking: a.opmerking || '',
                  doelstelling: a.doelstelling || ''
                }));
                
                const endpoint = user?.rol === 'admin' && evaluationMode === 'manager' 
                  ? `${API}/evaluaties/${fillingEvaluation.id}/manager-beoordeling`
                  : `${API}/evaluaties/${fillingEvaluation.id}/zelfevaluatie`;
                
                await axios.put(endpoint, answersForApi, {
                  headers: { 'Authorization': `Bearer ${token}` }
                });
                
                setFillingEvaluation(null);
                setEvaluationAnswers([]);
                setEvaluationMode('zelfevaluatie'); // Reset to default
                fetchData();
                
                const successMessage = user?.rol === 'admin' && evaluationMode === 'manager'
                  ? 'Manager beoordeling succesvol opgeslagen'
                  : 'Zelfevaluatie succesvol opgeslagen';
                toast.success(successMessage);
              } catch (error) {
                console.error('Error saving evaluation:', error);
                toast.error(error.response?.data?.detail || 'Fout bij opslaan evaluatie');
              }
            }}>
              Opslaan
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Template Questions Editor Dialog */}
      <Dialog open={!!editingTemplateQuestions} onOpenChange={() => setEditingTemplateQuestions(null)}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Template Vragen Bewerken</DialogTitle>
            <DialogDescription>
              Bewerk de evaluatievragen voor "{editingTemplateQuestions?.naam}".
            </DialogDescription>
          </DialogHeader>
          {editingTemplateQuestions && (
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-semibold">Vragen ({templateQuestions.length})</h3>
                <Button onClick={addQuestion}>
                  <Plus className="h-4 w-4 mr-2" />
                  Vraag Toevoegen
                </Button>
              </div>
              
              <div className="space-y-3">
                {templateQuestions.map((vraag, index) => (
                  <div key={vraag.id} className="border rounded-lg p-4 space-y-3">
                    <div className="flex justify-between items-start">
                      <div className="grid grid-cols-2 gap-4 flex-1">
                        <div>
                          <Label>Sectie</Label>
                          <Input
                            value={vraag.sectie}
                            onChange={(e) => updateQuestion(index, 'sectie', e.target.value)}
                            placeholder="Bijvoorbeeld: Technische Vaardigheden"
                          />
                        </div>
                        <div>
                          <Label>Volgorde</Label>
                          <Input
                            type="number"
                            value={vraag.volgorde}
                            onChange={(e) => updateQuestion(index, 'volgorde', parseInt(e.target.value) || index + 1)}
                          />
                        </div>
                      </div>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={() => removeQuestion(index)}
                        className="ml-4"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                    
                    <div>
                      <Label>Vraagstelling</Label>
                      <Textarea
                        value={vraag.tekst}
                        onChange={(e) => updateQuestion(index, 'tekst', e.target.value)}
                        placeholder="Bijvoorbeeld: Toont voldoende technische kennis voor complexe projecten"
                        className="h-8 resize-y"
                        rows={1}
                      />
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={vraag.verplicht}
                        onChange={(e) => updateQuestion(index, 'verplicht', e.target.checked)}
                        className="rounded"
                      />
                      <Label className="text-sm">Verplichte vraag</Label>
                    </div>
                  </div>
                ))}
              </div>
              
              {templateQuestions.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  <p>Geen vragen toegevoegd. Klik op "Vraag Toevoegen" om te beginnen.</p>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingTemplateQuestions(null)}>
              Annuleren
            </Button>
            <Button onClick={async () => {
              try {
                // Validation
                const emptyQuestions = templateQuestions.filter(q => !q.tekst.trim() || !q.sectie.trim());
                if (emptyQuestions.length > 0) {
                  toast.error(`Fout: ${emptyQuestions.length} vragen hebben geen tekst of sectie ingevuld. Alle velden zijn verplicht.`);
                  return;
                }
                
                const updatedTemplate = {
                  ...editingTemplateQuestions,
                  vragen: templateQuestions.map((q, index) => ({
                    id: q.id.startsWith('temp-') ? `vraag-${Date.now()}-${index}` : q.id,
                    tekst: q.tekst.trim(),
                    sectie: q.sectie.trim(),
                    volgorde: q.volgorde,
                    verplicht: q.verplicht
                  }))
                };
                
                await axios.put(`${API}/evaluatie-templates/${editingTemplateQuestions.id}`, updatedTemplate, {
                  headers: { 'Authorization': `Bearer ${token}` }
                });
                
                setEditingTemplateQuestions(null);
                setTemplateQuestions([]);
                fetchData();
                toast.success('Template vragen succesvol bijgewerkt');
              } catch (error) {
                console.error('Error updating template questions:', error);
                const errorMsg = error.response?.data?.detail || error.message || 'Onbekende fout';
                toast.error(`Fout bij bijwerken template: ${errorMsg}`);
              }
            }}>
              Opslaan
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Maintenance Dialog */}
      <Dialog open={!!addingMaintenance} onOpenChange={() => setAddingMaintenance(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Onderhoud Toevoegen</DialogTitle>
            <DialogDescription>
              Voeg nieuw onderhoud toe aan de onderhoudshistorie.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Type Onderhoud</Label>
                <Select value={newMaintenance.type} onValueChange={(value) => setNewMaintenance({...newMaintenance, type: value})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="onderhoud">Onderhoud</SelectItem>
                    <SelectItem value="reparatie">Reparatie</SelectItem>
                    <SelectItem value="apk">APK Keuring</SelectItem>
                    <SelectItem value="winterbanden">Winterbanden</SelectItem>
                    <SelectItem value="zomerbanden">Zomerbanden</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Datum</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-start text-left font-normal">
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {newMaintenance.datum ? format(new Date(newMaintenance.datum), "PPP", { locale: nl }) : "Selecteer datum"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <Calendar
                      mode="single"
                      selected={newMaintenance.datum ? new Date(newMaintenance.datum) : null}
                      onSelect={(date) => setNewMaintenance({...newMaintenance, datum: date})}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Kilometerstand</Label>
                <Input
                  type="number"
                  value={newMaintenance.kilometerstand}
                  onChange={(e) => setNewMaintenance({...newMaintenance, kilometerstand: parseInt(e.target.value) || 0})}
                  placeholder="150000"
                />
              </div>
              <div>
                <Label>Kosten (€)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={newMaintenance.kosten}
                  onChange={(e) => setNewMaintenance({...newMaintenance, kosten: parseFloat(e.target.value) || 0})}
                  placeholder="250.00"
                />
              </div>
            </div>
            
            <div>
              <Label>Omschrijving</Label>
              <Textarea
                value={newMaintenance.omschrijving}
                onChange={(e) => setNewMaintenance({...newMaintenance, omschrijving: e.target.value})}
                placeholder="Beschrijf het uitgevoerde onderhoud..."
                className="h-20"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Garage</Label>
                <Input
                  value={newMaintenance.garage}
                  onChange={(e) => setNewMaintenance({...newMaintenance, garage: e.target.value})}
                  placeholder="Garage naam"
                />
              </div>
              <div>
                <Label>Factuurnummer</Label>
                <Input
                  value={newMaintenance.factuur_nummer}
                  onChange={(e) => setNewMaintenance({...newMaintenance, factuur_nummer: e.target.value})}
                  placeholder="Optioneel"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddingMaintenance(null)}>
              Annuleren
            </Button>
            <Button onClick={createMaintenance}>
              Onderhoud Toevoegen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Document Upload Dialog */}
      <Dialog open={!!showDocumentUpload} onOpenChange={() => setShowDocumentUpload(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Documenten Beheren</DialogTitle>
            <DialogDescription>
              Upload en beheer documenten voor {showDocumentUpload?.name}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6">
            {/* File Upload Section */}
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6">
              <div className="text-center">
                <Upload className="mx-auto h-12 w-12 text-gray-400" />
                <div className="mt-4">
                  <Label htmlFor="file-upload" className="cursor-pointer">
                    <span className="mt-2 block text-sm font-medium text-gray-900">
                      Sleep bestanden hierheen of klik om te uploaden
                    </span>
                    <span className="text-xs text-gray-500">
                      PDF, Word, afbeeldingen (max 10MB per bestand)
                    </span>
                  </Label>
                  <Input
                    id="file-upload"
                    type="file"
                    multiple
                    accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.txt"
                    className="sr-only"
                    onChange={(e) => {
                      if (e.target.files && e.target.files.length > 0) {
                        uploadDocument(Array.from(e.target.files));
                      }
                    }}
                  />
                </div>
              </div>
            </div>

            {/* Existing Documents */}
            {entityDocuments.length > 0 && (
              <div>
                <h4 className="font-medium mb-3">Geüploade Documenten</h4>
                <div className="space-y-2">
                  {entityDocuments.map((doc) => (
                    <div key={doc.id} className="flex items-center justify-between p-3 border rounded-lg">
                      <div className="flex-1">
                        <p className="font-medium text-sm">{doc.naam}</p>
                        <p className="text-xs text-gray-500">{doc.beschrijving}</p>
                        <p className="text-xs text-gray-400">
                          Geüpload op {format(new Date(doc.aangemaakt_op), "PPp", { locale: nl })}
                        </p>
                      </div>
                      <div className="flex space-x-2">
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => {
                            // In production, this would download the file
                            toast.info('Download functionaliteit nog niet geïmplementeerd');
                          }}
                        >
                          <Download className="h-4 w-4" />
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => deleteDocument(doc.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {entityDocuments.length === 0 && (
              <div className="text-center py-8">
                <FileText className="mx-auto h-12 w-12 text-gray-400" />
                <p className="mt-2 text-sm text-gray-600">Nog geen documenten geüpload</p>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDocumentUpload(null)}>
              Sluiten
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Familie Management Dialog */}
      <Dialog open={!!editingUserFamily} onOpenChange={() => setEditingUserFamily(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Familie Beheren - {editingUserFamily?.naam}</DialogTitle>
            <DialogDescription>
              Voeg familie leden toe, bewerk of verwijder bestaande familie leden
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6">
            {/* Add New Family Member */}
            <div className="border rounded-lg p-4 bg-green-50">
              <h4 className="font-medium mb-4">Nieuw Familielid Toevoegen</h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Naam</Label>
                  <Input
                    value={newFamilyMember.naam}
                    onChange={(e) => setNewFamilyMember({...newFamilyMember, naam: e.target.value})}
                    placeholder="Voornaam Achternaam"
                  />
                </div>
                <div>
                  <Label>Relatie</Label>
                  <Select value={newFamilyMember.relatie} onValueChange={(value) => setNewFamilyMember({...newFamilyMember, relatie: value})}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="man">Man</SelectItem>
                      <SelectItem value="vrouw">Vrouw</SelectItem>
                      <SelectItem value="zoon">Zoon</SelectItem>
                      <SelectItem value="dochter">Dochter</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="mt-4">
                <Button 
                  type="button"
                  onClick={async (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    console.log('Add family member clicked:', newFamilyMember);
                    
                    if (!newFamilyMember.naam.trim()) {
                      toast.error('Vul een naam in');
                      return;
                    }
                    try {
                      const response = await axios.post(`${API}/users/${editingUserFamily.id}/family`, newFamilyMember, {
                        headers: { 'Authorization': `Bearer ${token}` }
                      });
                      console.log('Family member added:', response.data);
                      setNewFamilyMember({ naam: '', relatie: 'man' });
                      await fetchUserFamilies();
                      toast.success('Familielid toegevoegd');
                    } catch (error) {
                      console.error('Error adding family member:', error);
                      toast.error(error.response?.data?.detail || 'Fout bij toevoegen familielid');
                    }
                  }}
                  disabled={!newFamilyMember.naam.trim()}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Toevoegen
                </Button>
              </div>
            </div>

            {/* Test Button */}
            <div className="bg-red-50 p-2 rounded">
              <Button type="button" onClick={testButtonClick} className="bg-red-500 hover:bg-red-600">
                TEST KNOP - Klik om te testen
              </Button>
            </div>

            {/* Existing Family Members */}
            <div>
              <h4 className="font-medium mb-4">Bestaande Familieleden</h4>
              {userFamilies[editingUserFamily?.id] && userFamilies[editingUserFamily.id].length > 0 ? (
                <div className="space-y-3">
                  {userFamilies[editingUserFamily.id].map((member) => (
                    <div key={member.id} className="flex items-center justify-between p-3 border rounded-lg bg-gray-50">
                      <div className="flex items-center space-x-4">
                        <Badge variant="outline" className="capitalize">
                          {member.relatie}
                        </Badge>
                        <span className="font-medium">{member.naam}</span>
                        <span className="text-xs text-gray-500">
                          Toegevoegd: {format(new Date(member.aangemaakt_op), "PPP", { locale: nl })}
                        </span>
                      </div>
                      <div className="flex space-x-2">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            console.log('Edit button clicked for member:', member);
                            handleEditFamilyMember(member);
                          }}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            console.log('Delete button clicked for member:', member);
                            handleDeleteFamilyMember(member);
                          }}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <p>Nog geen familieleden toegevoegd</p>
                </div>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingUserFamily(null)}>
              Sluiten
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Claim Contacts Management Dialog */}
      <Dialog open={!!showContactDialog} onOpenChange={() => {
        setShowContactDialog(null);
        setClaimContacts([]);
        setNewClaimContact({ naam: '', rol: '', email: '', gsm: '' });
        setEditingContact(null);
      }}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Contacten Beheren</DialogTitle>
            <DialogDescription>
              Beheer contactpersonen voor deze claim (experts, makelaars, advocaten, etc.)
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6">
            {/* Add New Contact Section */}
            <div className="border rounded-lg p-4">
              <h4 className="font-medium mb-3">Nieuw Contact Toevoegen</h4>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Naam <span className="text-red-500">*</span></Label>
                  <Input
                    value={newClaimContact.naam}
                    onChange={(e) => setNewClaimContact({...newClaimContact, naam: e.target.value})}
                    placeholder="Jan Jansen"
                  />
                </div>
                <div>
                  <Label>Rol <span className="text-red-500">*</span></Label>
                  <Input
                    value={newClaimContact.rol}
                    onChange={(e) => setNewClaimContact({...newClaimContact, rol: e.target.value})}
                    placeholder="Schade expert"
                  />
                </div>
                <div>
                  <Label>E-mail</Label>
                  <Input
                    type="email"
                    value={newClaimContact.email}
                    onChange={(e) => setNewClaimContact({...newClaimContact, email: e.target.value})}
                    placeholder="jan@example.com"
                  />
                </div>
                <div>
                  <Label>GSM</Label>
                  <Input
                    value={newClaimContact.gsm}
                    onChange={(e) => setNewClaimContact({...newClaimContact, gsm: e.target.value})}
                    placeholder="+32 123 45 67 89"
                  />
                </div>
              </div>
              <div className="flex justify-end mt-3">
                <Button
                  size="sm"
                  onClick={() => addClaimContact(showContactDialog?.claimId)}
                  disabled={!newClaimContact.naam.trim() || !newClaimContact.rol.trim()}
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Contact Toevoegen
                </Button>
              </div>
            </div>

            {/* Existing Contacts List */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-medium">Huidige Contacten</h4>
                <Badge variant="outline">{claimContacts.length} contacten</Badge>
              </div>
              
              {claimContacts.length > 0 ? (
                <div className="space-y-3">
                  {claimContacts.map((contact) => (
                    <div key={contact.id} className="border rounded-lg p-3">
                      {editingContact?.id === contact.id ? (
                        <div className="space-y-3">
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <Label>Naam</Label>
                              <Input
                                value={editingContact.naam}
                                onChange={(e) => setEditingContact({...editingContact, naam: e.target.value})}
                              />
                            </div>
                            <div>
                              <Label>Rol</Label>
                              <Input
                                value={editingContact.rol}
                                onChange={(e) => setEditingContact({...editingContact, rol: e.target.value})}
                              />
                            </div>
                            <div>
                              <Label>E-mail</Label>
                              <Input
                                type="email"
                                value={editingContact.email || ''}
                                onChange={(e) => setEditingContact({...editingContact, email: e.target.value})}
                              />
                            </div>
                            <div>
                              <Label>GSM</Label>
                              <Input
                                value={editingContact.gsm || ''}
                                onChange={(e) => setEditingContact({...editingContact, gsm: e.target.value})}
                              />
                            </div>
                          </div>
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              onClick={() => updateClaimContact(showContactDialog?.claimId, contact.id, editingContact)}
                            >
                              Opslaan
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => setEditingContact(null)}
                            >
                              Annuleren
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <p className="font-medium">{contact.naam}</p>
                            <p className="text-sm text-gray-500">
                              {contact.rol}
                              {contact.email && ` • ${contact.email}`}
                              {contact.gsm && ` • ${contact.gsm}`}
                            </p>
                          </div>
                          <div className="flex gap-1">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => setEditingContact({
                                id: contact.id,
                                naam: contact.naam,
                                rol: contact.rol,
                                email: contact.email || '',
                                gsm: contact.gsm || ''
                              })}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => deleteClaimContact(showContactDialog?.claimId, contact.id)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <Users className="h-12 w-12 mx-auto mb-2 opacity-50" />
                  <p>Nog geen contacten toegevoegd</p>
                </div>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowContactDialog(null)}>
              Sluiten
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const App = () => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Laden...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      {user ? <Dashboard /> : <LoginPage />}
      <Toaster />
    </>
  );
};

const AppWithAuth = () => {
  return (
    <AuthProvider>
      <BrowserRouter>
        <App />
      </BrowserRouter>
    </AuthProvider>
  );
};

export default AppWithAuth;